import { SavedSearchNotificationChannel, EnergyClass, Cellar, Furnished, KitchenEquipment, HasFee, Elevator, LocationInBuilding, DistributionType, EstateType, SavedSearchDeviceType, SavedSearchOrigin, SavedSearchState, SwimmingPool, Garden, BuildState, BuildingAttic, SpaceMeasureUnit, DistributionSubType, LegacyIWTRelaxMode, LegacyIWTFeatures, EstateSubType, LocationGeometryTravelMode, LocationGeometryCircleRadiusUnit, LocationGeometryTravelUnit, ManagementUseFor, NotificationsTargetEnum } from '@aviv-saved-search/types';
import { LocationType, BuildState as BuildState$1, CertificateOfEligibility, DistributionType as DistributionType$1, EstateSubType as EstateSubType$1, EstateType as EstateType$1, ProjectType, FeatureType, Furnished as Furnished$1, CommuteMode, LocationInBuildingType, UseFor } from '@aviv-search/types';
import { IMN_THEME, LI_THEME, MA_THEME, AVIV_THEME, SL_THEME, IWB_THEME, IWT_THEME } from '@gemini/tokens';
import fetch$1 from 'cross-fetch';
import { useState } from 'react';

const isWindowUndefined = ()=>typeof window === 'undefined';
const isDocumentUndefined = ()=>typeof document === 'undefined';
const isLocalStorageUndefined = ()=>typeof localStorage === 'undefined';
const isSSR = ()=>isWindowUndefined() || isDocumentUndefined() || isLocalStorageUndefined();

const getLocalStorageItem = (key)=>{
    if (!isLocalStorageUndefined()) return localStorage.getItem(key);
    else return null;
};
const setLocalStorageItem = (key, value)=>{
    if (!isLocalStorageUndefined()) localStorage.setItem(key, value);
};

const getTheme = (brandId)=>{
    switch(brandId.toUpperCase()){
        case 'IWT':
            return IWT_THEME;
        case 'IMN':
            return IMN_THEME;
        case 'IWB':
            return IWB_THEME;
        case 'SL':
            return SL_THEME;
        case 'AVIV':
            return AVIV_THEME;
        case 'MA':
            return MA_THEME;
        case 'LI':
            return LI_THEME;
        default:
            return IMN_THEME;
    }
};
const clearObject = (toClear)=>{
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    const cleared = JSON.stringify(toClear, (_, value)=>value === null ? undefined : value);
    return JSON.parse(cleared);
};
const isValidEmail = (email)=>{
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};
const sortByRencency = (items)=>{
    return items.sort((a, b)=>{
        return new Date(b.metadata.created).getTime() - new Date(a.metadata.created).getTime();
    });
};
const sortClassifiedsByRecency = (classifieds)=>{
    return classifieds.sort((a, b)=>{
        return new Date(b.metadata.creationDate).getTime() - new Date(a.metadata.creationDate).getTime();
    });
};
const formatNumber = (input, locale)=>{
    let number;
    if (!input) return null;
    if (typeof input === 'string') {
        if (isNaN(Number(input))) {
            throw new Error(`${input} is not a number`);
        }
        number = Number(input);
    } else {
        number = input;
    }
    if (locale === 'fr') return number.toLocaleString('fr-FR');
    if (locale === 'en') return number.toLocaleString('en-US');
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};
const getNumberOfLocations = (locations)=>{
    return locations.placeIds.length + locations.geometries.circles.length + locations.geometries.polygons.length + locations.geometries.travels.length;
};
const isLegacyIWTSavedSearch = (obj)=>{
    return !!obj && 'baseAttributes' in obj;
};
const isLegacySLGSavedSearch = (obj)=>{
    return !!obj && 'searchId' in obj;
};
const createIntermediaryMatchingMandatePayload = (data, search)=>{
    return {
        user: {
            name: data.name,
            email: data.email,
            phoneNumber: data.phoneNumber,
            description: data?.message ?? '',
            postalCode: '',
            address: ''
        },
        search: {
            criteria: {
                estateType: search?.estateTypes ?? null,
                distributionType: search?.distributionTypes ?? null,
                prices: {
                    priceMin: search?.priceMin ?? null,
                    priceMax: search?.priceMax ?? null
                },
                spaces: {
                    spaceMin: search?.spaceMin ?? null,
                    spaceMax: search?.spaceMax ?? null
                },
                structure: {
                    rooms: {
                        numberOfRoomsMin: search?.numberOfRoomsMin ?? null,
                        numberOfRoomsMax: search?.numberOfRoomsMax ?? null
                    }
                },
                location: {
                    placeIds: search?.locations?.flatMap((location)=>location.type === LocationType.COMMUTE ? location.placeIds : location.placeId) ?? null
                }
            }
        }
    };
};
const getMostRecentSearch = (searches)=>{
    if (searches.length === 0) return null;
    const activeSearches = searches.filter((search)=>search.channels.find((channel)=>channel.channel === SavedSearchNotificationChannel.email)?.enabled);
    if (activeSearches.length === 0) return null;
    return activeSearches.reduce((previousSavedSearch, currentSavedSearch)=>{
        const previousDate = new Date(previousSavedSearch.metadata.updated).getTime();
        const currentDate = new Date(currentSavedSearch.metadata.updated).getTime();
        return currentDate > previousDate ? currentSavedSearch : previousSavedSearch;
    });
};
const includeCommuteLocation = (locations)=>{
    const commutes = locations.filter((location)=>location.type === LocationType.COMMUTE);
    return commutes.length > 0;
};
const isIntermediaryMatchingMandateShowable = (brandConfig)=>{
    const { intermediaryMatchingMandate, intermediaryMatchingMandateWithLocal } = brandConfig.button;
    if (intermediaryMatchingMandate) return true;
    const hasLocalStorageValue = getLocalStorageItem(intermediaryMatchingMandateLocalStorageKey);
    const hasSessionStorageValue = sessionStorage.getItem(intermediaryMatchingMandateLocalStorageKey);
    if (!hasLocalStorageValue && !hasSessionStorageValue) return false;
    if (intermediaryMatchingMandateWithLocal) return true;
    return false;
};

const spacesSymbol = 'm²';
const currencySymbol = '€';
const roomsKey = ' Zimmer'; //TODO: Translation is needed for rooms <> Zimmer
const getRange = (min, max, symbol = '')=>{
    if (min && max) {
        return `${min}${symbol} - ${max}${symbol}`;
    } else if (min) {
        return `min ${min}${symbol}`;
    } else if (max) {
        return `max ${max}${symbol}`;
    }
    return ``;
};
const energyClassToWording = (energyClass)=>{
    switch(energyClass){
        case EnergyClass.A_PLUS_PLUS:
            return 'A++';
        case EnergyClass.A_PLUS:
            return 'A+';
        default:
            return energyClass;
    }
};
const mapCriteriaToWording = (item, translate)=>{
    const { criteria } = item;
    const criteriasArray = [];
    const criteriaCheckboxKey = 'savedSearch.criterias.features.checkbox';
    if (criteria?.structure?.parkingLots?.numberOfGaragesMin || criteria?.structure?.parkingLots?.numberOfParkingsMin) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.parking_garage`));
    }
    if (criteria?.structure?.rooms?.numberOfBalconiesMin || criteria?.structure?.rooms?.numberOfTerracesMin) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.balcony_terrace`));
    }
    if (criteria?.features?.garden && criteria?.features?.garden.length !== 0) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.garden`));
    }
    if (criteria?.structure?.building?.bath?.window) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.bathroom_with_window`));
    }
    if (criteria?.structure?.building?.bath?.bathtub) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.bathroom_with_bathtub`));
    }
    if (criteria?.structure?.building?.cellar === Cellar.ANYKIND) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.cellar`));
    }
    if (criteria?.features?.furnished === Furnished.FULL || criteria?.features?.furnished === Furnished.ANYKIND) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.furnished`));
    }
    if (criteria?.structure?.building?.kitchen?.equipment === KitchenEquipment.FULLY_EQUIPPED) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.built_in_kitchen`));
    }
    if (criteria?.management?.isRented === false) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.vacant`));
    }
    if (criteria?.prices?.brokerageFee?.hasFee === HasFee.YES) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.free_of_commission`));
    }
    if (criteria?.features?.wheelchairUse) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.reduced_mobility_access`));
    }
    if (criteria?.structure?.building?.elevator?.length !== 0 && !criteria?.structure?.building?.elevator?.includes(Elevator.None)) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.elevator`));
    }
    if (criteria?.management?.rent?.petsAllowed) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.pets_allowed`));
    }
    if (criteria?.structure?.building?.locationInBuilding && criteria?.structure?.building?.locationInBuilding?.indexOf(LocationInBuilding.GROUNDFLOOR_EXCLUDED) !== -1) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.no_ground_floor`));
    }
    if (criteria?.structure?.building?.locationInBuilding && criteria?.structure?.building?.locationInBuilding?.indexOf(LocationInBuilding.ROOF_STOREY_EXCLUDED) !== -1) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.no_attic_apartment`));
    }
    if (criteria?.management?.rent?.certificateOfEligibilityNeeded) {
        criteriasArray.push(translate(`${criteriaCheckboxKey}.certificate_of_eligibility_needed`));
    }
    if (criteria?.conditions?.energyClass) {
        criteria.conditions.energyClass.forEach((energyClass)=>{
            criteriasArray.push(energyClassToWording(energyClass));
        });
    }
    return criteriasArray;
};
const getLocationsWording = ({ placeIds, placeIdInfos, geometries: { circles, travels, polygons } }, translate, { prefixed = false } = {})=>{
    if (polygons.length) {
        return translate(`savedSearch.criterias.location.polygons${prefixed ? '_prefixed' : ''}`);
    }
    const placeNames = placeIdInfos.map((place)=>place.placeName);
    if (placeNames.length === 0) {
        // we don't have any valid place infos so we return an empty string
        return '';
    }
    const firstCircle = circles[0];
    if (firstCircle) {
        return translate(`savedSearch.criterias.location.circles${prefixed ? '_prefixed' : ''}`, {
            replace: {
                placeName: placeNames[0],
                radius: firstCircle.radius,
                radiusUnit: firstCircle.radiusUnit
            },
            unsafe_escapeValue: false
        });
    }
    const firstTravel = travels[0];
    if (firstTravel) {
        return translate(`savedSearch.criterias.location.travels`, {
            replace: {
                placeName: placeNames[0],
                mode: translate(`savedSearch.enums.travelMode.${firstTravel.travelMode[0]}_prefixed`),
                duration: firstTravel.travelValue
            },
            unsafe_escapeValue: false
        });
    }
    if (placeIds.length) {
        return translate(`savedSearch.criterias.location.places${prefixed ? '_prefixed' : ''}`, {
            count: placeIds.length,
            replace: {
                count: placeIds.length - 1,
                placeName: placeNames[0]
            },
            unsafe_escapeValue: false
        });
    }
    return '';
};
const generateSavedSearchTitle = (criteria, translate)=>{
    const estateTypes = criteria.estateType.map((el)=>translate(`savedSearch.enums.estateType.${el}`));
    const distributionTypeEnum = criteria.distributionType.includes(DistributionType.RENT) ? DistributionType.RENT : DistributionType.BUY;
    const distributionType = translate(`savedSearch.enums.distributionType.${distributionTypeEnum}_prefixed`);
    const location = getLocationsWording(criteria.location, translate, {
        prefixed: true
    });
    return translate(`savedSearch.title.location`, {
        replace: {
            estateTypes,
            distributionType,
            location
        },
        unsafe_escapeValue: false
    }).trim();
};
const getOldFormatLocale = (newLocale)=>{
    return newLocale.split('-')[0] ?? newLocale;
};
const includeUpperOrLower = (toCheck, value)=>{
    return toCheck.includes(value.toLowerCase()) || toCheck.includes(value.toUpperCase());
};
const getWLItemWording = (item, translate)=>{
    const { prices, spaces, location } = item.criteria;
    const title = item?.title ? item.title : generateSavedSearchTitle(item.criteria, translate);
    const rooms = item.criteria.structure?.rooms;
    const pricesRange = getRange(prices?.priceMin, prices?.priceMax, currencySymbol);
    const spacesRange = getRange(spaces?.spaceMin, spaces?.spaceMax, spacesSymbol);
    let roomsRange = getRange(rooms?.numberOfRoomsMin, rooms?.numberOfRoomsMax, '');
    if (roomsRange.trim().length > 0) {
        roomsRange += roomsKey;
    }
    const criteriasDisplay = mapCriteriaToWording(item, translate).join(' · ');
    const adLocations = getLocationsWording(location, translate);
    return {
        title,
        pricesRange,
        spacesRange,
        roomsRange,
        criteriasDisplay,
        adLocations
    };
};
const getHintType = (search, translate)=>{
    if (includeCommuteLocation(search.locations)) {
        return {
            type: 'travelTime',
            content: {
                title: translate('savedSearch.hint.travel_time.title'),
                text: translate('savedSearch.hint.travel_time.text')
            }
        };
    }
    return {
        type: 'default',
        content: {
            title: translate('savedSearch.hint.default.title'),
            text: translate('savedSearch.hint.default.text')
        }
    };
};

const config_IMN_DEU = {
    brandId: 'imn',
    searchBrand: 'immonet',
    favoriteBrand: 'immonet',
    links: {
        mySpace: '/meinbereich',
        ssmui: 'nutzerportal/gespeicherte-suchen'
    },
    hostnames: {
        signIn: 'https://{env}signin.immonet.de'
    },
    tracking: {
        site: 'INT'
    },
    legals: {
        upperLocal: "DE",
        country: "Deutschland",
        terms: '/',
        policy: '/'
    },
    legacy: {
        loadingEnabled: false,
        checkMigrationState: false,
        useRecommendations: false,
        channelInteractionEnabled: true
    },
    recommendations: true,
    createUtils: {
        supported: true,
        emailRequired: false
    },
    button: {
        supported: true,
        supportCard: true,
        legacy: true,
        hintFeatures: {
            default: true,
            travelTime: false
        },
        intermediaryMatchingMandate: false,
        intermediaryMatchingMandateWithLocal: false,
        secondPageAlertModal: true
    },
    login: {
        supported: true,
        oauth: true
    },
    anonymous: {
        supported: true,
        promoteLogin: true
    },
    hpList: {
        supported: true,
        showMore: false,
        withRelaxed: false,
        favorites: false
    }
};

const config_IWT_DEU = {
    brandId: 'iwt',
    searchBrand: 'immowelt',
    favoriteBrand: 'immowelt',
    links: {
        mySpace: '/meinbereich',
        ssmui: 'nutzerportal/gespeicherte-suchen'
    },
    hostnames: {
        signIn: 'https://{env}signin.immowelt.de'
    },
    tracking: {
        site: 'IWT'
    },
    legals: {
        upperLocal: 'DE',
        country: 'Deutschland',
        terms: '/',
        policy: '/'
    },
    legacy: {
        loadingEnabled: false,
        checkMigrationState: false,
        useRecommendations: true,
        channelInteractionEnabled: true
    },
    recommendations: true,
    createUtils: {
        supported: true,
        emailRequired: false
    },
    button: {
        supported: true,
        supportCard: false,
        legacy: true,
        hintFeatures: {
            default: true,
            travelTime: false
        },
        intermediaryMatchingMandate: false,
        intermediaryMatchingMandateWithLocal: false,
        secondPageAlertModal: true
    },
    login: {
        supported: true,
        oauth: true
    },
    anonymous: {
        supported: true,
        promoteLogin: true
    },
    hpList: {
        supported: false,
        showMore: false,
        withRelaxed: false,
        favorites: false
    }
};

const config_LIM_FRA = {
    brandId: 'li',
    searchBrand: 'logicimmo',
    favoriteBrand: 'logicimmo',
    links: {
        mySpace: '/mon-espace',
        ssmui: 'mes-alertes'
    },
    hostnames: {
        signIn: 'https://{env}signin.logic-immo.com'
    },
    tracking: {
        site: 'LICOM'
    },
    legals: {
        upperLocal: 'FR',
        country: 'France',
        terms: 'https://www.logic-immo.com/conditions-generales-d-utilisation.html',
        policy: 'https://www.logic-immo.com/cgu-et-politique-de-confidentialite.html'
    },
    legacy: {
        loadingEnabled: true,
        checkMigrationState: false,
        useRecommendations: false,
        channelInteractionEnabled: true
    },
    recommendations: true,
    createUtils: {
        supported: true,
        emailRequired: true
    },
    button: {
        supported: true,
        supportCard: true,
        legacy: true,
        hintFeatures: {
            default: true,
            travelTime: true
        },
        intermediaryMatchingMandate: false,
        intermediaryMatchingMandateWithLocal: true,
        secondPageAlertModal: true
    },
    login: {
        supported: true,
        oauth: true
    },
    anonymous: {
        supported: true,
        promoteLogin: true
    },
    hpList: {
        supported: false,
        showMore: false,
        withRelaxed: false,
        favorites: false
    }
};

const config_SLG_FRA = {
    brandId: 'sl',
    searchBrand: 'seloger',
    favoriteBrand: 'seloger',
    links: {
        mySpace: '/mon-espace',
        ssmui: 'mes-alertes'
    },
    hostnames: {
        signIn: 'https://{env}signin.seloger.com'
    },
    tracking: {
        site: 'SL'
    },
    legals: {
        upperLocal: 'FR',
        country: 'France',
        terms: 'https://www.seloger.com/Conditions_Generales_d_Utilisation.html',
        policy: 'https://www.seloger.com/CGU_politique_de_confidentialite.html'
    },
    legacy: {
        loadingEnabled: true,
        checkMigrationState: true,
        useRecommendations: false,
        channelInteractionEnabled: false
    },
    recommendations: true,
    createUtils: {
        supported: true,
        emailRequired: false
    },
    button: {
        supported: true,
        supportCard: true,
        legacy: false,
        hintFeatures: {
            default: true,
            travelTime: true
        },
        intermediaryMatchingMandate: true,
        intermediaryMatchingMandateWithLocal: false,
        secondPageAlertModal: true
    },
    login: {
        supported: true,
        oauth: true
    },
    anonymous: {
        supported: true,
        promoteLogin: true
    },
    hpList: {
        supported: true,
        showMore: false,
        withRelaxed: false,
        favorites: true
    }
};

const config_SLN_FRA = {
    brandId: 'sln',
    searchBrand: 'selogerneuf',
    hostnames: {
        // auth not supported yet on SLN
        signIn: 'https://{env}signin.selogerneuf.com'
    },
    tracking: {
        site: 'SLN'
    },
    legals: {
        upperLocal: "FR",
        country: "France",
        terms: 'https://www.selogerneuf.com/conditions-generales-utilisation/',
        policy: 'https://www.seloger.com/CGU_politique_de_confidentialite.html'
    },
    legacy: {
        loadingEnabled: true,
        checkMigrationState: true,
        useRecommendations: false,
        channelInteractionEnabled: false
    },
    recommendations: false,
    createUtils: {
        supported: false,
        emailRequired: false
    },
    button: {
        supported: false,
        supportCard: false,
        legacy: false,
        hintFeatures: {
            default: false,
            travelTime: false
        },
        intermediaryMatchingMandate: false,
        intermediaryMatchingMandateWithLocal: false,
        secondPageAlertModal: false
    },
    login: {
        supported: false,
        oauth: false
    },
    anonymous: {
        supported: false,
        promoteLogin: false
    },
    hpList: {
        supported: false,
        showMore: false,
        withRelaxed: false,
        favorites: false
    }
};

const brandConfigs = {
    imn: config_IMN_DEU,
    iwt: config_IWT_DEU,
    sl: config_SLG_FRA,
    sln: config_SLN_FRA,
    li: config_LIM_FRA
};
/**
 * Retrieve the brand config for a given brand defined by the unified-frontend
 * @param brand BrandId as defined by the unified-frontend
 * @returns our brand config if we support that brand
 */ function getBrandConfig(brand) {
    return brandConfigs[brand];
}
/**
 * Retrieve the brand config for a given brand defined in our types repo
 * @param brand From the brand defined in our types repo
 * @returns our brand config if we support that brand
 */ function getCustomBrandConfig(brand) {
    return brandConfigs[brand];
}

const getAvUrlPath = ()=>{
    if (isWindowUndefined()) return '';
    const { pathname, search } = new URL(window.location.href);
    return pathname + search;
};
const getOptinAlert = (emailNotification, pushNotification)=>{
    if (emailNotification && pushNotification) {
        return 'push-notifications_and_email';
    } else if (emailNotification) {
        return 'email';
    } else if (pushNotification) {
        return 'push-notifications';
    }
    return '';
};
const getDeviceType = ()=>{
    if (isWindowUndefined()) return 'desktop';
    const userAgent = navigator.userAgent || window.opera;
    if (/android/i.test(userAgent)) {
        return 'mobile';
    }
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return 'mobile';
    }
    if (/windows phone/i.test(userAgent)) {
        return 'mobile';
    }
    return 'desktop';
};
const mapMethod = (savedSearch)=>{
    let method = 'none';
    savedSearch.channels.forEach((channel)=>{
        if (channel.channel === 'email') {
            method = 'email';
        }
    });
    return method;
};
const mapDistributionTypeGlobal = (distributionType)=>{
    switch(distributionType){
        case DistributionType.RENT:
            return '1';
        case DistributionType.BUY:
            return '2';
        case DistributionType.BUY_AUCTION:
            return '3';
        case DistributionType.COMPULSORY_AUCTION:
            return '4';
        default:
            return '0';
    }
};
const mapEstateTypeGlobal = (estateType)=>{
    switch(estateType){
        case EstateType.AGRICULTURE_FORESTRY:
            return 'av_1';
        case EstateType.APARTMENT:
            return 'av_2';
        case EstateType.BUILDING:
            return 'av_3';
        case EstateType.GASTRONOMY_HOTEL:
            return 'av_4';
        case EstateType.HOUSE:
            return 'av_5';
        case EstateType.MISCELLANEOUS:
            return 'av_6';
        case EstateType.OFFICE:
            return 'av_7';
        case EstateType.PARKING:
            return 'av_8';
        case EstateType.PLOT:
            return 'av_9';
        case EstateType.PROJECT:
            return 'av_10';
        case EstateType.SENIOR:
            return 'av_11';
        case EstateType.STORAGE_PRODUCTION:
            return 'av_12';
        case EstateType.TRADING:
            return 'av_13';
        default:
            return '';
    }
};
const getMedium = ()=>{
    if (isWindowUndefined()) return '';
    const urlSearchParams = !isWindowUndefined() ? window?.location?.search : '';
    const queryParameters = new URLSearchParams(urlSearchParams);
    return queryParameters.get('m') ?? '';
};

const mapSavedSearchCommonsThorKeys = (savedSearch, initiative = 'wl_ss_web', category = 'alert')=>{
    const av_initiative = initiative;
    const av_category = category;
    const av_distribution_type_global = mapDistributionTypeGlobal(savedSearch?.criteria?.distributionType[0]);
    const estateType = savedSearch.criteria.estateType?.[0] ?? '';
    const av_estate_type_global = mapEstateTypeGlobal(estateType);
    return {
        av_initiative,
        av_category,
        av_distribution_type_global,
        av_estate_type_global
    };
};
const mapSavedSearchInterestToThorEvent = (savedSearch, eventType)=>{
    return {
        ...mapSavedSearchCommonsThorKeys(savedSearch),
        event: eventType
    };
};
const mapSavedSearchSubscripionToThorEvent = (savedSearch, eventType, origin = 'serp_full_list')=>{
    const eventBase = {
        ...mapSavedSearchInterestToThorEvent(savedSearch, eventType),
        av_medium: getMedium(),
        method: mapMethod(savedSearch)
    };
    return eventType === 'lead_classified_alert_opened' ? eventBase : {
        ...eventBase,
        av_feature: origin
    };
};
const mapSavedSearchDeletedToThorEvent = (savedSearch)=>{
    return {
        ...mapSavedSearchCommonsThorKeys(savedSearch),
        av_medium: getMedium(),
        event: 'lead_classified_alert_unsub'
    };
};
const mapSavedSearchSeePropertiesToThorEvent = (savedSearch)=>{
    return {
        ...mapSavedSearchCommonsThorKeys(savedSearch),
        link_text: 'see_properties',
        event: 'see_properties'
    };
};
const mapSavedSearchToogleAlertToThorEvent = (savedSearch, method, eventType)=>{
    return {
        ...mapSavedSearchCommonsThorKeys(savedSearch),
        method,
        event: eventType
    };
};
const mapSavedSearchEditAlert = (savedSearch, eventType)=>{
    return {
        ...mapSavedSearchCommonsThorKeys(savedSearch),
        event: eventType
    };
};
const intermediaryMatchingMandateThorEvent = (search, type)=>{
    const savedSearch = mapSearchDataModelToWLSearch(search);
    return {
        ...mapSavedSearchCommonsThorKeys(savedSearch, 'wl_ss_web', 'classified'),
        event: type
    };
};
const getViewProps = ({ data, legacyData, stage, local, brand })=>{
    const isAuth = !!getToken();
    const av_language = getOldFormatLocale(local).toUpperCase();
    let emailNotification = false;
    let pushNotification = false;
    let uid = '';
    let active_alert = 0;
    if (data && data.length > 0) {
        active_alert = active_alert + data.length;
        for (const record of data){
            if (uid === '') uid = record.identity.userId;
            for (const endpoint of record.channels){
                if (endpoint.enabled && endpoint.channel === SavedSearchNotificationChannel.email) emailNotification = true;
                if (endpoint.enabled && endpoint.channel === SavedSearchNotificationChannel.mobile) pushNotification = true;
            }
        }
    }
    if (legacyData && legacyData.length > 0) {
        active_alert = active_alert + legacyData.length;
        for (const record of legacyData){
            if (uid === '') uid = record.identity.userId;
            for (const endpoint of record.channels){
                if (endpoint.enabled && endpoint.channel === SavedSearchNotificationChannel.email) emailNotification = true;
                if (endpoint.enabled && endpoint.channel === SavedSearchNotificationChannel.mobile) pushNotification = true;
            }
        }
    }
    const optin_alert = getOptinAlert(emailNotification, pushNotification);
    return {
        av_user: {
            uid,
            active_alert: active_alert.toString(),
            optin_alert,
            status: isAuth ? 'logged' : 'unlogged'
        },
        av_pagetitle: 'saved_alert',
        av_site: getCustomBrandConfig(brand)?.tracking.site,
        av_language,
        av_environment: stage === 'live' ? stage : stage === 'preview' ? 'preprod' : stage,
        av_url_path: getAvUrlPath(),
        av_initiative: 'wl_ss_web',
        av_category: 'alert',
        av_medium: getMedium(),
        av_platform: 'web'
    };
};
const getUserInteractionData = (page, brandId, origin)=>{
    const device = getDeviceType();
    const originUrl = !isWindowUndefined() ? window?.location?.href : '';
    const newOrigin = origin ? origin : page === 'lead' ? 'leadFormular' : 'getAlertButton';
    return {
        page,
        position: newOrigin === 'seo_component' ? 'top' : 'bottom',
        origin: newOrigin,
        device,
        brandId,
        originUrl
    };
};
const matchingListTracking = (classifieds, savedSearch)=>{
    if (!classifieds || !savedSearch) {
        return undefined;
    }
    const { av_category, av_initiative, av_distribution_type_global, av_estate_type_global } = mapSavedSearchCommonsThorKeys(savedSearch, 'wl_ss_web', 'classified');
    const items = Object.values(classifieds).map((classified)=>classified.tracking).filter((tracking)=>tracking !== undefined && tracking !== null).map((tracking)=>({
            ...tracking,
            list_name: 'last_saved_search'
        }));
    // @ts-ignore
    const EVENT_DATA = {
        event: 'view_item_list',
        av_items: items,
        av_category,
        av_initiative,
        av_distribution_type_global,
        av_estate_type_global
    };
    return EVENT_DATA;
};
const getTrackingFavorite = (classified)=>{
    const tracking = classified.tracking;
    tracking.list_name = 'last_saved_search';
    return tracking;
};

const imnEstateTypeToWLEstateType = new Map([
    [
        'HAUS',
        EstateType.HOUSE
    ],
    [
        'WOHNUNG',
        EstateType.APARTMENT
    ],
    [
        'GEWERBE_GRUNDSTUECK',
        EstateType.PLOT
    ],
    [
        'GRUNDSTUECK',
        EstateType.PLOT
    ],
    [
        'BUERO_PRAXISFLAECHE',
        EstateType.OFFICE
    ],
    [
        'LADENFLAECHE',
        EstateType.TRADING
    ],
    [
        'GASTRONOMIE_HOTEL',
        EstateType.GASTRONOMY_HOTEL
    ],
    [
        'HALLEN_INDUSTRIEFLAECHE',
        EstateType.STORAGE_PRODUCTION
    ],
    [
        'LAND_FORSTWIRTSCHAFT',
        EstateType.AGRICULTURE_FORESTRY
    ],
    [
        'GARAGE_STELLPLATZ',
        EstateType.PARKING
    ],
    [
        'SONSTIGES',
        EstateType.MISCELLANEOUS
    ],
    [
        'WOHNGEMEINSCHAFT',
        undefined
    ],
    [
        'WOHNEN_AUF_ZEIT',
        undefined
    ],
    [
        'RENDITEOBJEKT',
        undefined
    ]
]);
const imnDistributionTypeToWlDistributionType = new Map([
    [
        'ZUM_KAUF',
        DistributionType.BUY
    ],
    [
        'ZUR_MIETE',
        DistributionType.RENT
    ],
    [
        'ZWANGSVERSTEIGERUNG',
        DistributionType.COMPULSORY_AUCTION
    ]
]);
const locationInBuildingMap = {
    KEIN_DACHGESCHOSS: [
        LocationInBuilding.ROOF_STOREY_EXCLUDED
    ],
    KEIN_ERDGESCHOSS: [
        LocationInBuilding.GROUNDFLOOR_EXCLUDED
    ]
};
// Mapping function for equipment to locationInBuilding
const mapEquipmentToLocationInBuilding = (equipment)=>{
    let mappedLocationInBuilding = [];
    equipment.forEach((item)=>{
        if (locationInBuildingMap[item]) {
            mappedLocationInBuilding = mappedLocationInBuilding.concat(locationInBuildingMap[item]);
        }
    });
    return mappedLocationInBuilding;
};
const LEGACY_MODEL_VERSION = 'legacy';
const isLegacySavedSearch = (savedSearch)=>savedSearch.metadata.modelVersion === LEGACY_MODEL_VERSION;
const mapLegacyIWTToSavedSearch = (legacySavedSearch)=>({
        criteria: {
            estateType: legacySavedSearch.baseAttributes.searchCriteria.general.estateTypes.map((imnEstateType)=>imnEstateTypeToWLEstateType.get(imnEstateType)).filter((x)=>x !== undefined),
            distributionType: legacySavedSearch.baseAttributes.searchCriteria.general.distributionTypes.map((imnDstributionType)=>imnDistributionTypeToWlDistributionType.get(imnDstributionType)).filter((x)=>x !== undefined),
            estateSubType: [],
            distributionSubType: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.NUR_NEUBAUPROJEKTE) ? [
                DistributionSubType.NEW_BUILD_UNIT
            ] : [],
            prices: {
                currencySymbol: '€',
                currencyUnit: 'EUR',
                priceMin: legacySavedSearch.baseAttributes.searchCriteria.pricing.priceMin,
                priceMax: legacySavedSearch.baseAttributes.searchCriteria.pricing.priceMax,
                brokerageFee: {
                    hasFee: HasFee.None
                }
            },
            spaces: {
                spaceMeasureSymbol: 'm²',
                spaceMeasureUnit: SpaceMeasureUnit.SQUARE_METER,
                spaceMin: legacySavedSearch.baseAttributes.searchCriteria.construction.livingAreaMin,
                spaceMax: legacySavedSearch.baseAttributes.searchCriteria.construction.livingAreaMax,
                plotSpaceMin: undefined,
                plotSpaceMax: undefined
            },
            location: {
                placeIds: [],
                geometries: {
                    circles: [],
                    polygons: [],
                    travels: []
                },
                placeIdInfos: []
            },
            energy: {
                energyType: {
                    energyHeatForm: [],
                    energyHeatMethod: [],
                    energySource: []
                }
            },
            structure: {
                rooms: {
                    numberOfRoomsMin: legacySavedSearch.baseAttributes.searchCriteria.construction.roomCountMin ?? undefined,
                    numberOfRoomsMax: legacySavedSearch.baseAttributes.searchCriteria.construction.roomCountMax ?? undefined,
                    numberOfBalconiesMin: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.BALKON_TERRASSE) ? 1 : undefined,
                    numberOfTerracesMin: undefined,
                    numberOfBedRoomsMin: undefined,
                    numberOfBedRoomsMax: undefined
                },
                building: {
                    locationInBuilding: mapEquipmentToLocationInBuilding(legacySavedSearch.baseAttributes.searchCriteria.general.equipment),
                    bath: {
                        window: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.BAD_MIT_FENSTER) ? true : undefined,
                        bathtub: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.BAD_MIT_WANNE) ? true : undefined
                    },
                    kitchen: {
                        equipment: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.EINBAUKUECHE) ? KitchenEquipment.FULLY_EQUIPPED : KitchenEquipment.UNKNOWN
                    },
                    withView: {},
                    elevator: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.PERSONENAUFZUG) ? [
                        Elevator.ANYKIND
                    ] : [],
                    cellar: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.TEILWEISE_UNTERKELLERT) || legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.VOLL_UNTERKELLERT) || legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.KELLER) ? Cellar.ANYKIND : Cellar.None,
                    attic: [
                        BuildingAttic.None
                    ]
                },
                parkingLots: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.GARAGE_STELLPLATZ) ? {
                    numberOfParkingsMin: 1,
                    numberOfGaragesMin: 1
                } : {
                    numberOfParkingsMin: undefined,
                    numberOfGaragesMin: undefined
                }
            },
            conditions: {
                yearOfConstructionMin: undefined,
                yearOfConstructionMax: undefined,
                buildState: [
                    BuildState.None
                ],
                energyClass: legacySavedSearch.baseAttributes.searchCriteria.general.energyCertificateClass
            },
            management: {
                isRented: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.FREI) ? false : undefined,
                isForSwap: undefined,
                rent: {
                    petsAllowed: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.HAUSTIERE_ERLAUBT) ? true : undefined,
                    certificateOfEligibilityNeeded: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.WOHNBERECHTIGUNGSSCHEIN) ? true : undefined
                },
                useFor: [],
                legalNotes: []
            },
            features: {
                wheelchairUse: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.BARRIEREFREI) || legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.ROLLSTUHLGERECHT),
                garden: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.GARTEN) ? [
                    Garden.ANYKIND
                ] : [],
                furnished: legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.VOLL_MOEBLIERT) || legacySavedSearch.baseAttributes.searchCriteria.general.equipment.includes(LegacyIWTFeatures.TEILWEISE_MOEBLIERT) ? Furnished.FULL : Furnished.NO,
                propertyOrientation: [],
                residential: {
                    swimmingPool: SwimmingPool.None
                }
            }
        },
        title: legacySavedSearch.baseAttributes.config.searchDisplayName,
        identity: {
            id: legacySavedSearch.baseAttributes.id,
            userId: legacySavedSearch.baseAttributes.userId + '',
            portal: legacySavedSearch.baseAttributes.origin
        },
        state: SavedSearchState.enabled,
        metadata: {
            modelVersion: LEGACY_MODEL_VERSION,
            created: '',
            updated: '',
            origin: SavedSearchOrigin.SavedSearch
        },
        channels: legacySavedSearch.baseAttributes.endpoints.map((legacyEndpoint)=>({
                channel: legacyEndpoint.channelType === 'EMAIL_MESSAGE' ? SavedSearchNotificationChannel.email : SavedSearchNotificationChannel.mobile,
                enabled: legacyEndpoint.channelType === 'EMAIL_MESSAGE' && legacyEndpoint.notificationFrequency.type !== 'NEVER',
                email: legacyEndpoint.channelType === 'EMAIL_MESSAGE' ? legacyEndpoint.endpointResourceId : ''
            })),
        externalProperties: {
            channelId: '',
            deviceType: SavedSearchDeviceType.None
        },
        relaxedCriteria: {
            isEnabled: legacySavedSearch?.baseAttributes?.searchCriteria?.relaxedCriteria?.relaxEquipmentMode === LegacyIWTRelaxMode.SINGLE,
            options: {
                locationSearchAreaIncrease: legacySavedSearch?.baseAttributes?.searchCriteria?.relaxedCriteria?.location ?? 0,
                priceIncreaseInPercent: legacySavedSearch?.baseAttributes?.searchCriteria?.relaxedCriteria?.primaryPriceMax ?? 0,
                surfaceDecreaseInPercent: legacySavedSearch?.baseAttributes?.searchCriteria?.relaxedCriteria?.primaryAreaMin ?? 0,
                relaxedCriteriaRemoval: false
            }
        }
    });
const mapLegacySLGDataToSavedSearch = (legacySavedSearch)=>({
        title: legacySavedSearch.title,
        externalProperties: {
            channelId: '',
            deviceType: SavedSearchDeviceType.None
        },
        state: SavedSearchState.enabled,
        metadata: {
            modelVersion: LEGACY_MODEL_VERSION,
            created: '',
            updated: '',
            origin: SavedSearchOrigin.SavedSearch
        },
        relaxedCriteria: {
            isEnabled: false,
            options: {}
        },
        identity: {
            id: legacySavedSearch.searchId.toString(),
            userId: '1',
            portal: 'sl'
        },
        channels: [
            {
                channel: SavedSearchNotificationChannel.email,
                enabled: legacySavedSearch.isActive
            }
        ],
        criteria: {
            estateType: legacySavedSearch.searchCriterias.includes('Appartement') ? [
                EstateType.APARTMENT
            ] : [
                EstateType.HOUSE
            ],
            distributionType: legacySavedSearch.searchCriterias.includes('Vente') ? [
                DistributionType.BUY
            ] : [
                DistributionType.RENT
            ],
            estateSubType: [],
            distributionSubType: [],
            prices: {
                currencySymbol: '€',
                currencyUnit: 'EUR',
                priceMin: undefined,
                priceMax: undefined,
                brokerageFee: {
                    hasFee: HasFee.None
                }
            },
            spaces: {
                spaceMeasureSymbol: 'm²',
                spaceMeasureUnit: SpaceMeasureUnit.SQUARE_METER,
                spaceMin: undefined,
                spaceMax: undefined,
                plotSpaceMin: undefined,
                plotSpaceMax: undefined
            },
            location: {
                placeIds: [],
                geometries: {
                    circles: [],
                    polygons: [],
                    travels: []
                },
                placeIdInfos: []
            },
            structure: {
                rooms: {
                    numberOfRoomsMin: undefined,
                    numberOfRoomsMax: undefined,
                    numberOfBalconiesMin: undefined,
                    numberOfTerracesMin: undefined,
                    numberOfBedRoomsMin: undefined,
                    numberOfBedRoomsMax: undefined
                },
                building: {
                    locationInBuilding: [],
                    bath: {
                        window: undefined,
                        bathtub: undefined
                    },
                    kitchen: {
                        equipment: KitchenEquipment.UNKNOWN
                    },
                    withView: {},
                    elevator: [],
                    cellar: Cellar.None,
                    attic: []
                },
                parkingLots: {
                    numberOfParkingsMin: undefined,
                    numberOfGaragesMin: undefined
                }
            },
            conditions: {
                yearOfConstructionMax: undefined,
                yearOfConstructionMin: undefined,
                buildState: [],
                energyClass: []
            },
            energy: {
                energyType: {
                    energyHeatForm: [],
                    energyHeatMethod: [],
                    energySource: []
                }
            },
            management: {
                isRented: undefined,
                isForSwap: undefined,
                rent: {
                    petsAllowed: undefined,
                    certificateOfEligibilityNeeded: undefined
                },
                useFor: [],
                legalNotes: []
            },
            features: {
                wheelchairUse: undefined,
                garden: [],
                furnished: Furnished.None,
                residential: {
                    swimmingPool: SwimmingPool.None
                },
                propertyOrientation: []
            }
        }
    });

/** WALDO -> HYP BuildState */ const getCriteriaBuildState = (searchModel)=>{
    return searchModel.buildState?.map((v)=>BuildState[v.toUpperCase()]).filter((v)=>v !== undefined) ?? [];
};
/** HYP -> WALDO BuildState */ const getSearchBuildState = (criterias)=>{
    const result = criterias?.conditions?.buildState?.map((v)=>BuildState$1[v.toUpperCase()]).filter((v)=>v !== undefined) ?? [];
    return result.length ? result : undefined;
};

/** WALDO -> HYP CertificateOfEligibilityNeeded */ const getCriteriaCertificateOfEligibilityNeeded = (searchModel)=>{
    switch(searchModel.certificateOfEligibilityNeeded){
        case CertificateOfEligibility.YES:
            return true;
        case CertificateOfEligibility.NO:
            return false;
    }
    return undefined;
};
/** HYP -> WALDO CertificateOfEligibilityNeeded */ const getSearchCertificateOfEligibilityNeeded = (criterias)=>{
    switch(criterias?.management?.rent?.certificateOfEligibilityNeeded){
        case true:
            return CertificateOfEligibility.YES;
        case false:
            return CertificateOfEligibility.NO;
    }
    return undefined;
};

const searchToCriteriaMap = new Map([
    [
        DistributionType$1.BUY,
        DistributionType.BUY
    ],
    [
        DistributionType$1.BUY_AUCTION,
        DistributionType.BUY_AUCTION
    ],
    [
        DistributionType$1.COMPULSORY_AUCTION,
        DistributionType.COMPULSORY_AUCTION
    ],
    [
        DistributionType$1.RENT,
        DistributionType.RENT
    ]
]);
/** WALDO -> HYP DistributionType */ const getCriteriaDistributionType = (searchModel)=>{
    const result = new Set();
    for (const v of searchModel?.distributionTypes ?? []){
        const distributionType = searchToCriteriaMap.get(v);
        if (distributionType !== undefined) {
            result.add(distributionType);
        }
    }
    return Array.from(result);
};
const criteriaToSearchMap = new Map([
    [
        DistributionType.BUY,
        DistributionType$1.BUY
    ],
    [
        DistributionType.BUY_AUCTION,
        DistributionType$1.BUY_AUCTION
    ],
    [
        DistributionType.COMPULSORY_AUCTION,
        DistributionType$1.COMPULSORY_AUCTION
    ],
    [
        DistributionType.RENT,
        DistributionType$1.RENT
    ]
]);
/** HYP -> WALDO DistributionType */ const getSearchDistributionType = (criterias)=>{
    const result = new Set();
    for (const v of criterias?.distributionType ?? []){
        const distributionType = criteriaToSearchMap.get(v);
        if (distributionType !== undefined) {
            result.add(distributionType);
        }
    }
    return Array.from(result);
};

/** WALDO -> HYP EnergyCertificateClass */ const getCriteriaEnergyCertificateClass = (searchModel)=>{
    const result = new Set();
    for (const v of searchModel.energyCertificateClass ?? []){
        const energyClass = EnergyClass[v.toUpperCase()];
        if (energyClass !== undefined && energyClass !== EnergyClass.None) {
            result.add(energyClass);
        }
    }
    return Array.from(result);
};
/** HYP -> WALDO EnergyCertificateClass */ const getSearchEnergyCertificateClass = (criterias)=>{
    const result = criterias?.conditions?.energyClass ?? [];
    return result.length ? result : undefined;
};

/** WALDO -> HYP EstateSubType */ const getCriteriaEstateSubType = (searchModel)=>{
    const result = new Set();
    for (const v of searchModel.estateSubTypes ?? []){
        const estateSubType = EstateSubType[v.toUpperCase()];
        if (estateSubType !== undefined && estateSubType !== EstateSubType.None) {
            result.add(estateSubType);
        }
    }
    return Array.from(result);
};
/** HYP -> WALDO EstateSubType */ const getSearchEstateSubType = (criterias)=>{
    const result = new Set();
    for (const v of criterias?.estateSubType ?? []){
        const estateSubType = EstateSubType$1[v];
        if (estateSubType !== undefined) {
            result.add(estateSubType);
        }
    }
    return result.size ? Array.from(result) : undefined;
};

const criteriaEstateTypeMap = new Map([
    [
        EstateType$1.HOUSE,
        EstateType.HOUSE
    ],
    [
        EstateType$1.APARTMENT,
        EstateType.APARTMENT
    ],
    [
        EstateType$1.PARKING,
        EstateType.PARKING
    ],
    [
        EstateType$1.PLOT,
        EstateType.PLOT
    ],
    [
        EstateType$1.MISCELLANEOUS,
        EstateType.MISCELLANEOUS
    ],
    [
        EstateType$1.OFFICE,
        EstateType.OFFICE
    ],
    [
        EstateType$1.TRADING,
        EstateType.TRADING
    ],
    [
        EstateType$1.GASTRONOMY_HOTEL,
        EstateType.GASTRONOMY_HOTEL
    ],
    [
        EstateType$1.STORAGE_PRODUCTION,
        EstateType.STORAGE_PRODUCTION
    ],
    [
        EstateType$1.AGRICULTURE_FORESTRY,
        EstateType.AGRICULTURE_FORESTRY
    ]
]);
/** WALDO -> HYP EstateType */ const getCriteriaEstateType = (searchModel)=>{
    const result = new Set();
    for (const estateType of searchModel.estateTypes ?? []){
        const mappedEstateType = criteriaEstateTypeMap.get(estateType);
        if (mappedEstateType) {
            result.add(mappedEstateType);
        }
    }
    if (searchModel.projectTypes?.includes(ProjectType.PROJECTED) || searchModel.estateTypes?.includes(EstateType$1.HOUSE) || searchModel.estateTypes?.includes(EstateType$1.APARTMENT)) {
        result.add(EstateType.PROJECT);
    }
    return Array.from(result);
};
const SearchEstateTypeMap = new Map([
    [
        EstateType.None,
        undefined
    ],
    [
        EstateType.HOUSE,
        EstateType$1.HOUSE
    ],
    [
        EstateType.APARTMENT,
        EstateType$1.APARTMENT
    ],
    [
        EstateType.PARKING,
        EstateType$1.PARKING
    ],
    [
        EstateType.PLOT,
        EstateType$1.PLOT
    ],
    [
        EstateType.MISCELLANEOUS,
        EstateType$1.MISCELLANEOUS
    ],
    [
        EstateType.OFFICE,
        EstateType$1.OFFICE
    ],
    [
        EstateType.TRADING,
        EstateType$1.TRADING
    ],
    [
        EstateType.GASTRONOMY_HOTEL,
        EstateType$1.GASTRONOMY_HOTEL
    ],
    [
        EstateType.STORAGE_PRODUCTION,
        EstateType$1.STORAGE_PRODUCTION
    ],
    [
        EstateType.AGRICULTURE_FORESTRY,
        EstateType$1.AGRICULTURE_FORESTRY
    ],
    [
        EstateType.BUILDING,
        undefined
    ],
    [
        EstateType.SENIOR,
        undefined
    ],
    [
        EstateType.PROJECT,
        undefined
    ],
    [
        EstateType.MISCELLANEOUS,
        undefined
    ]
]);
/** HYP -> WALDO EstateType */ const getSearchEstateType = (criterias)=>{
    const result = new Set();
    for (const estateType of criterias?.estateType ?? []){
        const mappedEstateType = SearchEstateTypeMap.get(estateType);
        if (mappedEstateType) {
            result.add(mappedEstateType);
        }
    }
    return result.size ? Array.from(result) : undefined;
};

/** WALDO -> HYP FeaturesIncluded */ const getCriteriaBrokerageFee = (searchModel)=>{
    return searchModel.featuresIncluded?.includes(FeatureType.COMMISSION_FREE) ? {
        hasFee: HasFee.YES
    } : {
        hasFee: HasFee.None
    };
};
const getCriteriaBathroom = (searchModel)=>{
    return {
        window: searchModel.featuresIncluded?.includes(FeatureType.BATHROOM_WINDOW) ? true : undefined,
        bathtub: searchModel.featuresIncluded?.includes(FeatureType.BATHROOM_BATHTUB) ? true : undefined
    };
};
const getCriteriaKitchen = (searchModel)=>{
    return {
        equipment: searchModel.featuresIncluded?.includes(FeatureType.KITCHEN_FULLY_EQUIPPED) ? KitchenEquipment.FULLY_EQUIPPED : KitchenEquipment.UNKNOWN
    };
};
const getCriteriaElevator = (searchModel)=>{
    return searchModel.featuresIncluded?.includes(FeatureType.ELEVATOR) ? [
        Elevator.ANYKIND
    ] : [];
};
const getCriteriaCellar = (searchModel)=>{
    return searchModel.featuresIncluded?.includes(FeatureType.CELLAR) ? Cellar.ANYKIND : Cellar.None;
};
const getCriteriaParkingLots = (searchModel)=>{
    return {
        numberOfParkingsMin: searchModel.featuresIncluded?.includes(FeatureType.PARKING_GARAGE) ? 1 : undefined,
        numberOfGaragesMin: undefined
    };
};
const getCriteriaIsRented = (searchModel)=>{
    return searchModel.featuresIncluded?.includes(FeatureType.VACANT) ? false : undefined;
};
const getCriteriaPetAllowed = (searchModel)=>{
    return searchModel.featuresIncluded?.includes(FeatureType.PETS_ALLOWED) ? true : undefined;
};
const getCriteriaWheelchairUse = (searchModel)=>{
    return searchModel.featuresIncluded?.includes(FeatureType.REDUCE_MOBILITY_ACCESS) ? true : undefined;
};
const getCriteriaGarden = (searchModel)=>{
    return searchModel.featuresIncluded?.includes(FeatureType.GARDEN) ? [
        Garden.ANYKIND
    ] : [];
};
const getCriteriaSwimmingPool = (searchModel)=>{
    return searchModel.featuresIncluded?.includes(FeatureType.SWIMMING_POOL) ? SwimmingPool.ANYKIND : SwimmingPool.None;
};
/** HYP -> WALDO FeaturesIncluded */ const getSearchFeaturesIncluded = (criterias)=>{
    const features = [];
    if (criterias?.prices?.brokerageFee?.hasFee === HasFee.YES) {
        features.push(FeatureType.COMMISSION_FREE);
    }
    if (criterias?.structure?.rooms?.numberOfBalconiesMin) {
        features.push(FeatureType.BALCONY_TERRACE);
    }
    if (criterias?.structure?.building?.bath?.window) {
        features.push(FeatureType.BATHROOM_WINDOW);
    }
    if (criterias?.structure?.building?.bath?.bathtub) {
        features.push(FeatureType.BATHROOM_BATHTUB);
    }
    if (criterias?.structure?.building?.kitchen?.equipment === KitchenEquipment.FULLY_EQUIPPED) {
        features.push(FeatureType.KITCHEN_FULLY_EQUIPPED);
    }
    if (criterias?.structure?.building?.elevator?.length) {
        features.push(FeatureType.ELEVATOR);
    }
    if (criterias?.structure?.building?.cellar === Cellar.ANYKIND) {
        features.push(FeatureType.CELLAR);
    }
    if (criterias?.structure?.parkingLots?.numberOfParkingsMin || criterias?.structure?.parkingLots?.numberOfGaragesMin) {
        features.push(FeatureType.PARKING_GARAGE);
    }
    if (criterias?.management?.rent?.petsAllowed) {
        features.push(FeatureType.PETS_ALLOWED);
    }
    if (criterias?.management?.isRented === false) {
        features.push(FeatureType.VACANT);
    }
    if (criterias?.features?.wheelchairUse) {
        features.push(FeatureType.REDUCE_MOBILITY_ACCESS);
    }
    if (criterias?.features?.garden?.length) {
        features.push(FeatureType.GARDEN);
    }
    if (criterias?.features?.residential?.swimmingPool === SwimmingPool.ANYKIND) {
        features.push(FeatureType.SWIMMING_POOL);
    }
    return features.length ? features : undefined;
};

/** WALDO -> HYP Furnished */ const getCriteriaFurnished = (searchModel)=>{
    const furnished = searchModel.furnished;
    if (!furnished) {
        return Furnished.None;
    }
    if (furnished.includes(Furnished$1.FULL) && furnished.includes(Furnished$1.NO)) {
        return Furnished.ANYKIND;
    }
    if (furnished.includes(Furnished$1.FULL)) {
        return Furnished.FULL;
    }
    if (furnished.includes(Furnished$1.NO)) {
        return Furnished.NO;
    }
    if (furnished.includes(Furnished$1.NOT_APPLICABLE)) {
        return Furnished.NOT_APPLICABLE;
    }
    return Furnished.None;
};
/** HYP -> WALDO Furnished */ const getSearchFurnished = (criterias)=>{
    switch(criterias?.features?.furnished){
        case Furnished.FULL:
            return [
                Furnished$1.FULL
            ];
        case Furnished.NO:
            return [
                Furnished$1.NO
            ];
        case Furnished.NOT_APPLICABLE:
            return [
                Furnished$1.NOT_APPLICABLE
            ];
        case Furnished.ANYKIND:
            return [
                Furnished$1.FULL,
                Furnished$1.NO
            ];
        default:
            return undefined;
    }
};

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var polyline = {exports: {}};

var hasRequiredPolyline;

function requirePolyline () {
	if (hasRequiredPolyline) return polyline.exports;
	hasRequiredPolyline = 1;
	(function (module) {

		/**
		 * Based off of [the offical Google document](https://developers.google.com/maps/documentation/utilities/polylinealgorithm)
		 *
		 * Some parts from [this implementation](http://facstaff.unca.edu/mcmcclur/GoogleMaps/EncodePolyline/PolylineEncoder.js)
		 * by [Mark McClure](http://facstaff.unca.edu/mcmcclur/)
		 *
		 * @module polyline
		 */

		var polyline = {};

		function py2_round(value) {
		    // Google's polyline algorithm uses the same rounding strategy as Python 2, which is different from JS for negative values
		    return Math.floor(Math.abs(value) + 0.5) * (value >= 0 ? 1 : -1);
		}

		function encode(current, previous, factor) {
		    current = py2_round(current * factor);
		    previous = py2_round(previous * factor);
		    var coordinate = (current - previous) * 2;
		    if (coordinate < 0) {
		        coordinate = -coordinate - 1;
		    }
		    var output = '';
		    while (coordinate >= 0x20) {
		        output += String.fromCharCode((0x20 | (coordinate & 0x1f)) + 63);
		        coordinate /= 32;
		    }
		    output += String.fromCharCode((coordinate | 0) + 63);
		    return output;
		}

		/**
		 * Decodes to a [latitude, longitude] coordinates array.
		 *
		 * This is adapted from the implementation in Project-OSRM.
		 *
		 * @param {String} str
		 * @param {Number} precision
		 * @returns {Array}
		 *
		 * @see https://github.com/Project-OSRM/osrm-frontend/blob/master/WebContent/routing/OSRM.RoutingGeometry.js
		 */
		polyline.decode = function(str, precision) {
		    var index = 0,
		        lat = 0,
		        lng = 0,
		        coordinates = [],
		        shift = 0,
		        result = 0,
		        byte = null,
		        latitude_change,
		        longitude_change,
		        factor = Math.pow(10, Number.isInteger(precision) ? precision : 5);

		    // Coordinates have variable length when encoded, so just keep
		    // track of whether we've hit the end of the string. In each
		    // loop iteration, a single coordinate is decoded.
		    while (index < str.length) {

		        // Reset shift, result, and byte
		        byte = null;
		        shift = 1;
		        result = 0;

		        do {
		            byte = str.charCodeAt(index++) - 63;
		            result += (byte & 0x1f) * shift;
		            shift *= 32;
		        } while (byte >= 0x20);

		        latitude_change = (result & 1) ? ((-result - 1) / 2) : (result / 2);

		        shift = 1;
		        result = 0;

		        do {
		            byte = str.charCodeAt(index++) - 63;
		            result += (byte & 0x1f) * shift;
		            shift *= 32;
		        } while (byte >= 0x20);

		        longitude_change = (result & 1) ? ((-result - 1) / 2) : (result / 2);

		        lat += latitude_change;
		        lng += longitude_change;

		        coordinates.push([lat / factor, lng / factor]);
		    }

		    return coordinates;
		};

		/**
		 * Encodes the given [latitude, longitude] coordinates array.
		 *
		 * @param {Array.<Array.<Number>>} coordinates
		 * @param {Number} precision
		 * @returns {String}
		 */
		polyline.encode = function(coordinates, precision) {
		    if (!coordinates.length) { return ''; }

		    var factor = Math.pow(10, Number.isInteger(precision) ? precision : 5),
		        output = encode(coordinates[0][0], 0, factor) + encode(coordinates[0][1], 0, factor);

		    for (var i = 1; i < coordinates.length; i++) {
		        var a = coordinates[i], b = coordinates[i - 1];
		        output += encode(a[0], b[0], factor);
		        output += encode(a[1], b[1], factor);
		    }

		    return output;
		};

		function flipped(coords) {
		    var flipped = [];
		    for (var i = 0; i < coords.length; i++) {
		        var coord = coords[i].slice();
		        flipped.push([coord[1], coord[0]]);
		    }
		    return flipped;
		}

		/**
		 * Encodes a GeoJSON LineString feature/geometry.
		 *
		 * @param {Object} geojson
		 * @param {Number} precision
		 * @returns {String}
		 */
		polyline.fromGeoJSON = function(geojson, precision) {
		    if (geojson && geojson.type === 'Feature') {
		        geojson = geojson.geometry;
		    }
		    if (!geojson || geojson.type !== 'LineString') {
		        throw new Error('Input must be a GeoJSON LineString');
		    }
		    return polyline.encode(flipped(geojson.coordinates), precision);
		};

		/**
		 * Decodes to a GeoJSON LineString geometry.
		 *
		 * @param {String} str
		 * @param {Number} precision
		 * @returns {Object}
		 */
		polyline.toGeoJSON = function(str, precision) {
		    var coords = polyline.decode(str, precision);
		    return {
		        type: 'LineString',
		        coordinates: flipped(coords)
		    };
		};

		if (module.exports) {
		    module.exports = polyline;
		} 
	} (polyline));
	return polyline.exports;
}

var polylineExports = requirePolyline();

var inputValidation = {};

var validateCenter = {};

var hasRequiredValidateCenter;

function requireValidateCenter () {
	if (hasRequiredValidateCenter) return validateCenter;
	hasRequiredValidateCenter = 1;
	validateCenter.validateCenter = function validateCenter(center) {
	  var validCenterLengths = [2, 3];
	  if (!Array.isArray(center) || !validCenterLengths.includes(center.length)) {
	    throw new Error("ERROR! Center has to be an array of length two or three");
	  }

	  var [lng, lat] = center;
	  if (typeof lng !== "number" || typeof lat !== "number") {
	    throw new Error(
	      `ERROR! Longitude and Latitude has to be numbers but where ${typeof lng} and ${typeof lat}`
	    );
	  }
	  if (lng > 180 || lng < -180) {
	    throw new Error(`ERROR! Longitude has to be between -180 and 180 but was ${lng}`);
	  }

	  if (lat > 90 || lat < -90) {
	    throw new Error(`ERROR! Latitude has to be between -90 and 90 but was ${lat}`);
	  }
	};
	return validateCenter;
}

var validateRadius = {};

var hasRequiredValidateRadius;

function requireValidateRadius () {
	if (hasRequiredValidateRadius) return validateRadius;
	hasRequiredValidateRadius = 1;
	validateRadius.validateRadius = function validateRadius(radius) {
	  if (typeof radius !== "number") {
	    throw new Error(`ERROR! Radius has to be a positive number but was: ${typeof radius}`);
	  }

	  if (radius <= 0) {
	    throw new Error(`ERROR! Radius has to be a positive number but was: ${radius}`);
	  }
	};
	return validateRadius;
}

var validateNumberOfEdges = {};

var hasRequiredValidateNumberOfEdges;

function requireValidateNumberOfEdges () {
	if (hasRequiredValidateNumberOfEdges) return validateNumberOfEdges;
	hasRequiredValidateNumberOfEdges = 1;
	validateNumberOfEdges.validateNumberOfEdges = function validateNumberOfEdges(numberOfEdges) {
	  if (typeof numberOfEdges !== "number") {
	    const ARGUMENT_TYPE = Array.isArray(numberOfEdges) ? "array" : typeof numberOfEdges;
	    throw new Error(`ERROR! Number of edges has to be a number but was: ${ARGUMENT_TYPE}`);
	  }

	  if (numberOfEdges < 3) {
	    throw new Error(`ERROR! Number of edges has to be at least 3 but was: ${numberOfEdges}`);
	  }
	};
	return validateNumberOfEdges;
}

var validateEarthRadius = {};

var hasRequiredValidateEarthRadius;

function requireValidateEarthRadius () {
	if (hasRequiredValidateEarthRadius) return validateEarthRadius;
	hasRequiredValidateEarthRadius = 1;
	validateEarthRadius.validateEarthRadius = function validateEarthRadius(earthRadius) {
	  if (typeof earthRadius !== "number") {
	    const ARGUMENT_TYPE = Array.isArray(earthRadius) ? "array" : typeof earthRadius;
	    throw new Error(`ERROR! Earth radius has to be a number but was: ${ARGUMENT_TYPE}`);
	  }

	  if (earthRadius <= 0) {
	    throw new Error(`ERROR! Earth radius has to be a positive number but was: ${earthRadius}`);
	  }
	};
	return validateEarthRadius;
}

var validateBearing = {};

var hasRequiredValidateBearing;

function requireValidateBearing () {
	if (hasRequiredValidateBearing) return validateBearing;
	hasRequiredValidateBearing = 1;
	validateBearing.validateBearing = function validateBearing(bearing) {
	  if (typeof bearing !== "number") {
	    const ARGUMENT_TYPE = Array.isArray(bearing) ? "array" : typeof bearing;
	    throw new Error(`ERROR! Bearing has to be a number but was: ${ARGUMENT_TYPE}`);
	  }
	};
	return validateBearing;
}

var hasRequiredInputValidation;

function requireInputValidation () {
	if (hasRequiredInputValidation) return inputValidation;
	hasRequiredInputValidation = 1;
	var validateCenter = requireValidateCenter().validateCenter;
	var validateRadius = requireValidateRadius().validateRadius;
	var validateNumberOfEdges = requireValidateNumberOfEdges().validateNumberOfEdges;
	var validateEarthRadius = requireValidateEarthRadius().validateEarthRadius;
	var validateBearing = requireValidateBearing().validateBearing;

	function validateInput({ center, radius, numberOfEdges, earthRadius, bearing }) {
	  validateCenter(center);
	  validateRadius(radius);
	  validateNumberOfEdges(numberOfEdges);
	  validateEarthRadius(earthRadius);
	  validateBearing(bearing);
	}

	inputValidation.validateCenter = validateCenter;
	inputValidation.validateRadius = validateRadius;
	inputValidation.validateNumberOfEdges = validateNumberOfEdges;
	inputValidation.validateEarthRadius = validateEarthRadius;
	inputValidation.validateBearing = validateBearing;
	inputValidation.validateInput = validateInput;
	return inputValidation;
}

var circleToPolygon$1;
var hasRequiredCircleToPolygon;

function requireCircleToPolygon () {
	if (hasRequiredCircleToPolygon) return circleToPolygon$1;
	hasRequiredCircleToPolygon = 1;
	var { validateInput } = requireInputValidation();

	const defaultEarthRadius = 6378137; // equatorial Earth radius

	function toRadians(angleInDegrees) {
	  return (angleInDegrees * Math.PI) / 180;
	}

	function toDegrees(angleInRadians) {
	  return (angleInRadians * 180) / Math.PI;
	}

	function offset(c1, distance, earthRadius, bearing) {
	  var lat1 = toRadians(c1[1]);
	  var lon1 = toRadians(c1[0]);
	  var dByR = distance / earthRadius;
	  var lat = Math.asin(
	    Math.sin(lat1) * Math.cos(dByR) + Math.cos(lat1) * Math.sin(dByR) * Math.cos(bearing)
	  );
	  var lon =
	    lon1 +
	    Math.atan2(
	      Math.sin(bearing) * Math.sin(dByR) * Math.cos(lat1),
	      Math.cos(dByR) - Math.sin(lat1) * Math.sin(lat)
	    );
	  return [toDegrees(lon), toDegrees(lat)];
	}

	circleToPolygon$1 = function circleToPolygon(center, radius, options) {
	  var n = getNumberOfEdges(options);
	  var earthRadius = getEarthRadius(options);
	  var bearing = getBearing(options);
	  var direction = getDirection(options);

	  // validateInput() throws error on invalid input and do nothing on valid input
	  validateInput({ center, radius, numberOfEdges: n, earthRadius, bearing });

	  var start = toRadians(bearing);
	  var coordinates = [];
	  for (var i = 0; i < n; ++i) {
	    coordinates.push(
	      offset(
	        center, radius, earthRadius, start + (direction * 2 * Math.PI * -i) / n
	      )
	    );
	  }
	  coordinates.push(coordinates[0]);

	  return {
	    type: "Polygon",
	    coordinates: [coordinates]
	  };
	};

	function getNumberOfEdges(options) {
	  if (isUndefinedOrNull(options)) {
	    return 32;
	  } else if (isObjectNotArray(options)) {
	    var numberOfEdges = options.numberOfEdges;
	    return numberOfEdges === undefined ? 32 : numberOfEdges;
	  }
	  return options;
	}

	function getEarthRadius(options) {
	  if (isUndefinedOrNull(options)) {
	    return defaultEarthRadius;
	  } else if (isObjectNotArray(options)) {
	    var earthRadius = options.earthRadius;
	    return earthRadius === undefined ? defaultEarthRadius : earthRadius;
	  }
	  return defaultEarthRadius;
	}

	function getDirection(options){
	  if (isObjectNotArray(options) && options.rightHandRule){
	    return -1;
	  }
	  return 1;
	}

	function getBearing(options) {
	  if (isUndefinedOrNull(options)) {
	    return 0;
	  } else if (isObjectNotArray(options)) {
	    var bearing = options.bearing;
	    return bearing === undefined ? 0 : bearing;
	  }
	  return 0;
	}

	function isObjectNotArray(argument) {
	  return argument !== null && typeof argument === "object" && !Array.isArray(argument);
	}

	function isUndefinedOrNull(argument) {
	  return argument === null || argument === undefined;
	}
	return circleToPolygon$1;
}

var circleToPolygonExports = requireCircleToPolygon();
var circleToPolygon = /*@__PURE__*/getDefaultExportFromCjs(circleToPolygonExports);

function isRadiusLocation(obj) {
    return obj.type === LocationType.RADIUS || 'radius' in obj && obj.radius !== undefined;
}
function isSearchCommuteLocation(obj) {
    return obj.type === LocationType.COMMUTE || 'duration' in obj && obj.duration !== undefined;
}
function isBaseLocation(obj) {
    return obj.type === LocationType.PLACE_ID || !isRadiusLocation(obj) && !isSearchCommuteLocation(obj);
}
/** WALDO -> HYP Location */ const getCriteriaLocation = (searchModel)=>{
    const result = {
        placeIds: [],
        geometries: {
            circles: [],
            polygons: [],
            travels: []
        },
        placeIdInfos: []
    };
    const locations = searchModel.locations;
    if (!locations?.length) {
        return result;
    }
    result.geometries.circles = locations.filter(isRadiusLocation).map((el)=>({
            radius: el.radius,
            radiusUnit: LocationGeometryCircleRadiusUnit.km,
            centerPlaceId: el.placeId,
            locationGeometryCoordinate: {
                latitude: 0,
                longitude: 0
            }
        }));
    result.geometries.travels = locations.filter(isSearchCommuteLocation).map((commute)=>({
            placeIds: commute.placeIds,
            travelMode: [
                searchCommuteToSavedSearchTravelMode[commute.mode]
            ],
            travelUnit: LocationGeometryTravelUnit.MINUTE,
            travelValue: commute.duration
        }));
    result.placeIds = locations.filter(isBaseLocation).map((l)=>l.placeId);
    return result;
};
const searchCommuteToSavedSearchTravelMode = {
    [CommuteMode.BIKE]: LocationGeometryTravelMode.BIKE,
    [CommuteMode.CAR]: LocationGeometryTravelMode.CAR,
    [CommuteMode.TRANSIT]: LocationGeometryTravelMode.TRANSIT,
    [CommuteMode.WALK]: LocationGeometryTravelMode.WALK
};
/** HYP -> WALDO Location */ const getSearchLocation = (criterias)=>{
    const locations = criterias?.location;
    if (!locations) {
        return [];
    }
    const result = [];
    if (locations.placeIds.length) {
        result.push(...locations.placeIds.map((placeId)=>({
                type: LocationType.PLACE_ID,
                placeId
            })));
    }
    if (locations.geometries.circles.length) {
        result.push(...locations.geometries.circles.map(getSearchRadiusLocation).filter((x)=>x !== undefined));
    }
    if (locations.geometries.travels.length) {
        result.push(...locations.geometries.travels.filter((travel)=>travel.travelUnit === LocationGeometryTravelUnit.MINUTE).flatMap((commute)=>commute.travelMode.map((mode)=>({
                    type: LocationType.COMMUTE,
                    duration: commute.travelValue,
                    mode: savedSearchTravelModeToSearchCommute[mode],
                    placeIds: commute.placeIds,
                    polylines: []
                }))));
    }
    return result;
};
const savedSearchTravelModeToSearchCommute = {
    [LocationGeometryTravelMode.BIKE]: CommuteMode.BIKE,
    [LocationGeometryTravelMode.CAR]: CommuteMode.CAR,
    [LocationGeometryTravelMode.TRANSIT]: CommuteMode.TRANSIT,
    [LocationGeometryTravelMode.WALK]: CommuteMode.WALK
};
const getSearchRadiusLocation = ({ radius, centerPlaceId, locationGeometryCoordinate: { latitude, longitude }, radiusUnit })=>{
    // method copy from https://github.com/axel-springer-kugawana/aviv_classified_search_mfe/blob/main/packages/refiner/src/services/getPolyline.ts
    try {
        const radiusInMeter = radiusUnit === LocationGeometryCircleRadiusUnit.km ? radius * 1000 : radius;
        const { coordinates: [firstCoordinate] } = circleToPolygon([
            longitude,
            latitude
        ], radiusInMeter, 32);
        if (!firstCoordinate || firstCoordinate.length === 0) {
            return undefined;
        }
        const polyline = polylineExports.encode(firstCoordinate.map(([longitude, latitude])=>[
                latitude,
                longitude
            ]));
        return {
            type: LocationType.RADIUS,
            placeId: centerPlaceId,
            radius,
            polyline
        };
    } catch  {
        return undefined;
    }
};

/** WALDO -> HYP LocationInBuilding */ const getCriteriaLocationInBuilding = (searchModel)=>{
    const result = new Set();
    for (const locationInBuilding of searchModel.locationsInBuildingIncluded ?? []){
        switch(locationInBuilding){
            case LocationInBuildingType.GROUND_FLOOR:
                result.add(LocationInBuilding.GROUNDFLOOR);
                break;
            case LocationInBuildingType.ROOF_STOREY:
                result.add(LocationInBuilding.ROOF_STOREY);
                break;
        }
    }
    for (const locationInBuilding of searchModel.locationsInBuildingExcluded ?? []){
        switch(locationInBuilding){
            case LocationInBuildingType.GROUND_FLOOR:
                result.add(LocationInBuilding.GROUNDFLOOR_EXCLUDED);
                break;
            case LocationInBuildingType.ROOF_STOREY:
                result.add(LocationInBuilding.ROOF_STOREY_EXCLUDED);
                break;
        }
    }
    return Array.from(result);
};
/** HYP -> WALDO LocationInBuilding */ const getSearchLocationsInBuilding = (criterias)=>{
    const excluded = new Set();
    const included = new Set();
    for (const element of criterias?.structure?.building?.locationInBuilding ?? []){
        switch(element){
            case LocationInBuilding.GROUNDFLOOR:
                included.add(LocationInBuildingType.GROUND_FLOOR);
                break;
            case LocationInBuilding.ROOF_STOREY:
                included.add(LocationInBuildingType.ROOF_STOREY);
                break;
            case LocationInBuilding.GROUNDFLOOR_EXCLUDED:
                excluded.add(LocationInBuildingType.GROUND_FLOOR);
                break;
            case LocationInBuilding.ROOF_STOREY_EXCLUDED:
                excluded.add(LocationInBuildingType.ROOF_STOREY);
                break;
        }
    }
    return {
        exclude: excluded.size ? Array.from(excluded) : undefined,
        include: included.size ? Array.from(included) : undefined
    };
};

const businessEstateTypes = new Set([
    EstateType$1.GASTRONOMY_HOTEL,
    EstateType$1.STORAGE_PRODUCTION,
    EstateType$1.TRADING
]);
/** WALDO -> HYP DistributionType */ const getCriteriaDistributionSubType = (searchModel)=>{
    const result = new Set();
    for (const projectType of searchModel.projectTypes ?? []){
        switch(projectType){
            case ProjectType.INVESTMENT:
                result.add(DistributionSubType.NEW_BUILD_INVESTMENT_PRODUCT);
                break;
            case ProjectType.NEW_BUILD:
                result.add(DistributionSubType.NEW_BUILD_UNIT);
                break;
            case ProjectType.PROJECTED:
                result.add(DistributionSubType.NEW_HOME);
                break;
            case ProjectType.LIFE_ANNUITY:
                result.add(DistributionSubType.LIFE_ANNUITY);
                break;
            case ProjectType.SHORT_TIME_RENTAL:
                result.add(DistributionSubType.TEMPORARY);
                result.add(DistributionSubType.VACATION);
                break;
            case ProjectType.STOCK:
                result.add(DistributionSubType.CLASSIC);
                result.add(DistributionSubType.BUSINESS);
                break;
            case ProjectType.RESALE:
                result.add(DistributionSubType.RESALE);
                if (searchModel.estateTypes?.some((estateType)=>businessEstateTypes.has(estateType))) {
                    result.add(DistributionSubType.BUSINESS_LEASEBACK);
                    result.add(DistributionSubType.BUSINESS_SALE_GOODWILL);
                }
                break;
            case ProjectType.SWAP_APARTMENT:
            case ProjectType.FLATSHARING:
        }
    }
    return Array.from(result);
};
const getCriteriaIsForSwap = (searchModel)=>{
    //https://avivgroup.atlassian.net/wiki/spaces/ATSS/pages/1381073256/studies+SSA+-+IsForSwap+fix#Fix-frontend-mapper
    //only apply to rent
    const isRent = searchModel.distributionTypes?.includes(DistributionType$1.RENT) ?? false;
    if (!isRent) return undefined;
    const projectType = searchModel.projectTypes ?? [];
    const isProjectTypesEmpty = projectType.length === 0;
    //seeker unselected all the project type: no filter is applied
    if (isProjectTypesEmpty) return undefined;
    const isSwapApartmentIncluded = projectType.includes(ProjectType.SWAP_APARTMENT);
    const hasOtherValueIncluded = projectType.filter((type)=>type !== ProjectType.SWAP_APARTMENT).length > 0;
    //seeker look for both swap and something else: isForSwap filter will not be applied
    if (isSwapApartmentIncluded && hasOtherValueIncluded) return undefined;
    //when seeker only look for swap e.g isSwapApartmentIncluded === true
    //nb:DistributionSubType should contain CLASSIC -> it will added by matcher engine if missing
    //https://avivgroup.atlassian.net/wiki/spaces/ATSS/pages/1381073256/studies+SSA+-+IsForSwap+fix#SSA-match-engine
    return isSwapApartmentIncluded;
};
/** HYP -> WALDO ProjectType */ const getSearchProjectType = (criterias)=>{
    const result = new Set();
    const distributionSubTypes = criterias.distributionSubType ?? [];
    for (const distributionSubType of distributionSubTypes){
        switch(distributionSubType){
            case DistributionSubType.BUSINESS_LEASEBACK:
            case DistributionSubType.BUSINESS_SALE_GOODWILL:
                result.add(ProjectType.RESALE);
                break;
            case DistributionSubType.RESALE:
                result.add(ProjectType.RESALE);
                break;
            case DistributionSubType.LIFE_ANNUITY:
                result.add(ProjectType.LIFE_ANNUITY);
                break;
            case DistributionSubType.NEW_BUILD_UNIT:
                result.add(ProjectType.NEW_BUILD);
                break;
            case DistributionSubType.NEW_BUILD_INVESTMENT_PRODUCT:
                result.add(ProjectType.INVESTMENT);
                result.add(ProjectType.NEW_BUILD);
                break;
            case DistributionSubType.NEW_BUILD_PROJECT:
                result.add(ProjectType.NEW_BUILD);
                break;
            case DistributionSubType.NEW_HOME:
                result.add(ProjectType.PROJECTED);
                break;
            case DistributionSubType.BUSINESS:
                result.add(ProjectType.STOCK);
                break;
            case DistributionSubType.CLASSIC:
                result.add(ProjectType.STOCK);
                break;
            case DistributionSubType.TEMPORARY:
                result.add(ProjectType.SHORT_TIME_RENTAL);
                break;
            case DistributionSubType.VACATION:
                result.add(ProjectType.SHORT_TIME_RENTAL);
                break;
        }
    }
    const isRent = criterias.distributionType?.includes(DistributionType.RENT) ?? false;
    if (isRent === true) {
        const distributionSubTypeEmpty = distributionSubTypes.length === 0;
        //https://avivgroup.atlassian.net/wiki/spaces/ATSS/pages/1393229856/SSA+-+MFE+-+SERP+Mapper#SSA-to-SERP
        //true: seeker look for swap - SWAP_APARTMENT is included
        //undefined : seeker don't care - SWAP_APARTMENT is included only when distributionSubType is not empty
        const hasToIncludeSwapApartment = criterias?.management?.isForSwap === true || !distributionSubTypeEmpty && criterias?.management?.isForSwap == undefined;
        if (hasToIncludeSwapApartment) {
            result.add(ProjectType.SWAP_APARTMENT);
        }
    //false: seeker doesn't look for swap - SWAP_APARTMENT is not included
    }
    return result.size ? Array.from(result) : undefined;
};

const useForToManagementUseFor = new Map([
    [
        UseFor.LIVING,
        ManagementUseFor.LIVING
    ],
    [
        UseFor.MIXED,
        ManagementUseFor.MIXED
    ],
    [
        UseFor.COMMERCIAL,
        ManagementUseFor.COMMERCIAL
    ],
    [
        UseFor.INDUSTRIAL,
        ManagementUseFor.INDUSTRIAL
    ],
    [
        UseFor.OFFICE,
        ManagementUseFor.OFFICE
    ]
]);
/** WALDO -> HYP UseFor */ const getCriteriaUseFor = (searchModel)=>{
    return searchModel.useFor?.map((useFor)=>useForToManagementUseFor.get(useFor)).filter((value)=>value !== undefined) ?? [];
};
const managementUseForToUseFor = new Map([
    [
        ManagementUseFor.LIVING,
        UseFor.LIVING
    ],
    [
        ManagementUseFor.MIXED,
        UseFor.MIXED
    ],
    [
        ManagementUseFor.COMMERCIAL,
        UseFor.COMMERCIAL
    ],
    [
        ManagementUseFor.INDUSTRIAL,
        UseFor.INDUSTRIAL
    ],
    [
        ManagementUseFor.OFFICE,
        UseFor.OFFICE
    ]
]);
/** HYP -> WALDO UseFor */ const getSearchUseFor = (criterias)=>{
    const useFor = criterias?.management?.useFor;
    const result = useFor?.map((useFor)=>managementUseForToUseFor.get(useFor))?.filter((value)=>value !== undefined) ?? [];
    return result.length ? result : undefined;
};

const mapSearchDataModelToWLSearch = (searchModel)=>{
    const resultModel = {
        title: '',
        externalProperties: {
            channelId: '',
            deviceType: SavedSearchDeviceType.None
        },
        criteria: getSavedSearchCriteria(searchModel),
        channels: [
            {
                enabled: true,
                channel: SavedSearchNotificationChannel.email
            }
        ]
    };
    return clearObject(resultModel);
};
const getSavedSearchCriteria = (searchModel)=>{
    return {
        estateType: getCriteriaEstateType(searchModel),
        estateSubType: getCriteriaEstateSubType(searchModel),
        distributionType: getCriteriaDistributionType(searchModel),
        distributionSubType: getCriteriaDistributionSubType(searchModel),
        prices: {
            currencySymbol: undefined,
            currencyUnit: undefined,
            priceMin: searchModel.priceMin,
            priceMax: searchModel.priceMax,
            brokerageFee: getCriteriaBrokerageFee(searchModel)
        },
        spaces: {
            spaceMeasureSymbol: undefined,
            spaceMeasureUnit: undefined,
            spaceMin: searchModel.spaceMin,
            spaceMax: searchModel.spaceMax,
            plotSpaceMin: searchModel.plotSpaceMin,
            plotSpaceMax: searchModel.plotSpaceMax
        },
        structure: {
            rooms: {
                numberOfRoomsMin: searchModel.numberOfRoomsMin,
                numberOfRoomsMax: searchModel.numberOfRoomsMax,
                numberOfBalconiesMin: searchModel.featuresIncluded?.includes(FeatureType.BALCONY_TERRACE) ? 1 : undefined,
                numberOfTerracesMin: undefined,
                numberOfBedRoomsMin: searchModel.numberOfBedroomsMin,
                numberOfBedRoomsMax: searchModel.numberOfBedroomsMax
            },
            building: {
                locationInBuilding: getCriteriaLocationInBuilding(searchModel),
                bath: getCriteriaBathroom(searchModel),
                kitchen: getCriteriaKitchen(searchModel),
                withView: {
                    niceView: undefined,
                    withoutOverlooking: undefined
                },
                elevator: getCriteriaElevator(searchModel),
                cellar: getCriteriaCellar(searchModel),
                attic: []
            },
            parkingLots: getCriteriaParkingLots(searchModel)
        },
        conditions: {
            yearOfConstructionMin: searchModel.yearOfConstructionMin,
            yearOfConstructionMax: searchModel.yearOfConstructionMax,
            buildState: getCriteriaBuildState(searchModel),
            energyClass: getCriteriaEnergyCertificateClass(searchModel)
        },
        location: getCriteriaLocation(searchModel),
        management: {
            isRented: getCriteriaIsRented(searchModel),
            isForSwap: getCriteriaIsForSwap(searchModel),
            rent: {
                petsAllowed: getCriteriaPetAllowed(searchModel),
                certificateOfEligibilityNeeded: getCriteriaCertificateOfEligibilityNeeded(searchModel)
            },
            useFor: getCriteriaUseFor(searchModel),
            legalNotes: []
        },
        features: {
            wheelchairUse: getCriteriaWheelchairUse(searchModel),
            garden: getCriteriaGarden(searchModel),
            residential: {
                swimmingPool: getCriteriaSwimmingPool(searchModel)
            },
            propertyOrientation: [],
            furnished: getCriteriaFurnished(searchModel)
        },
        energy: {
            energyType: {
                energyHeatForm: [],
                energyHeatMethod: [],
                energySource: []
            }
        }
    };
};
const reverseWLItemToSearchModel = (item)=>getSearchModelFromSavedSearchCriteria(item?.criteria);
const getSearchModelFromSavedSearchCriteria = (criterias)=>{
    const locationsInBuilding = getSearchLocationsInBuilding(criterias);
    return {
        distributionTypes: getSearchDistributionType(criterias),
        projectTypes: getSearchProjectType(criterias),
        estateTypes: getSearchEstateType(criterias),
        estateSubTypes: getSearchEstateSubType(criterias),
        locations: getSearchLocation(criterias),
        buildState: getSearchBuildState(criterias),
        certificateOfEligibilityNeeded: getSearchCertificateOfEligibilityNeeded(criterias),
        energyCertificateClass: getSearchEnergyCertificateClass(criterias),
        featuresIncluded: getSearchFeaturesIncluded(criterias),
        furnished: getSearchFurnished(criterias),
        useFor: getSearchUseFor(criterias),
        locationsInBuildingIncluded: locationsInBuilding.include,
        locationsInBuildingExcluded: locationsInBuilding.exclude,
        priceMin: criterias?.prices?.priceMin ?? undefined,
        priceMax: criterias?.prices?.priceMax ?? undefined,
        numberOfRoomsMin: criterias?.structure?.rooms?.numberOfRoomsMin ?? undefined,
        numberOfRoomsMax: criterias?.structure?.rooms?.numberOfRoomsMax ?? undefined,
        numberOfBedroomsMax: criterias?.structure?.rooms?.numberOfBedRoomsMax ?? undefined,
        numberOfBedroomsMin: criterias?.structure?.rooms?.numberOfBedRoomsMin ?? undefined,
        spaceMin: criterias?.spaces?.spaceMin ?? undefined,
        spaceMax: criterias?.spaces?.spaceMax ?? undefined,
        plotSpaceMin: criterias?.spaces?.plotSpaceMin ?? undefined,
        plotSpaceMax: criterias?.spaces?.plotSpaceMax ?? undefined,
        yearOfConstructionMin: criterias?.conditions?.yearOfConstructionMin ?? undefined,
        yearOfConstructionMax: criterias?.conditions?.yearOfConstructionMax ?? undefined,
        classifiedBusiness: undefined,
        listingsDisplay: undefined
    };
};

const REST_RETRY_COUNT = 2;
const REST_RETRY_TIMEOUT = 3000;
const legacyAlternativesOn = {
    location: 10,
    primaryPriceMax: 10,
    primaryAreaMin: 10,
    relaxEquipmentMode: LegacyIWTRelaxMode.SINGLE
};
const legacyAlternativesOff = {
    location: 0,
    primaryPriceMax: 0,
    primaryAreaMin: 0,
    relaxEquipmentMode: LegacyIWTRelaxMode.None
};
const errorCodes = {
    tooMany: 'ERROR_TOO_MANY_SAVED_SEARCHES',
    duplicated: 'ERROR_DUPLICATED_SAVED_SEARCH',
    tooLarge: 'ERROR_LOCATION_AREA_TOO_LARGE',
    tooSmall: 'ERROR_LOCATION_AREA_TOO_SMALL',
    duplicatePlaceId: 'ERROR_DUPLICATE_PLACEID',
    placeType: 'ERROR_LOCATION_PLACE_TYPE_NOT_ALLOWED'
};
const savedSearchApiErrorCode = [
    {
        errorCode: errorCodes.tooMany,
        translationKey: null
    },
    {
        errorCode: errorCodes.duplicated,
        translationKey: 'savedSearch_duplicateError_text'
    },
    {
        errorCode: errorCodes.tooLarge,
        translationKey: 'savedSearch_locationTooLarge_text'
    },
    {
        errorCode: errorCodes.tooSmall,
        translationKey: 'savedSearch_locationTooSmall_text'
    },
    {
        errorCode: errorCodes.duplicatePlaceId,
        translationKey: 'savedSearch_duplicatePlaceId_text'
    },
    {
        errorCode: errorCodes.placeType,
        translationKey: 'savedSearch_placeIDforbidden_text'
    }
];
const defaultChannels = [
    {
        channel: SavedSearchNotificationChannel.email,
        enabled: true
    }
];
const savedSearchButtonModalTestId = 'av-ssab-Modal';
const intermediaryMatchingMandateTestId = 'av-ssab-IntermediaryMatchingMandate';
const intermediaryMatchingMandateLokalizePrefix = 'savedSearch.intermediaryMatchingMandate';
const intermediaryMatchingMandateLocalStorageKey = 'showMatchingMandate';
const localKeys = {
    autoLocalStorageSearchKey: 'auto_save_search',
    logSearchDataModel: 'log_search_data_model',
    maxAlertsCustomKey: 'max_alerts_count',
    autoSaveSearchQueryParam: 'autoSaveSearch',
    secondPageAlreadyShown: 'ssal_second_page_already_shown'
};

const createLegacyEndpoint = (stage, brand, route = '')=>{
    switch(brand){
        case 'sl':
            return `https://${stage === 'live' ? 'www' : stage}.seloger.com${route}`;
        case 'imn':
            return `https://${stage === 'live' ? '' : stage + '-'}api.immowelt.com${route}`;
        case 'iwt':
            return `https://${stage === 'live' ? '' : stage + '-'}api.immowelt.com${route}`;
        case 'li':
            return `https://${stage === 'live' ? 'www' : stage}.logic-immo.com${route}`;
        default:
            return `https://${stage === 'live' ? '' : stage + '-'}api.immowelt.com${route}`;
    }
};
/***
 * From https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend_routing
 */ const brandHostnames = {
    dev: {
        imn: 'https://www.dev.immonet.de',
        iwt: 'https://dev.immowelt.de',
        sl: 'https://dev.seloger.com',
        slp: 'https://dev.myselogerpro.com',
        li: 'https://dev.logic-immo.com',
        iwb: 'https://dev.immoweb.be',
        sln: 'https://dev.selogerneuf.com',
        /** doesn't exist atm  */ ma: ''
    },
    preview: {
        imn: 'https://www.preview.immonet.de',
        iwt: 'https://preview.immowelt.de',
        sl: 'https://preview.seloger.com',
        slp: 'https://preview.myselogerpro.com',
        li: 'https://preview.logic-immo.com',
        iwb: 'https://preview.immoweb.be',
        sln: 'https://preview.selogerneuf.com',
        /** doesn't exist atm  */ ma: ''
    },
    live: {
        imn: 'https://www.immonet.de',
        iwt: 'https://www.immowelt.de',
        sl: 'https://www.seloger.com',
        slp: 'https://myselogerpro.com',
        li: 'https://www.logic-immo.com',
        iwb: 'https://www.immoweb.be',
        sln: 'https://www.selogerneuf.com',
        ma: 'https://www.meilleursagents.com'
    }
};
const getBrandHostname = (stage, brand)=>brandHostnames[stage][brand];

const JWT_COOKIE_KEY = 'oauth.access.token';
const CLIENT_TOKEN = 'auth.client.token';
const getToken = ()=>{
    if (!isDocumentUndefined()) {
        const cookieMatchArray = document.cookie.match(`(^|;)\\s*${JWT_COOKIE_KEY}\\s*=\\s*([^;]+)`);
        return cookieMatchArray ? cookieMatchArray.pop() : undefined;
    } else return undefined;
};
const getEPToken = ()=>{
    if (!isDocumentUndefined()) {
        const cookieMatchArray = document.cookie.match(`(^|;)\\s*${'ep-authorization'}\\s*=\\s*([^;]+)`);
        return cookieMatchArray ? cookieMatchArray.pop() : undefined;
    } else return undefined;
};
const getParams = ()=>{
    const params = new URLSearchParams(window?.location?.search ?? '');
    const token = params.get('token');
    const ssid = params.get('ssid');
    const ac = params.get('ac');
    if (!token && !ssid) return null;
    return {
        token,
        ssid,
        ac
    };
};
const getLegacyParams = ()=>{
    const params = new URLSearchParams(window?.location?.search ?? '');
    const idalerteimmo = params.get('idalerteimmo');
    const identifieur = params.get('identifieur');
    if (!idalerteimmo || !identifieur) {
        return null;
    }
    return {
        idalert: idalerteimmo,
        identifier: identifieur
    };
};
const getBasicKey = (basicKeys, brand)=>{
    switch(brand){
        case 'imn':
            return basicKeys.imn;
        case 'iwt':
            return basicKeys.iwt;
        default:
            return basicKeys.imn;
    }
};
const getIWTAnonymousAuthToken = async ({ basicsKey, brand, stage })=>{
    const savedToken = getLocalStorageItem(CLIENT_TOKEN);
    if (savedToken) {
        return savedToken;
    }
    const authKey = getBasicKey(basicsKey, brand);
    const headers = {
        'Content-Type': 'application/json',
        Authorization: `Basic ${btoa(authKey)}`
    };
    const body = {
        grant_type: 'client_credentials'
    };
    const fetchInit = {
        method: 'POST',
        body: JSON.stringify(body),
        headers
    };
    const legacyEndpoint = createLegacyEndpoint(stage, brand);
    const url = legacyEndpoint + '/auth/oauth/token?grant_type=client_credentials';
    const response = await fetch(url, fetchInit);
    const responseJson = await response.json();
    setLocalStorageItem(CLIENT_TOKEN, responseJson.access_token);
    return responseJson.access_token;
};

const baseApiWithResponse = async (success, error, options)=>{
    return getResponseOrThrowError(options).then(async (response)=>{
        const jsonResponse = await response.json();
        const payloadGenericObject = jsonResponse;
        success(payloadGenericObject);
    }).catch((errorResponse)=>{
        error(errorResponse);
    });
};
const baseApiWithoutResponse = async (success, error, options)=>{
    return getResponseOrThrowError(options).then(()=>{
        success();
    }).catch((errorResponse)=>{
        error(errorResponse);
    });
};
function delay(millis) {
    return new Promise((resolve)=>setTimeout(resolve, millis));
}
const getResponseOrThrowError = async (options)=>{
    const { url, httpMethod, body, authenticationToken, retryOnFailure, retryCount, retryDelay, headersOptions } = options;
    const headers = authenticationToken !== undefined ? {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authenticationToken}`,
        ...headersOptions
    } : {
        'Content-Type': 'application/json',
        ...headersOptions
    };
    const fetchInit = {
        method: httpMethod,
        body: JSON.stringify(body),
        headers: headers
    };
    try {
        const response = await fetch$1(url, fetchInit);
        if (!response.ok) {
            const responseStatusString = response.status.toString();
            // Check if the response is a server ERROR
            if (responseStatusString === '401') {
                throw {
                    httpStatusCode: 401,
                    errorMessage: 'Unauthorized',
                    errorCode: 'UNAUTHORIZED'
                };
            }
            if (responseStatusString === '301') {
                throw {
                    httpStatusCode: 301,
                    errorMessage: 'Permanently Moved',
                    errorCode: 'PERMANENTLY_MOVED'
                };
            }
            if (retryOnFailure && retryDelay && retryCount && retryCount >= 0) {
                return delay(retryDelay).then(()=>getResponseOrThrowError({
                        ...options,
                        retryCount: retryCount - 1
                    }));
            }
            if (responseStatusString.startsWith('5')) {
                // eslint-disable-next-line no-throw-literal
                throw {
                    httpStatusCode: 500,
                    errorMessage: 'Internal Error',
                    errorCode: 'INTERNAL_ERROR'
                };
            }
            // eslint-disable-next-line no-throw-literal
            const responseJson = await response.json();
            if (!responseJson.errors || responseJson.errors.length === 0) {
                throw {
                    httpStatusCode: response.status,
                    errorMessage: 'Unknown error',
                    errorCode: 'UNKNOWN_ERROR'
                };
            }
            const responseBody = responseJson.errors[0];
            throw {
                httpStatusCode: response.status,
                errorMessage: responseBody.errorMessage,
                propertyName: responseBody.propertyName,
                errorCode: responseBody.errorCode
            };
        }
        return response;
    } catch (error) {
        if (retryOnFailure && retryDelay && retryCount && retryCount >= 0 && error?.httpStatusCode !== 401) {
            return delay(retryDelay).then(()=>getResponseOrThrowError({
                    ...options,
                    retryCount: retryCount - 1
                }));
        } else throw error;
    }
};

const URL_baseRoute = '/collect-communicate-bff';
const URL_addSavedSearch = `${URL_baseRoute}/createUnified`;
const addSavedSearch = async (success, error, options)=>{
    const { body, authenticationToken } = options;
    return baseApiWithResponse(success, error, {
        url: URL_addSavedSearch,
        httpMethod: 'POST',
        authenticationToken,
        body
    });
};
const URL_addSavedSearchEmail = `${URL_baseRoute}/createUnifiedFromEmail`;
const addSavedSearchEmail = async (success, error, options)=>{
    const { body } = options;
    return baseApiWithResponse(success, error, {
        url: URL_addSavedSearchEmail,
        httpMethod: 'POST',
        body
    });
};
const URL_getLogicImmoMapping = `${URL_baseRoute}/getLogicImmoMapping`;
const addSavedSearchEmailLI = async (success, error, options)=>{
    const { config, body } = options;
    const remappedResponse = await fetch(URL_getLogicImmoMapping, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    }).catch((errorResponse)=>{
        console.error('Error while fetching remapped response', errorResponse);
        error(errorResponse);
    });
    if (!remappedResponse) {
        error({
            errorCode: 'UNKNOWN_ERROR',
            errorMessage: 'Error while fetching remapped response',
            httpStatusCode: 500,
            propertyName: 'error'
        });
        return;
    }
    if (!remappedResponse.ok) {
        error({
            errorCode: 'UNKNOWN_ERROR',
            errorMessage: remappedResponse.statusText,
            httpStatusCode: remappedResponse.status,
            propertyName: 'error'
        });
        return;
    }
    const logicImmoEndpointUrl = createLegacyEndpoint(config.stage, config.brand, '/refonte/aei?context=aeiContactDesktop');
    const remapped = await remappedResponse.text();
    await fetch(logicImmoEndpointUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
            contact: remapped
        })
    }).then((res)=>{
        if (!res.ok) {
            const errorObject = {
                httpStatusCode: res.status,
                errorMessage: res.statusText,
                errorCode: 'error',
                propertyName: 'error'
            };
            error(errorObject);
        } else {
            success(res);
        }
    }).catch((errorResponse)=>{
        console.error('Error while fetching Logic Immo', errorResponse);
        error(errorResponse);
    });
};
const URL_getSavedSearches = `${URL_baseRoute}/getAll`;
const getSavedSearches = async (success, error, options)=>{
    const { authenticationToken } = options;
    return baseApiWithResponse(success, error, {
        url: URL_getSavedSearches,
        httpMethod: 'GET',
        authenticationToken,
        retryOnFailure: true,
        retryCount: REST_RETRY_COUNT,
        retryDelay: REST_RETRY_TIMEOUT
    });
};
const URL_getSavedSearch = `${URL_baseRoute}/get`;
const getSavedSearch = async (success, error, options)=>{
    const { id, authenticationToken } = options;
    return baseApiWithResponse(success, error, {
        url: `${URL_getSavedSearch}?id=${id}`,
        httpMethod: 'GET',
        authenticationToken,
        retryOnFailure: true,
        retryCount: REST_RETRY_COUNT,
        retryDelay: REST_RETRY_TIMEOUT
    });
};
const URL_updateSavedSearch = `${URL_baseRoute}/update`;
const updateSavedSearch = async (success, error, options)=>{
    const { id, authenticationToken, body } = options;
    return baseApiWithResponse(success, error, {
        url: `${URL_updateSavedSearch}?id=${id}`,
        httpMethod: 'PUT',
        authenticationToken,
        body,
        retryOnFailure: true,
        retryCount: REST_RETRY_COUNT,
        retryDelay: REST_RETRY_TIMEOUT
    });
};
const URL_deleteSavedSearch = `${URL_baseRoute}/delete`;
const deleteSavedSearches = async (success, error, options)=>{
    const { id, authenticationToken } = options;
    return baseApiWithoutResponse(success, error, {
        url: `${URL_deleteSavedSearch}?id=${id}`,
        httpMethod: 'DELETE',
        authenticationToken
    });
};
const URL_updateChannelState = `${URL_baseRoute}/channel`;
const updateChannelState = async (success, error, options)=>{
    const { id, authenticationToken, body } = options;
    return baseApiWithoutResponse(success, error, {
        url: `${URL_updateChannelState}?id=${id}`,
        httpMethod: 'PUT',
        authenticationToken,
        body
    });
};
const URL_updateRelaxedCriteria = `${URL_baseRoute}/relaxedCriteria`;
const updateAlternativeState = async (success, error, options)=>{
    const { id, authenticationToken, value } = options;
    return baseApiWithoutResponse(success, error, {
        url: `${URL_updateRelaxedCriteria}?id=${id}`,
        httpMethod: 'PUT',
        authenticationToken,
        body: {
            enabled: value
        }
    });
};
const URL_getSavedSearchMatches = `${URL_baseRoute}/getSavedsearchMatches`;
const getSavedSearchMatches = async (success, error, options)=>{
    const { authenticationToken, savedSearchId } = options;
    return baseApiWithResponse(success, error, {
        url: `${URL_getSavedSearchMatches}?id=${savedSearchId}`,
        httpMethod: 'GET',
        authenticationToken
    });
};
const URL_getMigrationState = `${URL_baseRoute}/getMigrationState`;
const getMigrationState = async (success, error, options)=>{
    const { authenticationToken } = options;
    return baseApiWithResponse(success, error, {
        url: URL_getMigrationState,
        httpMethod: 'GET',
        authenticationToken
    });
};

// LEGACY
const getAnonymousLegacyIWTSavedSearch = async (success, error, options)=>{
    const { config, ssid } = options;
    const token = await getIWTAnonymousAuthToken(config);
    const endpointUrlLegacy = createLegacyEndpoint(config.stage, config.brand, `/savedsearch/v1/savedsearches?includeLookupNotifiedListings=true&referenceSavedSearchId=${ssid}`);
    return baseApiWithResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'GET',
        authenticationToken: token,
        retryOnFailure: true,
        retryCount: REST_RETRY_COUNT,
        retryDelay: REST_RETRY_TIMEOUT
    });
};
const deleteLegacyIWTSavedSearchesAnonymous = async (success, error, options)=>{
    const { config, ssid } = options;
    const endpointUrlLegacy = createLegacyEndpoint(config.stage, config.brand, `/savedsearch/v1/savedsearches/${ssid}`);
    const token = await getIWTAnonymousAuthToken(config);
    return baseApiWithoutResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'DELETE',
        authenticationToken: token
    });
};
const getLegacySLGAnonymous = async (success, error, options)=>{
    const { stage, params } = options;
    const { idalert, identifier } = params;
    const legacyEndpoint = createLegacyEndpoint(stage, 'sl', `/userpreferences-bff/bff/v1/alerts?identifier=${identifier}&idalert=${idalert}`);
    return baseApiWithResponse(success, error, {
        url: legacyEndpoint,
        httpMethod: 'GET'
    });
};
const updateLegacySLGNotificationsStateAnonymous = async (success, error, searchId, value, options)=>{
    const { stage, params } = options;
    const { idalert, identifier } = params;
    const legacyEndpoint = createLegacyEndpoint(stage, 'sl', value ? `/userpreferences-bff/bff/v1/alerts/${searchId}/subscribe?identifier=${identifier}&idalert=${idalert}` : `/userpreferences-bff/bff/v1/alerts/${searchId}/unsubscribe?identifier=${identifier}&idalert=${idalert}`);
    return baseApiWithoutResponse(success, error, {
        url: legacyEndpoint,
        httpMethod: 'POST'
    });
};
const deleteLegacySLGAnonymousSavedSearch = async (success, error, searchId, options)=>{
    const { stage, params } = options;
    const { idalert, identifier } = params;
    const legacyEndpoint = createLegacyEndpoint(stage, 'sl', `/userpreferences-bff/bff/v1/alerts/${searchId}?identifier=${identifier}&idalert=${idalert}`);
    return baseApiWithoutResponse(success, error, {
        url: legacyEndpoint,
        httpMethod: 'DELETE'
    });
};
const updateLegacyIWTNotificationsStateAnonymous = async (success, error, options)=>{
    const { config, ssid, item, value, target } = options;
    const token = await getIWTAnonymousAuthToken(config);
    const emailChannel = item.baseAttributes.endpoints.find((ch)=>ch.channelType === 'EMAIL_MESSAGE');
    const newLegacyChannel = {
        channelType: 'EMAIL_MESSAGE',
        displayName: emailChannel?.displayName ?? '',
        endpointResourceId: emailChannel?.endpointResourceId ?? '',
        notificationFrequency: {
            displayName: value ? 'Sofot' : 'Nie',
            intervalInMinutes: 0,
            type: value ? 'IMMEDIATE' : 'NEVER',
            referenceTimestamp: new Date().toISOString()
        }
    };
    const criterias = {
        ...item?.baseAttributes?.searchCriteria,
        relaxedCriteria: !value ? legacyAlternativesOff : legacyAlternativesOn
    };
    const updateBody = target.includes(NotificationsTargetEnum.EMAIL) && target.includes(NotificationsTargetEnum.RECOMMENDATIONS) ? {
        endpoints: [
            newLegacyChannel
        ],
        searchCriteria: criterias
    } : target.includes(NotificationsTargetEnum.EMAIL) ? {
        endpoints: [
            newLegacyChannel
        ]
    } : {
        searchCriteria: criterias
    };
    const endpointUrlLegacy = createLegacyEndpoint(config.stage, config.brand, '/savedsearch/v1/savedsearchmodifications');
    return baseApiWithResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'PUT',
        authenticationToken: token,
        body: {
            savedSearchId: ssid,
            ...updateBody
        }
    });
};
const updateLegacyNotificationsStatesAnonymous = async (success, error, options)=>{
    const { config, ssid, item, value, target } = options;
    const token = await getIWTAnonymousAuthToken(config);
    const emailChannel = item.baseAttributes.endpoints.find((ch)=>ch.channelType === 'EMAIL_MESSAGE');
    const newLegacyChannel = {
        channelType: 'EMAIL_MESSAGE',
        displayName: emailChannel?.displayName ?? '',
        endpointResourceId: emailChannel?.endpointResourceId ?? '',
        notificationFrequency: {
            displayName: value ? 'Sofot' : 'Nie',
            intervalInMinutes: 0,
            type: value ? 'IMMEDIATE' : 'NEVER',
            referenceTimestamp: new Date().toISOString()
        }
    };
    const criterias = {
        ...item?.baseAttributes?.searchCriteria,
        relaxedCriteria: !value.recommendation ? legacyAlternativesOff : legacyAlternativesOn
    };
    const updateBody = target.includes(NotificationsTargetEnum.EMAIL) && target.includes(NotificationsTargetEnum.RECOMMENDATIONS) ? {
        endpoints: [
            newLegacyChannel
        ],
        searchCriteria: criterias
    } : target.includes(NotificationsTargetEnum.EMAIL) ? {
        endpoints: [
            newLegacyChannel
        ]
    } : {
        searchCriteria: criterias
    };
    const endpointUrlLegacy = createLegacyEndpoint(config.stage, config.brand, '/savedsearch/v1/savedsearchmodifications');
    return baseApiWithResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'PUT',
        authenticationToken: token,
        body: {
            savedSearchId: ssid,
            ...updateBody
        }
    });
};
// WL
const URL_getWLSavedSearchAnonymous = `${URL_baseRoute}/getFromToken`;
const getWLSavedSearchAnonymous = async (success, error, options)=>{
    const { params } = options;
    return baseApiWithResponse(success, error, {
        url: `${URL_getWLSavedSearchAnonymous}?token=${params.token}`,
        httpMethod: 'GET'
    });
};
const URL_updateWLChannelAnonymous = `${URL_baseRoute}/channelFromToken`;
const updateWLChannelAnonymous = async (success, error, options)=>{
    const { params, value } = options;
    const body = {
        channelName: SavedSearchNotificationChannel.email,
        enabled: value !== 'never',
        frequency: value !== 'never' ? value : undefined
    };
    return baseApiWithResponse(success, error, {
        url: `${URL_updateWLChannelAnonymous}?token=${params.token}`,
        httpMethod: 'PUT',
        body
    });
};
const URL_unsubscribeAllWLChannelAnonymous = `${URL_baseRoute}/unsubscribeAll`;
const unsubscribeAllWLChannelAnonymous = async (success, error, options)=>{
    const { params } = options;
    return baseApiWithResponse(success, error, {
        url: `${URL_unsubscribeAllWLChannelAnonymous}?token=${params.token}`,
        httpMethod: 'POST',
        body: {}
    });
};
const URL_updateWLAlternativeAnonymous = `${URL_baseRoute}/relaxedCriteriaFromToken`;
const updateWLAlternativeAnonymous = async (success, error, options)=>{
    const { params, value } = options;
    const body = {
        enabled: value
    };
    return baseApiWithResponse(success, error, {
        url: `${URL_updateWLAlternativeAnonymous}?token=${params.token}`,
        httpMethod: 'PUT',
        body
    });
};

const getLegacyIWTSavedSearches = async (success, error, options)=>{
    const { config, authenticationToken } = options;
    const { stage, brand } = config;
    const endpointUrlLegacy = createLegacyEndpoint(stage, brand, '/savedsearch/v1/savedsearches?includeLookupNotifiedListings=true');
    return baseApiWithResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'GET',
        authenticationToken,
        retryOnFailure: true,
        retryCount: REST_RETRY_COUNT,
        retryDelay: REST_RETRY_TIMEOUT
    });
};
const getLegacySLGSavedSearches = async (success, error, options)=>{
    const { config, authenticationToken } = options;
    const { stage, brand } = config;
    const endpointUrlLegacy = createLegacyEndpoint(stage, brand, '/userpreferences-bff/bff/v1/alerts');
    return baseApiWithResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'GET',
        authenticationToken,
        retryOnFailure: true,
        retryCount: REST_RETRY_COUNT,
        retryDelay: REST_RETRY_TIMEOUT
    });
};
const updateLegacyIWTNotificationsState = async (success, error, options)=>{
    const { id, item, value, target, config, authenticationToken } = options;
    const emailChannel = item.baseAttributes.endpoints.find((ch)=>ch.channelType === 'EMAIL_MESSAGE');
    const newLegacyChannel = {
        channelType: 'EMAIL_MESSAGE',
        displayName: emailChannel?.displayName ?? '',
        endpointResourceId: emailChannel?.endpointResourceId ?? '',
        notificationFrequency: {
            displayName: value ? 'Sofot' : 'Nie',
            intervalInMinutes: 0,
            type: value ? 'IMMEDIATE' : 'NEVER',
            referenceTimestamp: new Date().toISOString()
        }
    };
    const criterias = {
        ...item?.baseAttributes?.searchCriteria,
        relaxedCriteria: !value ? legacyAlternativesOff : legacyAlternativesOn
    };
    const updateBody = target.includes(NotificationsTargetEnum.EMAIL) && target.includes(NotificationsTargetEnum.RECOMMENDATIONS) ? {
        endpoints: [
            newLegacyChannel
        ],
        searchCriteria: criterias
    } : target.includes(NotificationsTargetEnum.EMAIL) ? {
        endpoints: [
            newLegacyChannel
        ]
    } : {
        searchCriteria: criterias
    };
    const endpointUrlLegacy = createLegacyEndpoint(config.stage, config.brand, '/savedsearch/v1/savedsearchmodifications');
    return baseApiWithResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'PUT',
        authenticationToken,
        body: {
            savedSearchId: id,
            ...updateBody
        }
    });
};
const updateLegacyIWTNotificationsStates = async (success, error, options)=>{
    const { id, value, item, target, config, authenticationToken } = options;
    const emailChannel = item.baseAttributes.endpoints.find((ch)=>ch.channelType === 'EMAIL_MESSAGE');
    const newLegacyChannel = {
        channelType: 'EMAIL_MESSAGE',
        displayName: emailChannel?.displayName ?? '',
        endpointResourceId: emailChannel?.endpointResourceId ?? '',
        notificationFrequency: {
            displayName: value.email ? 'Sofot' : 'Nie',
            intervalInMinutes: 0,
            type: value.email ? 'IMMEDIATE' : 'NEVER',
            referenceTimestamp: new Date().toISOString()
        }
    };
    const criterias = {
        ...item?.baseAttributes?.searchCriteria,
        relaxedCriteria: !value.recommendation ? legacyAlternativesOff : legacyAlternativesOn
    };
    const updateBody = target.includes(NotificationsTargetEnum.EMAIL) && target.includes(NotificationsTargetEnum.RECOMMENDATIONS) ? {
        endpoints: [
            newLegacyChannel
        ],
        searchCriteria: criterias
    } : target.includes(NotificationsTargetEnum.EMAIL) ? {
        endpoints: [
            newLegacyChannel
        ]
    } : {
        searchCriteria: criterias
    };
    const endpointUrlLegacy = createLegacyEndpoint(config.stage, config.brand, '/savedsearch/v1/savedsearchmodifications');
    return baseApiWithResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'PUT',
        authenticationToken,
        body: {
            savedSearchId: id,
            ...updateBody
        }
    });
};
const updateLegacySLGNotificationsState = async (success, error, options)=>{
    const { id, value, config, authenticationToken } = options;
    const { stage, brand } = config;
    const endpointUrlLegacy = createLegacyEndpoint(stage, brand, value ? `/userpreferences-bff/bff/v1/alerts/${id}/subscribe` : `/userpreferences-bff/bff/v1/alerts/${id}/unsubscribe`);
    return baseApiWithoutResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'POST',
        authenticationToken
    });
};
const deleteLegacyIWTSavedSearches = async (success, error, options)=>{
    const { id, config, authenticationToken } = options;
    const endpointUrlLegacy = createLegacyEndpoint(config.stage, config.brand, `/savedsearch/v1/savedsearches/${id}`);
    return baseApiWithoutResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'DELETE',
        authenticationToken
    });
};
const deleteLegacySLGSavedSearches = async (success, error, options)=>{
    const { id, config, authenticationToken } = options;
    const { stage, brand } = config;
    const endpointUrlLegacy = createLegacyEndpoint(stage, brand, `/userpreferences-bff/bff/v1/alerts/${id}`);
    return baseApiWithoutResponse(success, error, {
        url: endpointUrlLegacy,
        httpMethod: 'DELETE',
        authenticationToken
    });
};

const mockedLegacySavedSearches = [
    {
        baseAttributes: {
            id: '20240702152114911-e76199bb',
            userId: 386462,
            config: {
                searchDisplayName: 'Haus zum Kauf in Hamburg',
                locationDisplayName: 'Hamburg'
            },
            searchCriteria: {
                pricing: {
                    priceMin: 10,
                    priceMax: 100000
                },
                construction: {
                    roomCountMin: null,
                    roomCountMax: null,
                    livingAreaMin: 0,
                    livingAreaMax: 1000
                },
                general: {
                    equipment: [],
                    estateTypes: [
                        'HAUS'
                    ],
                    distributionTypes: [
                        'ZWANGSVERSTEIGERUNG'
                    ],
                    energyCertificateClass: []
                },
                relaxedCriteria: {
                    location: 0,
                    primaryPriceMax: 0,
                    primaryAreaMin: 0,
                    relaxEquipmentMode: LegacyIWTRelaxMode.None
                }
            },
            endpoints: [
                {
                    channelType: 'EMAIL_MESSAGE',
                    endpointResourceId: 'christoph.semder@immonet.de',
                    displayName: 'christoph.semder@immonet.de',
                    notificationFrequency: {
                        type: 'IMMEDIATE',
                        displayName: 'Sofort',
                        referenceTimestamp: '2002-02-02T02:02:02Z',
                        intervalInMinutes: 5
                    }
                }
            ],
            formattedSearchCriteria: [
                'Haus zum Kauf'
            ],
            origin: 'migration-immonet-wl'
        },
        detailedLocationDisplayText: 'Hamburg'
    }
];
const mockedLegacySavedSearchesToWLResult = [
    {
        criteria: {
            estateType: [
                EstateType.HOUSE
            ],
            distributionType: [
                DistributionType.COMPULSORY_AUCTION
            ],
            distributionSubType: [],
            estateSubType: [],
            energy: {
                energyType: {
                    energyHeatForm: [],
                    energyHeatMethod: [],
                    energySource: []
                }
            },
            prices: {
                currencySymbol: '€',
                currencyUnit: 'EUR',
                priceMin: 10,
                priceMax: 100000,
                brokerageFee: {
                    hasFee: HasFee.None
                }
            },
            spaces: {
                spaceMeasureSymbol: 'm²',
                spaceMeasureUnit: SpaceMeasureUnit.SQUARE_METER,
                spaceMin: 0,
                spaceMax: 1000,
                plotSpaceMin: undefined,
                plotSpaceMax: undefined
            },
            location: {
                geometries: {
                    circles: [],
                    polygons: [],
                    travels: []
                },
                placeIdInfos: [],
                placeIds: []
            },
            structure: {
                rooms: {
                    numberOfRoomsMin: undefined,
                    numberOfRoomsMax: undefined,
                    numberOfBalconiesMin: undefined,
                    numberOfTerracesMin: undefined,
                    numberOfBedRoomsMin: undefined,
                    numberOfBedRoomsMax: undefined
                },
                building: {
                    locationInBuilding: [],
                    bath: {
                        window: undefined,
                        bathtub: undefined
                    },
                    kitchen: {
                        equipment: KitchenEquipment.UNKNOWN
                    },
                    withView: {},
                    elevator: [],
                    cellar: Cellar.None,
                    attic: []
                },
                parkingLots: {}
            },
            conditions: {
                yearOfConstructionMin: undefined,
                yearOfConstructionMax: undefined,
                buildState: [
                    BuildState.None
                ],
                energyClass: [
                    EnergyClass.A,
                    EnergyClass.B
                ]
            },
            management: {
                isRented: undefined,
                isForSwap: undefined,
                rent: {
                    petsAllowed: undefined,
                    certificateOfEligibilityNeeded: undefined
                },
                useFor: [],
                legalNotes: []
            },
            features: {
                wheelchairUse: false,
                garden: [],
                furnished: Furnished.NO,
                residential: {
                    swimmingPool: SwimmingPool.None
                },
                propertyOrientation: []
            }
        },
        title: 'Haus zum Kauf in Hamburg',
        identity: {
            id: '20240702152114911-e76199bb',
            userId: '386462',
            portal: 'migration-immonet-wl'
        },
        state: SavedSearchState.enabled,
        metadata: {
            modelVersion: LEGACY_MODEL_VERSION,
            created: '',
            updated: '',
            origin: SavedSearchOrigin.SavedSearch
        },
        channels: [
            {
                channel: SavedSearchNotificationChannel.email,
                enabled: true
            }
        ],
        externalProperties: {
            channelId: '',
            deviceType: SavedSearchDeviceType.None
        },
        relaxedCriteria: {
            isEnabled: false,
            options: {
                locationSearchAreaIncrease: 10,
                priceIncreaseInPercent: 10,
                relaxedCriteriaRemoval: false,
                surfaceDecreaseInPercent: 10
            }
        }
    }
];
const legacySLMock = {
    searchId: 76823,
    title: 'Mon futur bien à : Perpignan (2)',
    frequency: 1,
    isActive: true,
    searchCriterias: [
        'Vente',
        'Appartement',
        'Divers',
        'Immeuble',
        'Bâtiment',
        'Château',
        'Hôtel particulier',
        'Maison/Villa',
        'Parking/Box',
        'Terrain',
        'Loft/Atelier/Surface',
        'Péniche',
        'Perpignan',
        'entre 20€ et 120000€',
        'entre 10m² et 299m²',
        'entre 30m² et 900m²',
        'Ancien',
        'Neuf',
        '2,3 pièces',
        '1,2,3 chambres',
        'Terrasse',
        'Parking ouvert',
        'Box',
        'Cave',
        'Piscine',
        'Balcon',
        'Ascenseur',
        'Accès handicapé',
        'Garage'
    ],
    searchQueryString: 'basement=1&ci=660136&enterprise=0&epc=A,B&houseboat=1&idtt=2&idtypebien=1,10,11,12,13,14,2,3,4,9&mandatorycommodities=0&naturebien=1,2,4&nb_balconsbtw=1/1&nb_chambres=1,2,3&nb_pieces=2,3&pxbtw=20/120000&si_ascenseur=1&si_boxes=1&si_cave=1&si_handicape=1&si_jardin=1&si_parkings=1&si_piscine=1&si_terrasse=1&sigarage=1&surf_terrainbtw=30/900&surfacebtw=10/299'
};
const legacySLMockAsWL = {
    title: 'Mon futur bien à : Perpignan (2)',
    externalProperties: {
        channelId: '',
        deviceType: SavedSearchDeviceType.None
    },
    state: SavedSearchState.enabled,
    metadata: {
        modelVersion: LEGACY_MODEL_VERSION,
        created: '',
        updated: '',
        origin: SavedSearchOrigin.SavedSearch
    },
    relaxedCriteria: {
        isEnabled: false,
        options: {
            locationSearchAreaIncrease: 10,
            priceIncreaseInPercent: 10,
            relaxedCriteriaRemoval: false,
            surfaceDecreaseInPercent: 10
        }
    },
    identity: {
        id: '76823',
        userId: '1',
        portal: 'sl'
    },
    channels: [
        {
            channel: SavedSearchNotificationChannel.email,
            enabled: true
        }
    ],
    criteria: {
        estateType: [
            EstateType.APARTMENT
        ],
        distributionType: [
            DistributionType.BUY
        ],
        distributionSubType: [],
        estateSubType: [],
        prices: {
            currencySymbol: '€',
            currencyUnit: 'EUR',
            priceMin: undefined,
            priceMax: undefined,
            brokerageFee: {
                hasFee: HasFee.None
            }
        },
        spaces: {
            spaceMeasureSymbol: 'm²',
            spaceMeasureUnit: SpaceMeasureUnit.SQUARE_METER,
            spaceMin: undefined,
            spaceMax: undefined,
            plotSpaceMin: undefined,
            plotSpaceMax: undefined
        },
        location: {
            placeIds: [],
            geometries: {
                circles: [],
                polygons: [],
                travels: []
            },
            placeIdInfos: []
        },
        structure: {
            rooms: {
                numberOfRoomsMin: undefined,
                numberOfRoomsMax: undefined,
                numberOfBalconiesMin: undefined,
                numberOfTerracesMin: undefined,
                numberOfBedRoomsMin: undefined,
                numberOfBedRoomsMax: undefined
            },
            building: {
                locationInBuilding: [],
                bath: {
                    window: undefined,
                    bathtub: undefined
                },
                kitchen: {
                    equipment: KitchenEquipment.UNKNOWN
                },
                withView: {},
                elevator: [],
                cellar: Cellar.None,
                attic: []
            },
            parkingLots: {}
        },
        conditions: {
            yearOfConstructionMax: undefined,
            yearOfConstructionMin: undefined,
            buildState: [],
            energyClass: []
        },
        energy: {
            energyType: {
                energyHeatForm: [],
                energyHeatMethod: [],
                energySource: []
            }
        },
        management: {
            isRented: undefined,
            isForSwap: undefined,
            rent: {
                petsAllowed: undefined,
                certificateOfEligibilityNeeded: undefined
            },
            useFor: [],
            legalNotes: []
        },
        features: {
            wheelchairUse: undefined,
            garden: [],
            furnished: Furnished.None,
            residential: {
                swimmingPool: SwimmingPool.None
            },
            propertyOrientation: []
        }
    }
};

const mockedResponseId = 'fa7aa6b4-1b1e-4116-a888-aaee9b21bb8c';
const cardList = [
    {
        title: '',
        externalProperties: {
            channelId: '',
            deviceType: SavedSearchDeviceType.None
        },
        criteria: {
            estateType: [
                EstateType.HOUSE,
                EstateType.APARTMENT
            ],
            estateSubType: [
                EstateSubType.MANOR_HOUSE
            ],
            distributionType: [
                DistributionType.BUY
            ],
            distributionSubType: [
                DistributionSubType.NEW_HOME
            ],
            prices: {
                currencySymbol: '€',
                currencyUnit: 'EUR',
                priceMin: 0,
                priceMax: 890,
                brokerageFee: {
                    hasFee: HasFee.None
                }
            },
            spaces: {
                spaceMeasureSymbol: 'm²',
                spaceMeasureUnit: SpaceMeasureUnit.SQUARE_METER,
                spaceMin: 0,
                spaceMax: 110,
                plotSpaceMin: 0,
                plotSpaceMax: 0
            },
            structure: {
                rooms: {
                    numberOfRoomsMin: 3,
                    numberOfRoomsMax: undefined,
                    numberOfBalconiesMin: 0,
                    numberOfTerracesMin: undefined,
                    numberOfBedRoomsMin: undefined,
                    numberOfBedRoomsMax: undefined
                },
                building: {
                    locationInBuilding: [],
                    bath: {
                        window: true,
                        bathtub: true
                    },
                    kitchen: {
                        equipment: KitchenEquipment.FULLY_EQUIPPED
                    },
                    withView: {},
                    elevator: [
                        Elevator.ANYKIND
                    ],
                    cellar: Cellar.ANYKIND,
                    attic: []
                },
                parkingLots: {}
            },
            conditions: {
                yearOfConstructionMin: undefined,
                yearOfConstructionMax: undefined,
                buildState: [],
                energyClass: [
                    EnergyClass.A,
                    EnergyClass.B
                ]
            },
            location: {
                placeIds: [
                    'NBH2DE65467'
                ],
                placeIdInfos: [
                    {
                        placeId: 'NBH2DE65467',
                        placeName: 'Allermöhe'
                    }
                ],
                geometries: {
                    circles: [],
                    polygons: [],
                    travels: []
                }
            },
            management: {
                isRented: false,
                isForSwap: true,
                rent: {
                    petsAllowed: true,
                    certificateOfEligibilityNeeded: true
                },
                useFor: [
                    ManagementUseFor.LIVING
                ],
                legalNotes: []
            },
            features: {
                wheelchairUse: undefined,
                garden: [],
                furnished: Furnished.FULL,
                residential: {
                    swimmingPool: SwimmingPool.None
                },
                propertyOrientation: []
            },
            energy: {
                energyType: {
                    energyHeatForm: [],
                    energyHeatMethod: [],
                    energySource: []
                }
            }
        },
        channels: [
            {
                enabled: true,
                channel: SavedSearchNotificationChannel.email
            }
        ]
    },
    {
        title: '',
        externalProperties: {
            channelId: '',
            deviceType: SavedSearchDeviceType.None
        },
        criteria: {
            estateType: [
                EstateType.HOUSE
            ],
            estateSubType: [],
            distributionType: [
                DistributionType.BUY
            ],
            distributionSubType: [
                DistributionSubType.NEW_HOME
            ],
            prices: {
                currencySymbol: '€',
                currencyUnit: 'EUR',
                priceMin: 0,
                priceMax: 1100,
                brokerageFee: {
                    hasFee: HasFee.None
                }
            },
            spaces: {
                spaceMeasureSymbol: 'm²',
                spaceMeasureUnit: SpaceMeasureUnit.SQUARE_METER,
                spaceMin: 0,
                spaceMax: 150,
                plotSpaceMin: 0,
                plotSpaceMax: 0
            },
            structure: {
                rooms: {
                    numberOfRoomsMin: 3,
                    numberOfRoomsMax: undefined,
                    numberOfBalconiesMin: 1,
                    numberOfTerracesMin: undefined,
                    numberOfBedRoomsMin: undefined,
                    numberOfBedRoomsMax: undefined
                },
                building: {
                    locationInBuilding: [
                        LocationInBuilding.ROOF_STOREY_EXCLUDED
                    ],
                    bath: {},
                    elevator: [],
                    kitchen: {
                        equipment: KitchenEquipment.FULLY_EQUIPPED
                    },
                    withView: {},
                    cellar: Cellar.None,
                    attic: []
                },
                parkingLots: {
                    numberOfParkingsMin: 1,
                    numberOfGaragesMin: undefined
                }
            },
            conditions: {
                yearOfConstructionMin: undefined,
                yearOfConstructionMax: undefined,
                buildState: [
                    BuildState.FULLY_RENOVATED
                ],
                energyClass: [
                    EnergyClass.A,
                    EnergyClass.B
                ]
            },
            location: {
                placeIds: [
                    'NBH2DE65467'
                ],
                placeIdInfos: [
                    {
                        placeId: 'NBH2DE65467',
                        placeName: 'Allermöhe'
                    }
                ],
                geometries: {
                    circles: [],
                    polygons: [],
                    travels: []
                }
            },
            management: {
                isRented: undefined,
                isForSwap: true,
                rent: {
                    petsAllowed: true,
                    certificateOfEligibilityNeeded: true
                },
                useFor: [
                    ManagementUseFor.LIVING
                ],
                legalNotes: []
            },
            features: {
                wheelchairUse: true,
                garden: [
                    Garden.ANYKIND
                ],
                furnished: Furnished.FULL,
                residential: {
                    swimmingPool: SwimmingPool.None
                },
                propertyOrientation: []
            },
            energy: {
                energyType: {
                    energyHeatForm: [],
                    energyHeatMethod: [],
                    energySource: []
                }
            }
        },
        channels: [
            {
                enabled: false,
                channel: SavedSearchNotificationChannel.email
            }
        ]
    }
];
const searchModelMock = {
    certificateOfEligibilityNeeded: CertificateOfEligibility.NO,
    featuresIncluded: [
        FeatureType.PETS_ALLOWED,
        FeatureType.BALCONY_TERRACE
    ],
    locationsInBuildingExcluded: [],
    locations: [
        {
            type: LocationType.PLACE_ID,
            placeId: 'NBH2DE65467'
        }
    ],
    estateTypes: [
        EstateType$1.HOUSE,
        EstateType$1.APARTMENT
    ],
    distributionTypes: [
        DistributionType$1.BUY
    ],
    estateSubTypes: [
        EstateSubType$1.CASTLE,
        EstateSubType$1.CHALET
    ],
    projectTypes: [
        ProjectType.SWAP_APARTMENT
    ],
    furnished: [
        Furnished$1.FULL,
        Furnished$1.NO
    ],
    energyCertificateClass: [
        'A',
        'B'
    ],
    useFor: [
        UseFor.LIVING
    ],
    buildState: [
        BuildState$1.NEED_OF_RENOVATION
    ]
};
const searchModelWithRadius = {
    ...searchModelMock,
    locations: [
        {
            type: LocationType.RADIUS,
            placeId: 'NBH2DE65467',
            radius: 10,
            polyline: ''
        }
    ]
};
const searchModelWithCommuteTime = {
    ...searchModelMock,
    locations: [
        {
            type: LocationType.COMMUTE,
            placeIds: [
                'NBH2DE65467'
            ],
            duration: 30,
            mode: CommuteMode.BIKE,
            polylines: []
        }
    ]
};
const ButtonSearchCriteriaMock = {
    searchModel: searchModelMock,
    searchUrl: '?locationIds=113932&marketingtype=1',
    stage: 'dev',
    brand: 'li',
    locale: 'fr',
    type: 'btn',
    origin: 'serp_full_list'
};

const redirectTo = (stage, brand, route)=>{
    const redirectUrl = `${getBrandHostname(stage, brand)}/${route ?? ''}`;
    window.location.href = redirectUrl;
};
const getListingsLink = (stage, brand, searchUrl)=>{
    const url = new URL(getBrandHostname(stage, brand));
    url.pathname = '/classified-search';
    url.search = searchUrl.startsWith('?') ? searchUrl : `?${searchUrl}`;
    return url.toString();
};
const redirectToLogin = (stage, brand, additionalQueryParams, register)=>{
    if (isWindowUndefined()) {
        return;
    }
    const { href, search } = window?.location;
    if (additionalQueryParams && search) {
        additionalQueryParams = '&' + additionalQueryParams;
    } else {
        additionalQueryParams = '';
    }
    const redirectUriParameter = encodeURIComponent(href + additionalQueryParams);
    let signInHostname = getCustomBrandConfig(brand)?.hostnames.signIn;
    if (!signInHostname) {
        return;
    }
    signInHostname = signInHostname.replace('{env}', stage === 'live' ? '' : `${stage}-`);
    window.location.href = `${signInHostname}/?${register ? "screen_hint=signup&" : ""}redirect_uri=${redirectUriParameter}`;
};

const isString = obj => typeof obj === 'string';
const defer = () => {
  let res;
  let rej;
  const promise = new Promise((resolve, reject) => {
    res = resolve;
    rej = reject;
  });
  promise.resolve = res;
  promise.reject = rej;
  return promise;
};
const makeString = object => {
  if (object == null) return '';
  return '' + object;
};
const copy = (a, s, t) => {
  a.forEach(m => {
    if (s[m]) t[m] = s[m];
  });
};
const lastOfPathSeparatorRegExp = /###/g;
const cleanKey = key => key && key.indexOf('###') > -1 ? key.replace(lastOfPathSeparatorRegExp, '.') : key;
const canNotTraverseDeeper = object => !object || isString(object);
const getLastOfPath = (object, path, Empty) => {
  const stack = !isString(path) ? path : path.split('.');
  let stackIndex = 0;
  while (stackIndex < stack.length - 1) {
    if (canNotTraverseDeeper(object)) return {};
    const key = cleanKey(stack[stackIndex]);
    if (!object[key] && Empty) object[key] = new Empty();
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      object = object[key];
    } else {
      object = {};
    }
    ++stackIndex;
  }
  if (canNotTraverseDeeper(object)) return {};
  return {
    obj: object,
    k: cleanKey(stack[stackIndex])
  };
};
const setPath = (object, path, newValue) => {
  const {
    obj,
    k
  } = getLastOfPath(object, path, Object);
  if (obj !== undefined || path.length === 1) {
    obj[k] = newValue;
    return;
  }
  let e = path[path.length - 1];
  let p = path.slice(0, path.length - 1);
  let last = getLastOfPath(object, p, Object);
  while (last.obj === undefined && p.length) {
    e = `${p[p.length - 1]}.${e}`;
    p = p.slice(0, p.length - 1);
    last = getLastOfPath(object, p, Object);
    if (last?.obj && typeof last.obj[`${last.k}.${e}`] !== 'undefined') {
      last.obj = undefined;
    }
  }
  last.obj[`${last.k}.${e}`] = newValue;
};
const pushPath = (object, path, newValue, concat) => {
  const {
    obj,
    k
  } = getLastOfPath(object, path, Object);
  obj[k] = obj[k] || [];
  obj[k].push(newValue);
};
const getPath = (object, path) => {
  const {
    obj,
    k
  } = getLastOfPath(object, path);
  if (!obj) return undefined;
  if (!Object.prototype.hasOwnProperty.call(obj, k)) return undefined;
  return obj[k];
};
const getPathWithDefaults = (data, defaultData, key) => {
  const value = getPath(data, key);
  if (value !== undefined) {
    return value;
  }
  return getPath(defaultData, key);
};
const deepExtend = (target, source, overwrite) => {
  for (const prop in source) {
    if (prop !== '__proto__' && prop !== 'constructor') {
      if (prop in target) {
        if (isString(target[prop]) || target[prop] instanceof String || isString(source[prop]) || source[prop] instanceof String) {
          if (overwrite) target[prop] = source[prop];
        } else {
          deepExtend(target[prop], source[prop], overwrite);
        }
      } else {
        target[prop] = source[prop];
      }
    }
  }
  return target;
};
const regexEscape = str => str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
var _entityMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '/': '&#x2F;'
};
const escape = data => {
  if (isString(data)) {
    return data.replace(/[&<>"'\/]/g, s => _entityMap[s]);
  }
  return data;
};
class RegExpCache {
  constructor(capacity) {
    this.capacity = capacity;
    this.regExpMap = new Map();
    this.regExpQueue = [];
  }
  getRegExp(pattern) {
    const regExpFromCache = this.regExpMap.get(pattern);
    if (regExpFromCache !== undefined) {
      return regExpFromCache;
    }
    const regExpNew = new RegExp(pattern);
    if (this.regExpQueue.length === this.capacity) {
      this.regExpMap.delete(this.regExpQueue.shift());
    }
    this.regExpMap.set(pattern, regExpNew);
    this.regExpQueue.push(pattern);
    return regExpNew;
  }
}
const chars = [' ', ',', '?', '!', ';'];
const looksLikeObjectPathRegExpCache = new RegExpCache(20);
const looksLikeObjectPath = (key, nsSeparator, keySeparator) => {
  nsSeparator = nsSeparator || '';
  keySeparator = keySeparator || '';
  const possibleChars = chars.filter(c => nsSeparator.indexOf(c) < 0 && keySeparator.indexOf(c) < 0);
  if (possibleChars.length === 0) return true;
  const r = looksLikeObjectPathRegExpCache.getRegExp(`(${possibleChars.map(c => c === '?' ? '\\?' : c).join('|')})`);
  let matched = !r.test(key);
  if (!matched) {
    const ki = key.indexOf(keySeparator);
    if (ki > 0 && !r.test(key.substring(0, ki))) {
      matched = true;
    }
  }
  return matched;
};
const deepFind = function (obj, path) {
  let keySeparator = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '.';
  if (!obj) return undefined;
  if (obj[path]) {
    if (!Object.prototype.hasOwnProperty.call(obj, path)) return undefined;
    return obj[path];
  }
  const tokens = path.split(keySeparator);
  let current = obj;
  for (let i = 0; i < tokens.length;) {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    let next;
    let nextPath = '';
    for (let j = i; j < tokens.length; ++j) {
      if (j !== i) {
        nextPath += keySeparator;
      }
      nextPath += tokens[j];
      next = current[nextPath];
      if (next !== undefined) {
        if (['string', 'number', 'boolean'].indexOf(typeof next) > -1 && j < tokens.length - 1) {
          continue;
        }
        i += j - i + 1;
        break;
      }
    }
    current = next;
  }
  return current;
};
const getCleanedCode = code => code?.replace('_', '-');

const consoleLogger = {
  type: 'logger',
  log(args) {
    this.output('log', args);
  },
  warn(args) {
    this.output('warn', args);
  },
  error(args) {
    this.output('error', args);
  },
  output(type, args) {
    console?.[type]?.apply?.(console, args);
  }
};
class Logger {
  constructor(concreteLogger) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    this.init(concreteLogger, options);
  }
  init(concreteLogger) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    this.prefix = options.prefix || 'i18next:';
    this.logger = concreteLogger || consoleLogger;
    this.options = options;
    this.debug = options.debug;
  }
  log() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return this.forward(args, 'log', '', true);
  }
  warn() {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    return this.forward(args, 'warn', '', true);
  }
  error() {
    for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      args[_key3] = arguments[_key3];
    }
    return this.forward(args, 'error', '');
  }
  deprecate() {
    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }
    return this.forward(args, 'warn', 'WARNING DEPRECATED: ', true);
  }
  forward(args, lvl, prefix, debugOnly) {
    if (debugOnly && !this.debug) return null;
    if (isString(args[0])) args[0] = `${prefix}${this.prefix} ${args[0]}`;
    return this.logger[lvl](args);
  }
  create(moduleName) {
    return new Logger(this.logger, {
      ...{
        prefix: `${this.prefix}:${moduleName}:`
      },
      ...this.options
    });
  }
  clone(options) {
    options = options || this.options;
    options.prefix = options.prefix || this.prefix;
    return new Logger(this.logger, options);
  }
}
var baseLogger = new Logger();

class EventEmitter {
  constructor() {
    this.observers = {};
  }
  on(events, listener) {
    events.split(' ').forEach(event => {
      if (!this.observers[event]) this.observers[event] = new Map();
      const numListeners = this.observers[event].get(listener) || 0;
      this.observers[event].set(listener, numListeners + 1);
    });
    return this;
  }
  off(event, listener) {
    if (!this.observers[event]) return;
    if (!listener) {
      delete this.observers[event];
      return;
    }
    this.observers[event].delete(listener);
  }
  emit(event) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    if (this.observers[event]) {
      const cloned = Array.from(this.observers[event].entries());
      cloned.forEach(_ref => {
        let [observer, numTimesAdded] = _ref;
        for (let i = 0; i < numTimesAdded; i++) {
          observer(...args);
        }
      });
    }
    if (this.observers['*']) {
      const cloned = Array.from(this.observers['*'].entries());
      cloned.forEach(_ref2 => {
        let [observer, numTimesAdded] = _ref2;
        for (let i = 0; i < numTimesAdded; i++) {
          observer.apply(observer, [event, ...args]);
        }
      });
    }
  }
}

class ResourceStore extends EventEmitter {
  constructor(data) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      ns: ['translation'],
      defaultNS: 'translation'
    };
    super();
    this.data = data || {};
    this.options = options;
    if (this.options.keySeparator === undefined) {
      this.options.keySeparator = '.';
    }
    if (this.options.ignoreJSONStructure === undefined) {
      this.options.ignoreJSONStructure = true;
    }
  }
  addNamespaces(ns) {
    if (this.options.ns.indexOf(ns) < 0) {
      this.options.ns.push(ns);
    }
  }
  removeNamespaces(ns) {
    const index = this.options.ns.indexOf(ns);
    if (index > -1) {
      this.options.ns.splice(index, 1);
    }
  }
  getResource(lng, ns, key) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    const keySeparator = options.keySeparator !== undefined ? options.keySeparator : this.options.keySeparator;
    const ignoreJSONStructure = options.ignoreJSONStructure !== undefined ? options.ignoreJSONStructure : this.options.ignoreJSONStructure;
    let path;
    if (lng.indexOf('.') > -1) {
      path = lng.split('.');
    } else {
      path = [lng, ns];
      if (key) {
        if (Array.isArray(key)) {
          path.push(...key);
        } else if (isString(key) && keySeparator) {
          path.push(...key.split(keySeparator));
        } else {
          path.push(key);
        }
      }
    }
    const result = getPath(this.data, path);
    if (!result && !ns && !key && lng.indexOf('.') > -1) {
      lng = path[0];
      ns = path[1];
      key = path.slice(2).join('.');
    }
    if (result || !ignoreJSONStructure || !isString(key)) return result;
    return deepFind(this.data?.[lng]?.[ns], key, keySeparator);
  }
  addResource(lng, ns, key, value) {
    let options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {
      silent: false
    };
    const keySeparator = options.keySeparator !== undefined ? options.keySeparator : this.options.keySeparator;
    let path = [lng, ns];
    if (key) path = path.concat(keySeparator ? key.split(keySeparator) : key);
    if (lng.indexOf('.') > -1) {
      path = lng.split('.');
      value = ns;
      ns = path[1];
    }
    this.addNamespaces(ns);
    setPath(this.data, path, value);
    if (!options.silent) this.emit('added', lng, ns, key, value);
  }
  addResources(lng, ns, resources) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {
      silent: false
    };
    for (const m in resources) {
      if (isString(resources[m]) || Array.isArray(resources[m])) this.addResource(lng, ns, m, resources[m], {
        silent: true
      });
    }
    if (!options.silent) this.emit('added', lng, ns, resources);
  }
  addResourceBundle(lng, ns, resources, deep, overwrite) {
    let options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {
      silent: false,
      skipCopy: false
    };
    let path = [lng, ns];
    if (lng.indexOf('.') > -1) {
      path = lng.split('.');
      deep = resources;
      resources = ns;
      ns = path[1];
    }
    this.addNamespaces(ns);
    let pack = getPath(this.data, path) || {};
    if (!options.skipCopy) resources = JSON.parse(JSON.stringify(resources));
    if (deep) {
      deepExtend(pack, resources, overwrite);
    } else {
      pack = {
        ...pack,
        ...resources
      };
    }
    setPath(this.data, path, pack);
    if (!options.silent) this.emit('added', lng, ns, resources);
  }
  removeResourceBundle(lng, ns) {
    if (this.hasResourceBundle(lng, ns)) {
      delete this.data[lng][ns];
    }
    this.removeNamespaces(ns);
    this.emit('removed', lng, ns);
  }
  hasResourceBundle(lng, ns) {
    return this.getResource(lng, ns) !== undefined;
  }
  getResourceBundle(lng, ns) {
    if (!ns) ns = this.options.defaultNS;
    return this.getResource(lng, ns);
  }
  getDataByLanguage(lng) {
    return this.data[lng];
  }
  hasLanguageSomeTranslations(lng) {
    const data = this.getDataByLanguage(lng);
    const n = data && Object.keys(data) || [];
    return !!n.find(v => data[v] && Object.keys(data[v]).length > 0);
  }
  toJSON() {
    return this.data;
  }
}

var postProcessor = {
  processors: {},
  addPostProcessor(module) {
    this.processors[module.name] = module;
  },
  handle(processors, value, key, options, translator) {
    processors.forEach(processor => {
      value = this.processors[processor]?.process(value, key, options, translator) ?? value;
    });
    return value;
  }
};

const checkedLoadedFor = {};
const shouldHandleAsObject = res => !isString(res) && typeof res !== 'boolean' && typeof res !== 'number';
class Translator extends EventEmitter {
  constructor(services) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super();
    copy(['resourceStore', 'languageUtils', 'pluralResolver', 'interpolator', 'backendConnector', 'i18nFormat', 'utils'], services, this);
    this.options = options;
    if (this.options.keySeparator === undefined) {
      this.options.keySeparator = '.';
    }
    this.logger = baseLogger.create('translator');
  }
  changeLanguage(lng) {
    if (lng) this.language = lng;
  }
  exists(key) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      interpolation: {}
    };
    if (key == null) {
      return false;
    }
    const resolved = this.resolve(key, options);
    return resolved?.res !== undefined;
  }
  extractFromKey(key, options) {
    let nsSeparator = options.nsSeparator !== undefined ? options.nsSeparator : this.options.nsSeparator;
    if (nsSeparator === undefined) nsSeparator = ':';
    const keySeparator = options.keySeparator !== undefined ? options.keySeparator : this.options.keySeparator;
    let namespaces = options.ns || this.options.defaultNS || [];
    const wouldCheckForNsInKey = nsSeparator && key.indexOf(nsSeparator) > -1;
    const seemsNaturalLanguage = !this.options.userDefinedKeySeparator && !options.keySeparator && !this.options.userDefinedNsSeparator && !options.nsSeparator && !looksLikeObjectPath(key, nsSeparator, keySeparator);
    if (wouldCheckForNsInKey && !seemsNaturalLanguage) {
      const m = key.match(this.interpolator.nestingRegexp);
      if (m && m.length > 0) {
        return {
          key,
          namespaces: isString(namespaces) ? [namespaces] : namespaces
        };
      }
      const parts = key.split(nsSeparator);
      if (nsSeparator !== keySeparator || nsSeparator === keySeparator && this.options.ns.indexOf(parts[0]) > -1) namespaces = parts.shift();
      key = parts.join(keySeparator);
    }
    return {
      key,
      namespaces: isString(namespaces) ? [namespaces] : namespaces
    };
  }
  translate(keys, options, lastKey) {
    if (typeof options !== 'object' && this.options.overloadTranslationOptionHandler) {
      options = this.options.overloadTranslationOptionHandler(arguments);
    }
    if (typeof options === 'object') options = {
      ...options
    };
    if (!options) options = {};
    if (keys == null) return '';
    if (!Array.isArray(keys)) keys = [String(keys)];
    const returnDetails = options.returnDetails !== undefined ? options.returnDetails : this.options.returnDetails;
    const keySeparator = options.keySeparator !== undefined ? options.keySeparator : this.options.keySeparator;
    const {
      key,
      namespaces
    } = this.extractFromKey(keys[keys.length - 1], options);
    const namespace = namespaces[namespaces.length - 1];
    const lng = options.lng || this.language;
    const appendNamespaceToCIMode = options.appendNamespaceToCIMode || this.options.appendNamespaceToCIMode;
    if (lng?.toLowerCase() === 'cimode') {
      if (appendNamespaceToCIMode) {
        const nsSeparator = options.nsSeparator || this.options.nsSeparator;
        if (returnDetails) {
          return {
            res: `${namespace}${nsSeparator}${key}`,
            usedKey: key,
            exactUsedKey: key,
            usedLng: lng,
            usedNS: namespace,
            usedParams: this.getUsedParamsDetails(options)
          };
        }
        return `${namespace}${nsSeparator}${key}`;
      }
      if (returnDetails) {
        return {
          res: key,
          usedKey: key,
          exactUsedKey: key,
          usedLng: lng,
          usedNS: namespace,
          usedParams: this.getUsedParamsDetails(options)
        };
      }
      return key;
    }
    const resolved = this.resolve(keys, options);
    let res = resolved?.res;
    const resUsedKey = resolved?.usedKey || key;
    const resExactUsedKey = resolved?.exactUsedKey || key;
    const noObject = ['[object Number]', '[object Function]', '[object RegExp]'];
    const joinArrays = options.joinArrays !== undefined ? options.joinArrays : this.options.joinArrays;
    const handleAsObjectInI18nFormat = !this.i18nFormat || this.i18nFormat.handleAsObject;
    const needsPluralHandling = options.count !== undefined && !isString(options.count);
    const hasDefaultValue = Translator.hasDefaultValue(options);
    const defaultValueSuffix = needsPluralHandling ? this.pluralResolver.getSuffix(lng, options.count, options) : '';
    const defaultValueSuffixOrdinalFallback = options.ordinal && needsPluralHandling ? this.pluralResolver.getSuffix(lng, options.count, {
      ordinal: false
    }) : '';
    const needsZeroSuffixLookup = needsPluralHandling && !options.ordinal && options.count === 0;
    const defaultValue = needsZeroSuffixLookup && options[`defaultValue${this.options.pluralSeparator}zero`] || options[`defaultValue${defaultValueSuffix}`] || options[`defaultValue${defaultValueSuffixOrdinalFallback}`] || options.defaultValue;
    let resForObjHndl = res;
    if (handleAsObjectInI18nFormat && !res && hasDefaultValue) {
      resForObjHndl = defaultValue;
    }
    const handleAsObject = shouldHandleAsObject(resForObjHndl);
    const resType = Object.prototype.toString.apply(resForObjHndl);
    if (handleAsObjectInI18nFormat && resForObjHndl && handleAsObject && noObject.indexOf(resType) < 0 && !(isString(joinArrays) && Array.isArray(resForObjHndl))) {
      if (!options.returnObjects && !this.options.returnObjects) {
        if (!this.options.returnedObjectHandler) {
          this.logger.warn('accessing an object - but returnObjects options is not enabled!');
        }
        const r = this.options.returnedObjectHandler ? this.options.returnedObjectHandler(resUsedKey, resForObjHndl, {
          ...options,
          ns: namespaces
        }) : `key '${key} (${this.language})' returned an object instead of string.`;
        if (returnDetails) {
          resolved.res = r;
          resolved.usedParams = this.getUsedParamsDetails(options);
          return resolved;
        }
        return r;
      }
      if (keySeparator) {
        const resTypeIsArray = Array.isArray(resForObjHndl);
        const copy = resTypeIsArray ? [] : {};
        const newKeyToUse = resTypeIsArray ? resExactUsedKey : resUsedKey;
        for (const m in resForObjHndl) {
          if (Object.prototype.hasOwnProperty.call(resForObjHndl, m)) {
            const deepKey = `${newKeyToUse}${keySeparator}${m}`;
            if (hasDefaultValue && !res) {
              copy[m] = this.translate(deepKey, {
                ...options,
                defaultValue: shouldHandleAsObject(defaultValue) ? defaultValue[m] : undefined,
                ...{
                  joinArrays: false,
                  ns: namespaces
                }
              });
            } else {
              copy[m] = this.translate(deepKey, {
                ...options,
                ...{
                  joinArrays: false,
                  ns: namespaces
                }
              });
            }
            if (copy[m] === deepKey) copy[m] = resForObjHndl[m];
          }
        }
        res = copy;
      }
    } else if (handleAsObjectInI18nFormat && isString(joinArrays) && Array.isArray(res)) {
      res = res.join(joinArrays);
      if (res) res = this.extendTranslation(res, keys, options, lastKey);
    } else {
      let usedDefault = false;
      let usedKey = false;
      if (!this.isValidLookup(res) && hasDefaultValue) {
        usedDefault = true;
        res = defaultValue;
      }
      if (!this.isValidLookup(res)) {
        usedKey = true;
        res = key;
      }
      const missingKeyNoValueFallbackToKey = options.missingKeyNoValueFallbackToKey || this.options.missingKeyNoValueFallbackToKey;
      const resForMissing = missingKeyNoValueFallbackToKey && usedKey ? undefined : res;
      const updateMissing = hasDefaultValue && defaultValue !== res && this.options.updateMissing;
      if (usedKey || usedDefault || updateMissing) {
        this.logger.log(updateMissing ? 'updateKey' : 'missingKey', lng, namespace, key, updateMissing ? defaultValue : res);
        if (keySeparator) {
          const fk = this.resolve(key, {
            ...options,
            keySeparator: false
          });
          if (fk && fk.res) this.logger.warn('Seems the loaded translations were in flat JSON format instead of nested. Either set keySeparator: false on init or make sure your translations are published in nested format.');
        }
        let lngs = [];
        const fallbackLngs = this.languageUtils.getFallbackCodes(this.options.fallbackLng, options.lng || this.language);
        if (this.options.saveMissingTo === 'fallback' && fallbackLngs && fallbackLngs[0]) {
          for (let i = 0; i < fallbackLngs.length; i++) {
            lngs.push(fallbackLngs[i]);
          }
        } else if (this.options.saveMissingTo === 'all') {
          lngs = this.languageUtils.toResolveHierarchy(options.lng || this.language);
        } else {
          lngs.push(options.lng || this.language);
        }
        const send = (l, k, specificDefaultValue) => {
          const defaultForMissing = hasDefaultValue && specificDefaultValue !== res ? specificDefaultValue : resForMissing;
          if (this.options.missingKeyHandler) {
            this.options.missingKeyHandler(l, namespace, k, defaultForMissing, updateMissing, options);
          } else if (this.backendConnector?.saveMissing) {
            this.backendConnector.saveMissing(l, namespace, k, defaultForMissing, updateMissing, options);
          }
          this.emit('missingKey', l, namespace, k, res);
        };
        if (this.options.saveMissing) {
          if (this.options.saveMissingPlurals && needsPluralHandling) {
            lngs.forEach(language => {
              const suffixes = this.pluralResolver.getSuffixes(language, options);
              if (needsZeroSuffixLookup && options[`defaultValue${this.options.pluralSeparator}zero`] && suffixes.indexOf(`${this.options.pluralSeparator}zero`) < 0) {
                suffixes.push(`${this.options.pluralSeparator}zero`);
              }
              suffixes.forEach(suffix => {
                send([language], key + suffix, options[`defaultValue${suffix}`] || defaultValue);
              });
            });
          } else {
            send(lngs, key, defaultValue);
          }
        }
      }
      res = this.extendTranslation(res, keys, options, resolved, lastKey);
      if (usedKey && res === key && this.options.appendNamespaceToMissingKey) res = `${namespace}:${key}`;
      if ((usedKey || usedDefault) && this.options.parseMissingKeyHandler) {
        res = this.options.parseMissingKeyHandler(this.options.appendNamespaceToMissingKey ? `${namespace}:${key}` : key, usedDefault ? res : undefined);
      }
    }
    if (returnDetails) {
      resolved.res = res;
      resolved.usedParams = this.getUsedParamsDetails(options);
      return resolved;
    }
    return res;
  }
  extendTranslation(res, key, options, resolved, lastKey) {
    var _this = this;
    if (this.i18nFormat?.parse) {
      res = this.i18nFormat.parse(res, {
        ...this.options.interpolation.defaultVariables,
        ...options
      }, options.lng || this.language || resolved.usedLng, resolved.usedNS, resolved.usedKey, {
        resolved
      });
    } else if (!options.skipInterpolation) {
      if (options.interpolation) this.interpolator.init({
        ...options,
        ...{
          interpolation: {
            ...this.options.interpolation,
            ...options.interpolation
          }
        }
      });
      const skipOnVariables = isString(res) && (options?.interpolation?.skipOnVariables !== undefined ? options.interpolation.skipOnVariables : this.options.interpolation.skipOnVariables);
      let nestBef;
      if (skipOnVariables) {
        const nb = res.match(this.interpolator.nestingRegexp);
        nestBef = nb && nb.length;
      }
      let data = options.replace && !isString(options.replace) ? options.replace : options;
      if (this.options.interpolation.defaultVariables) data = {
        ...this.options.interpolation.defaultVariables,
        ...data
      };
      res = this.interpolator.interpolate(res, data, options.lng || this.language || resolved.usedLng, options);
      if (skipOnVariables) {
        const na = res.match(this.interpolator.nestingRegexp);
        const nestAft = na && na.length;
        if (nestBef < nestAft) options.nest = false;
      }
      if (!options.lng && resolved && resolved.res) options.lng = this.language || resolved.usedLng;
      if (options.nest !== false) res = this.interpolator.nest(res, function () {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }
        if (lastKey?.[0] === args[0] && !options.context) {
          _this.logger.warn(`It seems you are nesting recursively key: ${args[0]} in key: ${key[0]}`);
          return null;
        }
        return _this.translate(...args, key);
      }, options);
      if (options.interpolation) this.interpolator.reset();
    }
    const postProcess = options.postProcess || this.options.postProcess;
    const postProcessorNames = isString(postProcess) ? [postProcess] : postProcess;
    if (res != null && postProcessorNames?.length && options.applyPostProcessor !== false) {
      res = postProcessor.handle(postProcessorNames, res, key, this.options && this.options.postProcessPassResolved ? {
        i18nResolved: {
          ...resolved,
          usedParams: this.getUsedParamsDetails(options)
        },
        ...options
      } : options, this);
    }
    return res;
  }
  resolve(keys) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let found;
    let usedKey;
    let exactUsedKey;
    let usedLng;
    let usedNS;
    if (isString(keys)) keys = [keys];
    keys.forEach(k => {
      if (this.isValidLookup(found)) return;
      const extracted = this.extractFromKey(k, options);
      const key = extracted.key;
      usedKey = key;
      let namespaces = extracted.namespaces;
      if (this.options.fallbackNS) namespaces = namespaces.concat(this.options.fallbackNS);
      const needsPluralHandling = options.count !== undefined && !isString(options.count);
      const needsZeroSuffixLookup = needsPluralHandling && !options.ordinal && options.count === 0;
      const needsContextHandling = options.context !== undefined && (isString(options.context) || typeof options.context === 'number') && options.context !== '';
      const codes = options.lngs ? options.lngs : this.languageUtils.toResolveHierarchy(options.lng || this.language, options.fallbackLng);
      namespaces.forEach(ns => {
        if (this.isValidLookup(found)) return;
        usedNS = ns;
        if (!checkedLoadedFor[`${codes[0]}-${ns}`] && this.utils?.hasLoadedNamespace && !this.utils?.hasLoadedNamespace(usedNS)) {
          checkedLoadedFor[`${codes[0]}-${ns}`] = true;
          this.logger.warn(`key "${usedKey}" for languages "${codes.join(', ')}" won't get resolved as namespace "${usedNS}" was not yet loaded`, 'This means something IS WRONG in your setup. You access the t function before i18next.init / i18next.loadNamespace / i18next.changeLanguage was done. Wait for the callback or Promise to resolve before accessing it!!!');
        }
        codes.forEach(code => {
          if (this.isValidLookup(found)) return;
          usedLng = code;
          const finalKeys = [key];
          if (this.i18nFormat?.addLookupKeys) {
            this.i18nFormat.addLookupKeys(finalKeys, key, code, ns, options);
          } else {
            let pluralSuffix;
            if (needsPluralHandling) pluralSuffix = this.pluralResolver.getSuffix(code, options.count, options);
            const zeroSuffix = `${this.options.pluralSeparator}zero`;
            const ordinalPrefix = `${this.options.pluralSeparator}ordinal${this.options.pluralSeparator}`;
            if (needsPluralHandling) {
              finalKeys.push(key + pluralSuffix);
              if (options.ordinal && pluralSuffix.indexOf(ordinalPrefix) === 0) {
                finalKeys.push(key + pluralSuffix.replace(ordinalPrefix, this.options.pluralSeparator));
              }
              if (needsZeroSuffixLookup) {
                finalKeys.push(key + zeroSuffix);
              }
            }
            if (needsContextHandling) {
              const contextKey = `${key}${this.options.contextSeparator}${options.context}`;
              finalKeys.push(contextKey);
              if (needsPluralHandling) {
                finalKeys.push(contextKey + pluralSuffix);
                if (options.ordinal && pluralSuffix.indexOf(ordinalPrefix) === 0) {
                  finalKeys.push(contextKey + pluralSuffix.replace(ordinalPrefix, this.options.pluralSeparator));
                }
                if (needsZeroSuffixLookup) {
                  finalKeys.push(contextKey + zeroSuffix);
                }
              }
            }
          }
          let possibleKey;
          while (possibleKey = finalKeys.pop()) {
            if (!this.isValidLookup(found)) {
              exactUsedKey = possibleKey;
              found = this.getResource(code, ns, possibleKey, options);
            }
          }
        });
      });
    });
    return {
      res: found,
      usedKey,
      exactUsedKey,
      usedLng,
      usedNS
    };
  }
  isValidLookup(res) {
    return res !== undefined && !(!this.options.returnNull && res === null) && !(!this.options.returnEmptyString && res === '');
  }
  getResource(code, ns, key) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    if (this.i18nFormat?.getResource) return this.i18nFormat.getResource(code, ns, key, options);
    return this.resourceStore.getResource(code, ns, key, options);
  }
  getUsedParamsDetails() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const optionsKeys = ['defaultValue', 'ordinal', 'context', 'replace', 'lng', 'lngs', 'fallbackLng', 'ns', 'keySeparator', 'nsSeparator', 'returnObjects', 'returnDetails', 'joinArrays', 'postProcess', 'interpolation'];
    const useOptionsReplaceForData = options.replace && !isString(options.replace);
    let data = useOptionsReplaceForData ? options.replace : options;
    if (useOptionsReplaceForData && typeof options.count !== 'undefined') {
      data.count = options.count;
    }
    if (this.options.interpolation.defaultVariables) {
      data = {
        ...this.options.interpolation.defaultVariables,
        ...data
      };
    }
    if (!useOptionsReplaceForData) {
      data = {
        ...data
      };
      for (const key of optionsKeys) {
        delete data[key];
      }
    }
    return data;
  }
  static hasDefaultValue(options) {
    const prefix = 'defaultValue';
    for (const option in options) {
      if (Object.prototype.hasOwnProperty.call(options, option) && prefix === option.substring(0, prefix.length) && undefined !== options[option]) {
        return true;
      }
    }
    return false;
  }
}

class LanguageUtil {
  constructor(options) {
    this.options = options;
    this.supportedLngs = this.options.supportedLngs || false;
    this.logger = baseLogger.create('languageUtils');
  }
  getScriptPartFromCode(code) {
    code = getCleanedCode(code);
    if (!code || code.indexOf('-') < 0) return null;
    const p = code.split('-');
    if (p.length === 2) return null;
    p.pop();
    if (p[p.length - 1].toLowerCase() === 'x') return null;
    return this.formatLanguageCode(p.join('-'));
  }
  getLanguagePartFromCode(code) {
    code = getCleanedCode(code);
    if (!code || code.indexOf('-') < 0) return code;
    const p = code.split('-');
    return this.formatLanguageCode(p[0]);
  }
  formatLanguageCode(code) {
    if (isString(code) && code.indexOf('-') > -1) {
      let formattedCode;
      try {
        formattedCode = Intl.getCanonicalLocales(code)[0];
      } catch (e) {}
      if (formattedCode && this.options.lowerCaseLng) {
        formattedCode = formattedCode.toLowerCase();
      }
      if (formattedCode) return formattedCode;
      if (this.options.lowerCaseLng) {
        return code.toLowerCase();
      }
      return code;
    }
    return this.options.cleanCode || this.options.lowerCaseLng ? code.toLowerCase() : code;
  }
  isSupportedCode(code) {
    if (this.options.load === 'languageOnly' || this.options.nonExplicitSupportedLngs) {
      code = this.getLanguagePartFromCode(code);
    }
    return !this.supportedLngs || !this.supportedLngs.length || this.supportedLngs.indexOf(code) > -1;
  }
  getBestMatchFromCodes(codes) {
    if (!codes) return null;
    let found;
    codes.forEach(code => {
      if (found) return;
      const cleanedLng = this.formatLanguageCode(code);
      if (!this.options.supportedLngs || this.isSupportedCode(cleanedLng)) found = cleanedLng;
    });
    if (!found && this.options.supportedLngs) {
      codes.forEach(code => {
        if (found) return;
        const lngOnly = this.getLanguagePartFromCode(code);
        if (this.isSupportedCode(lngOnly)) return found = lngOnly;
        found = this.options.supportedLngs.find(supportedLng => {
          if (supportedLng === lngOnly) return supportedLng;
          if (supportedLng.indexOf('-') < 0 && lngOnly.indexOf('-') < 0) return;
          if (supportedLng.indexOf('-') > 0 && lngOnly.indexOf('-') < 0 && supportedLng.substring(0, supportedLng.indexOf('-')) === lngOnly) return supportedLng;
          if (supportedLng.indexOf(lngOnly) === 0 && lngOnly.length > 1) return supportedLng;
        });
      });
    }
    if (!found) found = this.getFallbackCodes(this.options.fallbackLng)[0];
    return found;
  }
  getFallbackCodes(fallbacks, code) {
    if (!fallbacks) return [];
    if (typeof fallbacks === 'function') fallbacks = fallbacks(code);
    if (isString(fallbacks)) fallbacks = [fallbacks];
    if (Array.isArray(fallbacks)) return fallbacks;
    if (!code) return fallbacks.default || [];
    let found = fallbacks[code];
    if (!found) found = fallbacks[this.getScriptPartFromCode(code)];
    if (!found) found = fallbacks[this.formatLanguageCode(code)];
    if (!found) found = fallbacks[this.getLanguagePartFromCode(code)];
    if (!found) found = fallbacks.default;
    return found || [];
  }
  toResolveHierarchy(code, fallbackCode) {
    const fallbackCodes = this.getFallbackCodes(fallbackCode || this.options.fallbackLng || [], code);
    const codes = [];
    const addCode = c => {
      if (!c) return;
      if (this.isSupportedCode(c)) {
        codes.push(c);
      } else {
        this.logger.warn(`rejecting language code not found in supportedLngs: ${c}`);
      }
    };
    if (isString(code) && (code.indexOf('-') > -1 || code.indexOf('_') > -1)) {
      if (this.options.load !== 'languageOnly') addCode(this.formatLanguageCode(code));
      if (this.options.load !== 'languageOnly' && this.options.load !== 'currentOnly') addCode(this.getScriptPartFromCode(code));
      if (this.options.load !== 'currentOnly') addCode(this.getLanguagePartFromCode(code));
    } else if (isString(code)) {
      addCode(this.formatLanguageCode(code));
    }
    fallbackCodes.forEach(fc => {
      if (codes.indexOf(fc) < 0) addCode(this.formatLanguageCode(fc));
    });
    return codes;
  }
}

const suffixesOrder = {
  zero: 0,
  one: 1,
  two: 2,
  few: 3,
  many: 4,
  other: 5
};
const dummyRule = {
  select: count => count === 1 ? 'one' : 'other',
  resolvedOptions: () => ({
    pluralCategories: ['one', 'other']
  })
};
class PluralResolver {
  constructor(languageUtils) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    this.languageUtils = languageUtils;
    this.options = options;
    this.logger = baseLogger.create('pluralResolver');
    this.pluralRulesCache = {};
  }
  addRule(lng, obj) {
    this.rules[lng] = obj;
  }
  clearCache() {
    this.pluralRulesCache = {};
  }
  getRule(code) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const cleanedCode = getCleanedCode(code === 'dev' ? 'en' : code);
    const type = options.ordinal ? 'ordinal' : 'cardinal';
    const cacheKey = JSON.stringify({
      cleanedCode,
      type
    });
    if (cacheKey in this.pluralRulesCache) {
      return this.pluralRulesCache[cacheKey];
    }
    let rule;
    try {
      rule = new Intl.PluralRules(cleanedCode, {
        type
      });
    } catch (err) {
      if (!Intl) {
        this.logger.error('No Intl support, please use an Intl polyfill!');
        return dummyRule;
      }
      if (!code.match(/-|_/)) return dummyRule;
      const lngPart = this.languageUtils.getLanguagePartFromCode(code);
      rule = this.getRule(lngPart, options);
    }
    this.pluralRulesCache[cacheKey] = rule;
    return rule;
  }
  needsPlural(code) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let rule = this.getRule(code, options);
    if (!rule) rule = this.getRule('dev', options);
    return rule?.resolvedOptions().pluralCategories.length > 1;
  }
  getPluralFormsOfKey(code, key) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    return this.getSuffixes(code, options).map(suffix => `${key}${suffix}`);
  }
  getSuffixes(code) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let rule = this.getRule(code, options);
    if (!rule) rule = this.getRule('dev', options);
    if (!rule) return [];
    return rule.resolvedOptions().pluralCategories.sort((pluralCategory1, pluralCategory2) => suffixesOrder[pluralCategory1] - suffixesOrder[pluralCategory2]).map(pluralCategory => `${this.options.prepend}${options.ordinal ? `ordinal${this.options.prepend}` : ''}${pluralCategory}`);
  }
  getSuffix(code, count) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    const rule = this.getRule(code, options);
    if (rule) {
      return `${this.options.prepend}${options.ordinal ? `ordinal${this.options.prepend}` : ''}${rule.select(count)}`;
    }
    this.logger.warn(`no plural rule found for: ${code}`);
    return this.getSuffix('dev', count, options);
  }
}

const deepFindWithDefaults = function (data, defaultData, key) {
  let keySeparator = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '.';
  let ignoreJSONStructure = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : true;
  let path = getPathWithDefaults(data, defaultData, key);
  if (!path && ignoreJSONStructure && isString(key)) {
    path = deepFind(data, key, keySeparator);
    if (path === undefined) path = deepFind(defaultData, key, keySeparator);
  }
  return path;
};
const regexSafe = val => val.replace(/\$/g, '$$$$');
class Interpolator {
  constructor() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this.logger = baseLogger.create('interpolator');
    this.options = options;
    this.format = options?.interpolation?.format || (value => value);
    this.init(options);
  }
  init() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    if (!options.interpolation) options.interpolation = {
      escapeValue: true
    };
    const {
      escape: escape$1,
      escapeValue,
      useRawValueToEscape,
      prefix,
      prefixEscaped,
      suffix,
      suffixEscaped,
      formatSeparator,
      unescapeSuffix,
      unescapePrefix,
      nestingPrefix,
      nestingPrefixEscaped,
      nestingSuffix,
      nestingSuffixEscaped,
      nestingOptionsSeparator,
      maxReplaces,
      alwaysFormat
    } = options.interpolation;
    this.escape = escape$1 !== undefined ? escape$1 : escape;
    this.escapeValue = escapeValue !== undefined ? escapeValue : true;
    this.useRawValueToEscape = useRawValueToEscape !== undefined ? useRawValueToEscape : false;
    this.prefix = prefix ? regexEscape(prefix) : prefixEscaped || '{{';
    this.suffix = suffix ? regexEscape(suffix) : suffixEscaped || '}}';
    this.formatSeparator = formatSeparator || ',';
    this.unescapePrefix = unescapeSuffix ? '' : unescapePrefix || '-';
    this.unescapeSuffix = this.unescapePrefix ? '' : unescapeSuffix || '';
    this.nestingPrefix = nestingPrefix ? regexEscape(nestingPrefix) : nestingPrefixEscaped || regexEscape('$t(');
    this.nestingSuffix = nestingSuffix ? regexEscape(nestingSuffix) : nestingSuffixEscaped || regexEscape(')');
    this.nestingOptionsSeparator = nestingOptionsSeparator || ',';
    this.maxReplaces = maxReplaces || 1000;
    this.alwaysFormat = alwaysFormat !== undefined ? alwaysFormat : false;
    this.resetRegExp();
  }
  reset() {
    if (this.options) this.init(this.options);
  }
  resetRegExp() {
    const getOrResetRegExp = (existingRegExp, pattern) => {
      if (existingRegExp?.source === pattern) {
        existingRegExp.lastIndex = 0;
        return existingRegExp;
      }
      return new RegExp(pattern, 'g');
    };
    this.regexp = getOrResetRegExp(this.regexp, `${this.prefix}(.+?)${this.suffix}`);
    this.regexpUnescape = getOrResetRegExp(this.regexpUnescape, `${this.prefix}${this.unescapePrefix}(.+?)${this.unescapeSuffix}${this.suffix}`);
    this.nestingRegexp = getOrResetRegExp(this.nestingRegexp, `${this.nestingPrefix}(.+?)${this.nestingSuffix}`);
  }
  interpolate(str, data, lng, options) {
    let match;
    let value;
    let replaces;
    const defaultData = this.options && this.options.interpolation && this.options.interpolation.defaultVariables || {};
    const handleFormat = key => {
      if (key.indexOf(this.formatSeparator) < 0) {
        const path = deepFindWithDefaults(data, defaultData, key, this.options.keySeparator, this.options.ignoreJSONStructure);
        return this.alwaysFormat ? this.format(path, undefined, lng, {
          ...options,
          ...data,
          interpolationkey: key
        }) : path;
      }
      const p = key.split(this.formatSeparator);
      const k = p.shift().trim();
      const f = p.join(this.formatSeparator).trim();
      return this.format(deepFindWithDefaults(data, defaultData, k, this.options.keySeparator, this.options.ignoreJSONStructure), f, lng, {
        ...options,
        ...data,
        interpolationkey: k
      });
    };
    this.resetRegExp();
    const missingInterpolationHandler = options?.missingInterpolationHandler || this.options.missingInterpolationHandler;
    const skipOnVariables = options?.interpolation?.skipOnVariables !== undefined ? options.interpolation.skipOnVariables : this.options.interpolation.skipOnVariables;
    const todos = [{
      regex: this.regexpUnescape,
      safeValue: val => regexSafe(val)
    }, {
      regex: this.regexp,
      safeValue: val => this.escapeValue ? regexSafe(this.escape(val)) : regexSafe(val)
    }];
    todos.forEach(todo => {
      replaces = 0;
      while (match = todo.regex.exec(str)) {
        const matchedVar = match[1].trim();
        value = handleFormat(matchedVar);
        if (value === undefined) {
          if (typeof missingInterpolationHandler === 'function') {
            const temp = missingInterpolationHandler(str, match, options);
            value = isString(temp) ? temp : '';
          } else if (options && Object.prototype.hasOwnProperty.call(options, matchedVar)) {
            value = '';
          } else if (skipOnVariables) {
            value = match[0];
            continue;
          } else {
            this.logger.warn(`missed to pass in variable ${matchedVar} for interpolating ${str}`);
            value = '';
          }
        } else if (!isString(value) && !this.useRawValueToEscape) {
          value = makeString(value);
        }
        const safeValue = todo.safeValue(value);
        str = str.replace(match[0], safeValue);
        if (skipOnVariables) {
          todo.regex.lastIndex += value.length;
          todo.regex.lastIndex -= match[0].length;
        } else {
          todo.regex.lastIndex = 0;
        }
        replaces++;
        if (replaces >= this.maxReplaces) {
          break;
        }
      }
    });
    return str;
  }
  nest(str, fc) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    let match;
    let value;
    let clonedOptions;
    const handleHasOptions = (key, inheritedOptions) => {
      const sep = this.nestingOptionsSeparator;
      if (key.indexOf(sep) < 0) return key;
      const c = key.split(new RegExp(`${sep}[ ]*{`));
      let optionsString = `{${c[1]}`;
      key = c[0];
      optionsString = this.interpolate(optionsString, clonedOptions);
      const matchedSingleQuotes = optionsString.match(/'/g);
      const matchedDoubleQuotes = optionsString.match(/"/g);
      if ((matchedSingleQuotes?.length ?? 0) % 2 === 0 && !matchedDoubleQuotes || matchedDoubleQuotes.length % 2 !== 0) {
        optionsString = optionsString.replace(/'/g, '"');
      }
      try {
        clonedOptions = JSON.parse(optionsString);
        if (inheritedOptions) clonedOptions = {
          ...inheritedOptions,
          ...clonedOptions
        };
      } catch (e) {
        this.logger.warn(`failed parsing options string in nesting for key ${key}`, e);
        return `${key}${sep}${optionsString}`;
      }
      if (clonedOptions.defaultValue && clonedOptions.defaultValue.indexOf(this.prefix) > -1) delete clonedOptions.defaultValue;
      return key;
    };
    while (match = this.nestingRegexp.exec(str)) {
      let formatters = [];
      clonedOptions = {
        ...options
      };
      clonedOptions = clonedOptions.replace && !isString(clonedOptions.replace) ? clonedOptions.replace : clonedOptions;
      clonedOptions.applyPostProcessor = false;
      delete clonedOptions.defaultValue;
      let doReduce = false;
      if (match[0].indexOf(this.formatSeparator) !== -1 && !/{.*}/.test(match[1])) {
        const r = match[1].split(this.formatSeparator).map(elem => elem.trim());
        match[1] = r.shift();
        formatters = r;
        doReduce = true;
      }
      value = fc(handleHasOptions.call(this, match[1].trim(), clonedOptions), clonedOptions);
      if (value && match[0] === str && !isString(value)) return value;
      if (!isString(value)) value = makeString(value);
      if (!value) {
        this.logger.warn(`missed to resolve ${match[1]} for nesting ${str}`);
        value = '';
      }
      if (doReduce) {
        value = formatters.reduce((v, f) => this.format(v, f, options.lng, {
          ...options,
          interpolationkey: match[1].trim()
        }), value.trim());
      }
      str = str.replace(match[0], value);
      this.regexp.lastIndex = 0;
    }
    return str;
  }
}

const parseFormatStr = formatStr => {
  let formatName = formatStr.toLowerCase().trim();
  const formatOptions = {};
  if (formatStr.indexOf('(') > -1) {
    const p = formatStr.split('(');
    formatName = p[0].toLowerCase().trim();
    const optStr = p[1].substring(0, p[1].length - 1);
    if (formatName === 'currency' && optStr.indexOf(':') < 0) {
      if (!formatOptions.currency) formatOptions.currency = optStr.trim();
    } else if (formatName === 'relativetime' && optStr.indexOf(':') < 0) {
      if (!formatOptions.range) formatOptions.range = optStr.trim();
    } else {
      const opts = optStr.split(';');
      opts.forEach(opt => {
        if (opt) {
          const [key, ...rest] = opt.split(':');
          const val = rest.join(':').trim().replace(/^'+|'+$/g, '');
          const trimmedKey = key.trim();
          if (!formatOptions[trimmedKey]) formatOptions[trimmedKey] = val;
          if (val === 'false') formatOptions[trimmedKey] = false;
          if (val === 'true') formatOptions[trimmedKey] = true;
          if (!isNaN(val)) formatOptions[trimmedKey] = parseInt(val, 10);
        }
      });
    }
  }
  return {
    formatName,
    formatOptions
  };
};
const createCachedFormatter = fn => {
  const cache = {};
  return (val, lng, options) => {
    let optForCache = options;
    if (options && options.interpolationkey && options.formatParams && options.formatParams[options.interpolationkey] && options[options.interpolationkey]) {
      optForCache = {
        ...optForCache,
        [options.interpolationkey]: undefined
      };
    }
    const key = lng + JSON.stringify(optForCache);
    let formatter = cache[key];
    if (!formatter) {
      formatter = fn(getCleanedCode(lng), options);
      cache[key] = formatter;
    }
    return formatter(val);
  };
};
class Formatter {
  constructor() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this.logger = baseLogger.create('formatter');
    this.options = options;
    this.formats = {
      number: createCachedFormatter((lng, opt) => {
        const formatter = new Intl.NumberFormat(lng, {
          ...opt
        });
        return val => formatter.format(val);
      }),
      currency: createCachedFormatter((lng, opt) => {
        const formatter = new Intl.NumberFormat(lng, {
          ...opt,
          style: 'currency'
        });
        return val => formatter.format(val);
      }),
      datetime: createCachedFormatter((lng, opt) => {
        const formatter = new Intl.DateTimeFormat(lng, {
          ...opt
        });
        return val => formatter.format(val);
      }),
      relativetime: createCachedFormatter((lng, opt) => {
        const formatter = new Intl.RelativeTimeFormat(lng, {
          ...opt
        });
        return val => formatter.format(val, opt.range || 'day');
      }),
      list: createCachedFormatter((lng, opt) => {
        const formatter = new Intl.ListFormat(lng, {
          ...opt
        });
        return val => formatter.format(val);
      })
    };
    this.init(options);
  }
  init(services) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      interpolation: {}
    };
    this.formatSeparator = options.interpolation.formatSeparator || ',';
  }
  add(name, fc) {
    this.formats[name.toLowerCase().trim()] = fc;
  }
  addCached(name, fc) {
    this.formats[name.toLowerCase().trim()] = createCachedFormatter(fc);
  }
  format(value, format, lng) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    const formats = format.split(this.formatSeparator);
    if (formats.length > 1 && formats[0].indexOf('(') > 1 && formats[0].indexOf(')') < 0 && formats.find(f => f.indexOf(')') > -1)) {
      const lastIndex = formats.findIndex(f => f.indexOf(')') > -1);
      formats[0] = [formats[0], ...formats.splice(1, lastIndex)].join(this.formatSeparator);
    }
    const result = formats.reduce((mem, f) => {
      const {
        formatName,
        formatOptions
      } = parseFormatStr(f);
      if (this.formats[formatName]) {
        let formatted = mem;
        try {
          const valOptions = options?.formatParams?.[options.interpolationkey] || {};
          const l = valOptions.locale || valOptions.lng || options.locale || options.lng || lng;
          formatted = this.formats[formatName](mem, l, {
            ...formatOptions,
            ...options,
            ...valOptions
          });
        } catch (error) {
          this.logger.warn(error);
        }
        return formatted;
      } else {
        this.logger.warn(`there was no format function for ${formatName}`);
      }
      return mem;
    }, value);
    return result;
  }
}

const removePending = (q, name) => {
  if (q.pending[name] !== undefined) {
    delete q.pending[name];
    q.pendingCount--;
  }
};
class Connector extends EventEmitter {
  constructor(backend, store, services) {
    let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    super();
    this.backend = backend;
    this.store = store;
    this.services = services;
    this.languageUtils = services.languageUtils;
    this.options = options;
    this.logger = baseLogger.create('backendConnector');
    this.waitingReads = [];
    this.maxParallelReads = options.maxParallelReads || 10;
    this.readingCalls = 0;
    this.maxRetries = options.maxRetries >= 0 ? options.maxRetries : 5;
    this.retryTimeout = options.retryTimeout >= 1 ? options.retryTimeout : 350;
    this.state = {};
    this.queue = [];
    this.backend?.init?.(services, options.backend, options);
  }
  queueLoad(languages, namespaces, options, callback) {
    const toLoad = {};
    const pending = {};
    const toLoadLanguages = {};
    const toLoadNamespaces = {};
    languages.forEach(lng => {
      let hasAllNamespaces = true;
      namespaces.forEach(ns => {
        const name = `${lng}|${ns}`;
        if (!options.reload && this.store.hasResourceBundle(lng, ns)) {
          this.state[name] = 2;
        } else if (this.state[name] < 0) ; else if (this.state[name] === 1) {
          if (pending[name] === undefined) pending[name] = true;
        } else {
          this.state[name] = 1;
          hasAllNamespaces = false;
          if (pending[name] === undefined) pending[name] = true;
          if (toLoad[name] === undefined) toLoad[name] = true;
          if (toLoadNamespaces[ns] === undefined) toLoadNamespaces[ns] = true;
        }
      });
      if (!hasAllNamespaces) toLoadLanguages[lng] = true;
    });
    if (Object.keys(toLoad).length || Object.keys(pending).length) {
      this.queue.push({
        pending,
        pendingCount: Object.keys(pending).length,
        loaded: {},
        errors: [],
        callback
      });
    }
    return {
      toLoad: Object.keys(toLoad),
      pending: Object.keys(pending),
      toLoadLanguages: Object.keys(toLoadLanguages),
      toLoadNamespaces: Object.keys(toLoadNamespaces)
    };
  }
  loaded(name, err, data) {
    const s = name.split('|');
    const lng = s[0];
    const ns = s[1];
    if (err) this.emit('failedLoading', lng, ns, err);
    if (!err && data) {
      this.store.addResourceBundle(lng, ns, data, undefined, undefined, {
        skipCopy: true
      });
    }
    this.state[name] = err ? -1 : 2;
    if (err && data) this.state[name] = 0;
    const loaded = {};
    this.queue.forEach(q => {
      pushPath(q.loaded, [lng], ns);
      removePending(q, name);
      if (err) q.errors.push(err);
      if (q.pendingCount === 0 && !q.done) {
        Object.keys(q.loaded).forEach(l => {
          if (!loaded[l]) loaded[l] = {};
          const loadedKeys = q.loaded[l];
          if (loadedKeys.length) {
            loadedKeys.forEach(n => {
              if (loaded[l][n] === undefined) loaded[l][n] = true;
            });
          }
        });
        q.done = true;
        if (q.errors.length) {
          q.callback(q.errors);
        } else {
          q.callback();
        }
      }
    });
    this.emit('loaded', loaded);
    this.queue = this.queue.filter(q => !q.done);
  }
  read(lng, ns, fcName) {
    let tried = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
    let wait = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : this.retryTimeout;
    let callback = arguments.length > 5 ? arguments[5] : undefined;
    if (!lng.length) return callback(null, {});
    if (this.readingCalls >= this.maxParallelReads) {
      this.waitingReads.push({
        lng,
        ns,
        fcName,
        tried,
        wait,
        callback
      });
      return;
    }
    this.readingCalls++;
    const resolver = (err, data) => {
      this.readingCalls--;
      if (this.waitingReads.length > 0) {
        const next = this.waitingReads.shift();
        this.read(next.lng, next.ns, next.fcName, next.tried, next.wait, next.callback);
      }
      if (err && data && tried < this.maxRetries) {
        setTimeout(() => {
          this.read.call(this, lng, ns, fcName, tried + 1, wait * 2, callback);
        }, wait);
        return;
      }
      callback(err, data);
    };
    const fc = this.backend[fcName].bind(this.backend);
    if (fc.length === 2) {
      try {
        const r = fc(lng, ns);
        if (r && typeof r.then === 'function') {
          r.then(data => resolver(null, data)).catch(resolver);
        } else {
          resolver(null, r);
        }
      } catch (err) {
        resolver(err);
      }
      return;
    }
    return fc(lng, ns, resolver);
  }
  prepareLoading(languages, namespaces) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    let callback = arguments.length > 3 ? arguments[3] : undefined;
    if (!this.backend) {
      this.logger.warn('No backend was added via i18next.use. Will not load resources.');
      return callback && callback();
    }
    if (isString(languages)) languages = this.languageUtils.toResolveHierarchy(languages);
    if (isString(namespaces)) namespaces = [namespaces];
    const toLoad = this.queueLoad(languages, namespaces, options, callback);
    if (!toLoad.toLoad.length) {
      if (!toLoad.pending.length) callback();
      return null;
    }
    toLoad.toLoad.forEach(name => {
      this.loadOne(name);
    });
  }
  load(languages, namespaces, callback) {
    this.prepareLoading(languages, namespaces, {}, callback);
  }
  reload(languages, namespaces, callback) {
    this.prepareLoading(languages, namespaces, {
      reload: true
    }, callback);
  }
  loadOne(name) {
    let prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    const s = name.split('|');
    const lng = s[0];
    const ns = s[1];
    this.read(lng, ns, 'read', undefined, undefined, (err, data) => {
      if (err) this.logger.warn(`${prefix}loading namespace ${ns} for language ${lng} failed`, err);
      if (!err && data) this.logger.log(`${prefix}loaded namespace ${ns} for language ${lng}`, data);
      this.loaded(name, err, data);
    });
  }
  saveMissing(languages, namespace, key, fallbackValue, isUpdate) {
    let options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    let clb = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : () => {};
    if (this.services?.utils?.hasLoadedNamespace && !this.services?.utils?.hasLoadedNamespace(namespace)) {
      this.logger.warn(`did not save key "${key}" as the namespace "${namespace}" was not yet loaded`, 'This means something IS WRONG in your setup. You access the t function before i18next.init / i18next.loadNamespace / i18next.changeLanguage was done. Wait for the callback or Promise to resolve before accessing it!!!');
      return;
    }
    if (key === undefined || key === null || key === '') return;
    if (this.backend?.create) {
      const opts = {
        ...options,
        isUpdate
      };
      const fc = this.backend.create.bind(this.backend);
      if (fc.length < 6) {
        try {
          let r;
          if (fc.length === 5) {
            r = fc(languages, namespace, key, fallbackValue, opts);
          } else {
            r = fc(languages, namespace, key, fallbackValue);
          }
          if (r && typeof r.then === 'function') {
            r.then(data => clb(null, data)).catch(clb);
          } else {
            clb(null, r);
          }
        } catch (err) {
          clb(err);
        }
      } else {
        fc(languages, namespace, key, fallbackValue, clb, opts);
      }
    }
    if (!languages || !languages[0]) return;
    this.store.addResource(languages[0], namespace, key, fallbackValue);
  }
}

const get = () => ({
  debug: false,
  initAsync: true,
  ns: ['translation'],
  defaultNS: ['translation'],
  fallbackLng: ['dev'],
  fallbackNS: false,
  supportedLngs: false,
  nonExplicitSupportedLngs: false,
  load: 'all',
  preload: false,
  simplifyPluralSuffix: true,
  keySeparator: '.',
  nsSeparator: ':',
  pluralSeparator: '_',
  contextSeparator: '_',
  partialBundledLanguages: false,
  saveMissing: false,
  updateMissing: false,
  saveMissingTo: 'fallback',
  saveMissingPlurals: true,
  missingKeyHandler: false,
  missingInterpolationHandler: false,
  postProcess: false,
  postProcessPassResolved: false,
  returnNull: false,
  returnEmptyString: true,
  returnObjects: false,
  joinArrays: false,
  returnedObjectHandler: false,
  parseMissingKeyHandler: false,
  appendNamespaceToMissingKey: false,
  appendNamespaceToCIMode: false,
  overloadTranslationOptionHandler: args => {
    let ret = {};
    if (typeof args[1] === 'object') ret = args[1];
    if (isString(args[1])) ret.defaultValue = args[1];
    if (isString(args[2])) ret.tDescription = args[2];
    if (typeof args[2] === 'object' || typeof args[3] === 'object') {
      const options = args[3] || args[2];
      Object.keys(options).forEach(key => {
        ret[key] = options[key];
      });
    }
    return ret;
  },
  interpolation: {
    escapeValue: true,
    format: value => value,
    prefix: '{{',
    suffix: '}}',
    formatSeparator: ',',
    unescapePrefix: '-',
    nestingPrefix: '$t(',
    nestingSuffix: ')',
    nestingOptionsSeparator: ',',
    maxReplaces: 1000,
    skipOnVariables: true
  }
});
const transformOptions = options => {
  if (isString(options.ns)) options.ns = [options.ns];
  if (isString(options.fallbackLng)) options.fallbackLng = [options.fallbackLng];
  if (isString(options.fallbackNS)) options.fallbackNS = [options.fallbackNS];
  if (options.supportedLngs?.indexOf?.('cimode') < 0) {
    options.supportedLngs = options.supportedLngs.concat(['cimode']);
  }
  if (typeof options.initImmediate === 'boolean') options.initAsync = options.initImmediate;
  return options;
};

const noop = () => {};
const bindMemberFunctions = inst => {
  const mems = Object.getOwnPropertyNames(Object.getPrototypeOf(inst));
  mems.forEach(mem => {
    if (typeof inst[mem] === 'function') {
      inst[mem] = inst[mem].bind(inst);
    }
  });
};
class I18n extends EventEmitter {
  constructor() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let callback = arguments.length > 1 ? arguments[1] : undefined;
    super();
    this.options = transformOptions(options);
    this.services = {};
    this.logger = baseLogger;
    this.modules = {
      external: []
    };
    bindMemberFunctions(this);
    if (callback && !this.isInitialized && !options.isClone) {
      if (!this.options.initAsync) {
        this.init(options, callback);
        return this;
      }
      setTimeout(() => {
        this.init(options, callback);
      }, 0);
    }
  }
  init() {
    var _this = this;
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let callback = arguments.length > 1 ? arguments[1] : undefined;
    this.isInitializing = true;
    if (typeof options === 'function') {
      callback = options;
      options = {};
    }
    if (options.defaultNS == null && options.ns) {
      if (isString(options.ns)) {
        options.defaultNS = options.ns;
      } else if (options.ns.indexOf('translation') < 0) {
        options.defaultNS = options.ns[0];
      }
    }
    const defOpts = get();
    this.options = {
      ...defOpts,
      ...this.options,
      ...transformOptions(options)
    };
    this.options.interpolation = {
      ...defOpts.interpolation,
      ...this.options.interpolation
    };
    if (options.keySeparator !== undefined) {
      this.options.userDefinedKeySeparator = options.keySeparator;
    }
    if (options.nsSeparator !== undefined) {
      this.options.userDefinedNsSeparator = options.nsSeparator;
    }
    const createClassOnDemand = ClassOrObject => {
      if (!ClassOrObject) return null;
      if (typeof ClassOrObject === 'function') return new ClassOrObject();
      return ClassOrObject;
    };
    if (!this.options.isClone) {
      if (this.modules.logger) {
        baseLogger.init(createClassOnDemand(this.modules.logger), this.options);
      } else {
        baseLogger.init(null, this.options);
      }
      let formatter;
      if (this.modules.formatter) {
        formatter = this.modules.formatter;
      } else {
        formatter = Formatter;
      }
      const lu = new LanguageUtil(this.options);
      this.store = new ResourceStore(this.options.resources, this.options);
      const s = this.services;
      s.logger = baseLogger;
      s.resourceStore = this.store;
      s.languageUtils = lu;
      s.pluralResolver = new PluralResolver(lu, {
        prepend: this.options.pluralSeparator,
        simplifyPluralSuffix: this.options.simplifyPluralSuffix
      });
      if (formatter && (!this.options.interpolation.format || this.options.interpolation.format === defOpts.interpolation.format)) {
        s.formatter = createClassOnDemand(formatter);
        s.formatter.init(s, this.options);
        this.options.interpolation.format = s.formatter.format.bind(s.formatter);
      }
      s.interpolator = new Interpolator(this.options);
      s.utils = {
        hasLoadedNamespace: this.hasLoadedNamespace.bind(this)
      };
      s.backendConnector = new Connector(createClassOnDemand(this.modules.backend), s.resourceStore, s, this.options);
      s.backendConnector.on('*', function (event) {
        for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }
        _this.emit(event, ...args);
      });
      if (this.modules.languageDetector) {
        s.languageDetector = createClassOnDemand(this.modules.languageDetector);
        if (s.languageDetector.init) s.languageDetector.init(s, this.options.detection, this.options);
      }
      if (this.modules.i18nFormat) {
        s.i18nFormat = createClassOnDemand(this.modules.i18nFormat);
        if (s.i18nFormat.init) s.i18nFormat.init(this);
      }
      this.translator = new Translator(this.services, this.options);
      this.translator.on('*', function (event) {
        for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
          args[_key2 - 1] = arguments[_key2];
        }
        _this.emit(event, ...args);
      });
      this.modules.external.forEach(m => {
        if (m.init) m.init(this);
      });
    }
    this.format = this.options.interpolation.format;
    if (!callback) callback = noop;
    if (this.options.fallbackLng && !this.services.languageDetector && !this.options.lng) {
      const codes = this.services.languageUtils.getFallbackCodes(this.options.fallbackLng);
      if (codes.length > 0 && codes[0] !== 'dev') this.options.lng = codes[0];
    }
    if (!this.services.languageDetector && !this.options.lng) {
      this.logger.warn('init: no languageDetector is used and no lng is defined');
    }
    const storeApi = ['getResource', 'hasResourceBundle', 'getResourceBundle', 'getDataByLanguage'];
    storeApi.forEach(fcName => {
      this[fcName] = function () {
        return _this.store[fcName](...arguments);
      };
    });
    const storeApiChained = ['addResource', 'addResources', 'addResourceBundle', 'removeResourceBundle'];
    storeApiChained.forEach(fcName => {
      this[fcName] = function () {
        _this.store[fcName](...arguments);
        return _this;
      };
    });
    const deferred = defer();
    const load = () => {
      const finish = (err, t) => {
        this.isInitializing = false;
        if (this.isInitialized && !this.initializedStoreOnce) this.logger.warn('init: i18next is already initialized. You should call init just once!');
        this.isInitialized = true;
        if (!this.options.isClone) this.logger.log('initialized', this.options);
        this.emit('initialized', this.options);
        deferred.resolve(t);
        callback(err, t);
      };
      if (this.languages && !this.isInitialized) return finish(null, this.t.bind(this));
      this.changeLanguage(this.options.lng, finish);
    };
    if (this.options.resources || !this.options.initAsync) {
      load();
    } else {
      setTimeout(load, 0);
    }
    return deferred;
  }
  loadResources(language) {
    let callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
    let usedCallback = callback;
    const usedLng = isString(language) ? language : this.language;
    if (typeof language === 'function') usedCallback = language;
    if (!this.options.resources || this.options.partialBundledLanguages) {
      if (usedLng?.toLowerCase() === 'cimode' && (!this.options.preload || this.options.preload.length === 0)) return usedCallback();
      const toLoad = [];
      const append = lng => {
        if (!lng) return;
        if (lng === 'cimode') return;
        const lngs = this.services.languageUtils.toResolveHierarchy(lng);
        lngs.forEach(l => {
          if (l === 'cimode') return;
          if (toLoad.indexOf(l) < 0) toLoad.push(l);
        });
      };
      if (!usedLng) {
        const fallbacks = this.services.languageUtils.getFallbackCodes(this.options.fallbackLng);
        fallbacks.forEach(l => append(l));
      } else {
        append(usedLng);
      }
      this.options.preload?.forEach?.(l => append(l));
      this.services.backendConnector.load(toLoad, this.options.ns, e => {
        if (!e && !this.resolvedLanguage && this.language) this.setResolvedLanguage(this.language);
        usedCallback(e);
      });
    } else {
      usedCallback(null);
    }
  }
  reloadResources(lngs, ns, callback) {
    const deferred = defer();
    if (typeof lngs === 'function') {
      callback = lngs;
      lngs = undefined;
    }
    if (typeof ns === 'function') {
      callback = ns;
      ns = undefined;
    }
    if (!lngs) lngs = this.languages;
    if (!ns) ns = this.options.ns;
    if (!callback) callback = noop;
    this.services.backendConnector.reload(lngs, ns, err => {
      deferred.resolve();
      callback(err);
    });
    return deferred;
  }
  use(module) {
    if (!module) throw new Error('You are passing an undefined module! Please check the object you are passing to i18next.use()');
    if (!module.type) throw new Error('You are passing a wrong module! Please check the object you are passing to i18next.use()');
    if (module.type === 'backend') {
      this.modules.backend = module;
    }
    if (module.type === 'logger' || module.log && module.warn && module.error) {
      this.modules.logger = module;
    }
    if (module.type === 'languageDetector') {
      this.modules.languageDetector = module;
    }
    if (module.type === 'i18nFormat') {
      this.modules.i18nFormat = module;
    }
    if (module.type === 'postProcessor') {
      postProcessor.addPostProcessor(module);
    }
    if (module.type === 'formatter') {
      this.modules.formatter = module;
    }
    if (module.type === '3rdParty') {
      this.modules.external.push(module);
    }
    return this;
  }
  setResolvedLanguage(l) {
    if (!l || !this.languages) return;
    if (['cimode', 'dev'].indexOf(l) > -1) return;
    for (let li = 0; li < this.languages.length; li++) {
      const lngInLngs = this.languages[li];
      if (['cimode', 'dev'].indexOf(lngInLngs) > -1) continue;
      if (this.store.hasLanguageSomeTranslations(lngInLngs)) {
        this.resolvedLanguage = lngInLngs;
        break;
      }
    }
  }
  changeLanguage(lng, callback) {
    var _this2 = this;
    this.isLanguageChangingTo = lng;
    const deferred = defer();
    this.emit('languageChanging', lng);
    const setLngProps = l => {
      this.language = l;
      this.languages = this.services.languageUtils.toResolveHierarchy(l);
      this.resolvedLanguage = undefined;
      this.setResolvedLanguage(l);
    };
    const done = (err, l) => {
      if (l) {
        setLngProps(l);
        this.translator.changeLanguage(l);
        this.isLanguageChangingTo = undefined;
        this.emit('languageChanged', l);
        this.logger.log('languageChanged', l);
      } else {
        this.isLanguageChangingTo = undefined;
      }
      deferred.resolve(function () {
        return _this2.t(...arguments);
      });
      if (callback) callback(err, function () {
        return _this2.t(...arguments);
      });
    };
    const setLng = lngs => {
      if (!lng && !lngs && this.services.languageDetector) lngs = [];
      const l = isString(lngs) ? lngs : this.services.languageUtils.getBestMatchFromCodes(lngs);
      if (l) {
        if (!this.language) {
          setLngProps(l);
        }
        if (!this.translator.language) this.translator.changeLanguage(l);
        this.services.languageDetector?.cacheUserLanguage?.(l);
      }
      this.loadResources(l, err => {
        done(err, l);
      });
    };
    if (!lng && this.services.languageDetector && !this.services.languageDetector.async) {
      setLng(this.services.languageDetector.detect());
    } else if (!lng && this.services.languageDetector && this.services.languageDetector.async) {
      if (this.services.languageDetector.detect.length === 0) {
        this.services.languageDetector.detect().then(setLng);
      } else {
        this.services.languageDetector.detect(setLng);
      }
    } else {
      setLng(lng);
    }
    return deferred;
  }
  getFixedT(lng, ns, keyPrefix) {
    var _this3 = this;
    const fixedT = function (key, opts) {
      let options;
      if (typeof opts !== 'object') {
        for (var _len3 = arguments.length, rest = new Array(_len3 > 2 ? _len3 - 2 : 0), _key3 = 2; _key3 < _len3; _key3++) {
          rest[_key3 - 2] = arguments[_key3];
        }
        options = _this3.options.overloadTranslationOptionHandler([key, opts].concat(rest));
      } else {
        options = {
          ...opts
        };
      }
      options.lng = options.lng || fixedT.lng;
      options.lngs = options.lngs || fixedT.lngs;
      options.ns = options.ns || fixedT.ns;
      if (options.keyPrefix !== '') options.keyPrefix = options.keyPrefix || keyPrefix || fixedT.keyPrefix;
      const keySeparator = _this3.options.keySeparator || '.';
      let resultKey;
      if (options.keyPrefix && Array.isArray(key)) {
        resultKey = key.map(k => `${options.keyPrefix}${keySeparator}${k}`);
      } else {
        resultKey = options.keyPrefix ? `${options.keyPrefix}${keySeparator}${key}` : key;
      }
      return _this3.t(resultKey, options);
    };
    if (isString(lng)) {
      fixedT.lng = lng;
    } else {
      fixedT.lngs = lng;
    }
    fixedT.ns = ns;
    fixedT.keyPrefix = keyPrefix;
    return fixedT;
  }
  t() {
    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }
    return this.translator?.translate(...args);
  }
  exists() {
    for (var _len5 = arguments.length, args = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
      args[_key5] = arguments[_key5];
    }
    return this.translator?.exists(...args);
  }
  setDefaultNamespace(ns) {
    this.options.defaultNS = ns;
  }
  hasLoadedNamespace(ns) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (!this.isInitialized) {
      this.logger.warn('hasLoadedNamespace: i18next was not initialized', this.languages);
      return false;
    }
    if (!this.languages || !this.languages.length) {
      this.logger.warn('hasLoadedNamespace: i18n.languages were undefined or empty', this.languages);
      return false;
    }
    const lng = options.lng || this.resolvedLanguage || this.languages[0];
    const fallbackLng = this.options ? this.options.fallbackLng : false;
    const lastLng = this.languages[this.languages.length - 1];
    if (lng.toLowerCase() === 'cimode') return true;
    const loadNotPending = (l, n) => {
      const loadState = this.services.backendConnector.state[`${l}|${n}`];
      return loadState === -1 || loadState === 0 || loadState === 2;
    };
    if (options.precheck) {
      const preResult = options.precheck(this, loadNotPending);
      if (preResult !== undefined) return preResult;
    }
    if (this.hasResourceBundle(lng, ns)) return true;
    if (!this.services.backendConnector.backend || this.options.resources && !this.options.partialBundledLanguages) return true;
    if (loadNotPending(lng, ns) && (!fallbackLng || loadNotPending(lastLng, ns))) return true;
    return false;
  }
  loadNamespaces(ns, callback) {
    const deferred = defer();
    if (!this.options.ns) {
      if (callback) callback();
      return Promise.resolve();
    }
    if (isString(ns)) ns = [ns];
    ns.forEach(n => {
      if (this.options.ns.indexOf(n) < 0) this.options.ns.push(n);
    });
    this.loadResources(err => {
      deferred.resolve();
      if (callback) callback(err);
    });
    return deferred;
  }
  loadLanguages(lngs, callback) {
    const deferred = defer();
    if (isString(lngs)) lngs = [lngs];
    const preloaded = this.options.preload || [];
    const newLngs = lngs.filter(lng => preloaded.indexOf(lng) < 0 && this.services.languageUtils.isSupportedCode(lng));
    if (!newLngs.length) {
      if (callback) callback();
      return Promise.resolve();
    }
    this.options.preload = preloaded.concat(newLngs);
    this.loadResources(err => {
      deferred.resolve();
      if (callback) callback(err);
    });
    return deferred;
  }
  dir(lng) {
    if (!lng) lng = this.resolvedLanguage || (this.languages?.length > 0 ? this.languages[0] : this.language);
    if (!lng) return 'rtl';
    const rtlLngs = ['ar', 'shu', 'sqr', 'ssh', 'xaa', 'yhd', 'yud', 'aao', 'abh', 'abv', 'acm', 'acq', 'acw', 'acx', 'acy', 'adf', 'ads', 'aeb', 'aec', 'afb', 'ajp', 'apc', 'apd', 'arb', 'arq', 'ars', 'ary', 'arz', 'auz', 'avl', 'ayh', 'ayl', 'ayn', 'ayp', 'bbz', 'pga', 'he', 'iw', 'ps', 'pbt', 'pbu', 'pst', 'prp', 'prd', 'ug', 'ur', 'ydd', 'yds', 'yih', 'ji', 'yi', 'hbo', 'men', 'xmn', 'fa', 'jpr', 'peo', 'pes', 'prs', 'dv', 'sam', 'ckb'];
    const languageUtils = this.services?.languageUtils || new LanguageUtil(get());
    return rtlLngs.indexOf(languageUtils.getLanguagePartFromCode(lng)) > -1 || lng.toLowerCase().indexOf('-arab') > 1 ? 'rtl' : 'ltr';
  }
  static createInstance() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let callback = arguments.length > 1 ? arguments[1] : undefined;
    return new I18n(options, callback);
  }
  cloneInstance() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let callback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
    const forkResourceStore = options.forkResourceStore;
    if (forkResourceStore) delete options.forkResourceStore;
    const mergedOptions = {
      ...this.options,
      ...options,
      ...{
        isClone: true
      }
    };
    const clone = new I18n(mergedOptions);
    if (options.debug !== undefined || options.prefix !== undefined) {
      clone.logger = clone.logger.clone(options);
    }
    const membersToCopy = ['store', 'services', 'language'];
    membersToCopy.forEach(m => {
      clone[m] = this[m];
    });
    clone.services = {
      ...this.services
    };
    clone.services.utils = {
      hasLoadedNamespace: clone.hasLoadedNamespace.bind(clone)
    };
    if (forkResourceStore) {
      const clonedData = Object.keys(this.store.data).reduce((prev, l) => {
        prev[l] = {
          ...this.store.data[l]
        };
        return Object.keys(prev[l]).reduce((acc, n) => {
          acc[n] = {
            ...prev[l][n]
          };
          return acc;
        }, {});
      }, {});
      clone.store = new ResourceStore(clonedData, mergedOptions);
      clone.services.resourceStore = clone.store;
    }
    clone.translator = new Translator(clone.services, mergedOptions);
    clone.translator.on('*', function (event) {
      for (var _len6 = arguments.length, args = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
        args[_key6 - 1] = arguments[_key6];
      }
      clone.emit(event, ...args);
    });
    clone.init(mergedOptions, callback);
    clone.translator.options = mergedOptions;
    clone.translator.backendConnector.services.utils = {
      hasLoadedNamespace: clone.hasLoadedNamespace.bind(clone)
    };
    return clone;
  }
  toJSON() {
    return {
      options: this.options,
      store: this.store,
      language: this.language,
      languages: this.languages,
      resolvedLanguage: this.resolvedLanguage
    };
  }
}
const instance = I18n.createInstance();
instance.createInstance = I18n.createInstance;

instance.createInstance;
instance.dir;
instance.init;
instance.loadResources;
instance.reloadResources;
instance.use;
instance.changeLanguage;
instance.getFixedT;
instance.t;
instance.exists;
instance.setDefaultNamespace;
instance.hasLoadedNamespace;
instance.loadNamespaces;
instance.loadLanguages;

const matchHtmlEntity = /&(?:amp|#38|lt|#60|gt|#62|apos|#39|quot|#34|nbsp|#160|copy|#169|reg|#174|hellip|#8230|#x2F|#47);/g;
const htmlEntities = {
  '&amp;': '&',
  '&#38;': '&',
  '&lt;': '<',
  '&#60;': '<',
  '&gt;': '>',
  '&#62;': '>',
  '&apos;': "'",
  '&#39;': "'",
  '&quot;': '"',
  '&#34;': '"',
  '&nbsp;': ' ',
  '&#160;': ' ',
  '&copy;': '©',
  '&#169;': '©',
  '&reg;': '®',
  '&#174;': '®',
  '&hellip;': '…',
  '&#8230;': '…',
  '&#x2F;': '/',
  '&#47;': '/'
};
const unescapeHtmlEntity = m => htmlEntities[m];
const unescape = text => text.replace(matchHtmlEntity, unescapeHtmlEntity);

let defaultOptions = {
  bindI18n: 'languageChanged',
  bindI18nStore: '',
  transEmptyNodeValue: '',
  transSupportBasicHtmlNodes: true,
  transWrapTextNodes: '',
  transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
  useSuspense: true,
  unescape
};
const setDefaults = (options = {}) => {
  defaultOptions = {
    ...defaultOptions,
    ...options
  };
};

const initReactI18next = {
  type: '3rdParty',
  init(instance) {
    setDefaults(instance.options.react);
  }
};

var savedSearch$3 = {
	actions: {
		alertTitle: "Titel des Suchauftrags",
		disabled: "Aus",
		edit: "Bearbeiten",
		editAlert: "Suchauftrag bearbeiten",
		enabled: "Ein",
		listings: "Treffer anzeigen",
		update: "Speichern"
	},
	alert: {
		title: "Gespeicherte Suchen"
	},
	alertActivated: {
		action: "Suche gespeichert"
	},
	alertAlertsTab: {
		action: "Benachrichtigungen suchen"
	},
	alertAppNotifications: {
		option: "Push-Benachrichtigungen"
	},
	alertContinue: {
		action: "Fortfahren"
	},
	alertCreate: {
		action: "Suche speichern",
		text: "Erhalte Benachrichtigungen, wenn eine neue Immobilie deinen Kriterien entspricht"
	},
	alertDelete: {
		action: "Löschen"
	},
	alertDeletePopUp: {
		text: "Du hast die maximale Anzahl gespeicherter Suchen erreicht. Lösche mindestens eine, um fortzufahren.",
		title: "Gespeicherte Suche löschen"
	},
	alertEdit: {
		title: "Meine gespeicherte Suche bearbeiten"
	},
	alertEmail: {
		actionCreate: "",
		created: {
			action: "Treffer azeigen",
			text: "Du erhältst ab sofort neue Vorschläge an die folgende E-Mail-Adresse:",
			title: "Deine E-Mail-Benachrichtigung wurde erfolgreich eingerichtet."
		},
		disclaimer: {
			link: "",
			text: ""
		},
		invalid: "Bitte geben Sie eine gütige E-Mail-Adresse ein",
		option: "E-Mail",
		text: ""
	},
	alertEmptyState: {
		text: "Werde benachrichtigt, wenn neue Immobilien deinen Kriterien entsprechen",
		title: "Keine gespeicherte Suche"
	},
	alertFavouritesTab: {
		action: "Wunschliste"
	},
	alertForceLogin: {
		promote: "Verwalte deine gespeicherten Suchen von jedem Gerät. Registriere und melde dich an, um deine Einstellungen schnell und einfach zu aktualisieren.",
		text: "Melde dich an, um deine gespeicherten Suchen zu verwalten. Du wirst benachrichtigt, wenn neue Anzeigen deinen Kriterien entsprechen.",
		title: "Melde dich an, um deine gespeicherten Suchen zu verwalten"
	},
	alertInternetError: {
		text: "Schalte deine mobilen Daten ein oder stelle eine Verbindung zu einem WLAN-Netzwerk her",
		title: "Keine Internetverbindung"
	},
	alertInvalid: {
		text: "Ungültige Suchkriterien"
	},
	alertLoading: {
		title: "Deine gespeicherten Suchen werden geladen"
	},
	alertRetry: {
		action: "Erneut versuchen"
	},
	alertSave: {
		action: "Suche speichern"
	},
	alertSee: {
		action: "Immobilien anzeigen"
	},
	alertSetup: {
		title: "Erhalte Benachrichtigungen über neue Immobilien"
	},
	alertSignin: {
		action: "Anmelden "
	},
	alertSignup: {
		action: "Registrieren"
	},
	alertTechnicalError: {
		text: "Es gibt zurzeit ein paar Probleme. Bitte warte ein paar Minuten und versuche es erneut.",
		title: "Etwas ist schiefgelaufen"
	},
	alertValidate: {
		action: "Bestätigen"
	},
	alertsPaused: {
		text: "Alle Benachrichtigungen wurden deaktiviert. Du kannst sie jederzeit wieder aktivieren."
	},
	anonymousRegister: {
		action: "Anmelden",
		text: "Melden Sie sich an, um Ihre Suchaufträge weiter zu bearbeiten.",
		title: "Anmelden und Vorteile nutzen"
	},
	cancel: {
		action: "Abbrechen"
	},
	createAlertsByHitting: {
		text: "Du kannst eine Suche über die Suchergebnisseite speichern."
	},
	criterias: {
		area: {
			title: "Fläche"
		},
		common: {
			range: {
				label: {
					max: "Max",
					min: "Min"
				}
			}
		},
		context_switch: {
			commercial: "Gewerbe",
			residential: "Wohnen"
		},
		estate_subtype: {
			agriculture_forestry: {
				checkbox: {
					agriculture_company: "Landwirtschaftlicher Betrieb",
					farm_ranch: "Bauernhof",
					forestry: "Forstwirtschaft",
					gardening_orchard: "Gartenarbeit / Obstgarten",
					hunting_fishing: "Jagd und Fischerei",
					riding: "Reiterhof",
					winery: "Weingut"
				},
				title: "Objektart"
			},
			apartment: {
				checkbox: {
					loft: "Loft",
					multi_storey: "Mehrgeschosswohnung",
					penthouse: "Penthouse",
					studio: "Studio",
					terrace: "Terrassenwohnung"
				},
				title: "Wohnungstyp"
			},
			gastronomy_hotel: {
				checkbox: {
					cafe_bar_pub: "Café / Bar / Kneipe",
					club_discotheque: "Club / Diskothek",
					hotel_pension: "Hotel / Pension",
					restaurant: "Restaurant"
				},
				title: "Objektart"
			},
			house: {
				checkbox: {
					bungalow: "Bungalow",
					castle: "Schloss",
					chalet: "Chalet",
					farmhouse: "Bauernhaus",
					finca: "Finca",
					manor_house: "Herrenhaus",
					multi_family_house: "Mehrfamilienhaus",
					rustico: "Rustico",
					semidetached_house: "Doppelhaushälfte",
					single_family_house: "Einfamilienhaus",
					terrace_house: "Reihenhaus",
					town_house: "Stadthaus",
					villa: "Villa"
				},
				title: "Haustyp"
			},
			office: {
				checkbox: {
					atelier: "Atelier",
					coworking: "Coworking",
					living_and_commercial_building: "Wohn- und Geschäftshaus",
					medical: "Praxis",
					office: "Büro"
				},
				title: "Objektart"
			},
			parking: {
				checkbox: {
					carport: "Carport",
					duplex: "Doppelgarage",
					garage: "Garage",
					outside: "Außenstellplatz",
					underground_garage: "Tiefgarage",
					underground_parking_space: "Tiefgaragenstellplatz"
				},
				title: "Stellplatztyp"
			},
			plot: {
				checkbox: {
					commercial: "Gewerbliche Nutzung",
					leisure_facility: "Freizeit",
					living: "Wohnen"
				},
				title: "Grundstücksart"
			},
			storage_production: {
				checkbox: {
					garage_repair: "Werkstatt",
					logistic_center: "Logistik-Center",
					miscellaneous_storage: "Sonstige Lagerung",
					outdoor_space: "Außenfläche",
					production_hall: "Industriehalle",
					warehouse_hall: "Lagerhalle"
				},
				title: "Objektart"
			},
			trading: {
				checkbox: {
					kiosk: "Kiosk",
					sales_area: "Verkaufsfläche",
					shopping_centre: "Einkaufszentrum",
					showroom_space: "Ausstellungsfläche",
					store: "Laden Sie"
				},
				title: "Objektart"
			}
		},
		features: {
			checkbox: {
				attic_apartment: "Dachgeschoss-Wohnung",
				balcony_terrace: "Balkon / Terrasse",
				bathroom_with_bathtub: "Badezimmer mit Badewanne",
				bathroom_with_window: "Bad mit Fenster",
				built_in_kitchen: "Einbauküche",
				cellar: "Keller",
				certificate_of_eligibility_needed: "mit Wohnberechtigungsschein",
				elevator: "Aufzug",
				free_of_commission: "Provisionsfrei",
				furnished: "Vollständig möbliert",
				garden: "Garten",
				no_attic_apartment: "Kein Dachgeschoss",
				no_ground_floor: "Kein Erdgeschoss",
				parking_garage: "Parkplatz / Garage",
				pets_allowed: "Haustiere erlaubt",
				reduced_mobility_access: "Barrierefrei",
				unfurnished: "Unmöbliert",
				vacant: "Frei / Nicht vermietet"
			},
			subtitle: {
				accessibility: "Erreichbarkeit",
				furnished_unfurnished: "Möbliert/unmöbliert",
				indoor: "Innenbereich",
				usage: "Verwendung"
			},
			title: {
				features_excluded: "Ausschlusskriterien",
				features_included: "Eigenschaften",
				foreclosure: "Zwangsversteigerung",
				new_builds: "Neubauprojekte",
				subsidizing_housing: "Wohnberechtigungsschein"
			}
		},
		floor: "Geschoss",
		floors: {
			checkbox: {
				ground_floor: "Erdgeschoss",
				no_ground_floor: "Kein Erdgeschoss",
				other_floors: "Andere Geschosse"
			}
		},
		living_area: {
			input_field: {
				surface_type: "m²"
			},
			title: "Wohnfläche"
		},
		location: {
			circles: "{{placeName}} +{{radius}} {{radiusUnit}}",
			circles_prefixed: "in {{placeName}} +{{radius}} {{radiusUnit}}",
			places_one: "{{placeName}}",
			places_other: "{{placeName}} (+{{count}})",
			places_prefixed_one: "in {{placeName}}",
			places_prefixed_other: "in {{placeName}} (+{{count}})",
			places_prefixed_zero: "",
			polygons: "persönlichem Kartenausschnitt",
			polygons_prefixed: "in persönlichem Kartenausschnitt",
			travels: "{{duration}} min {{mode}} von {{placeName}}",
			travels_prefixed: "bei {{duration}} min {{mode}} von {{placeName}}"
		},
		plot_area: {
			input_field: {
				surface_type: "m²"
			},
			title: "Grundstücksfläche"
		},
		project_type: {
			checkbox: {
				foreclosure_sale: "Zwangsversteigerung",
				investment: "Anlageobjekte",
				new_builds: "Neubauprojekte",
				resale: "Wiederverkauf",
				shared_flats: "Wohngemeinschaften",
				stock: "Lagerbestand",
				temporary_living: "Vorübergehendes Wohnen"
			},
			title: "Projekttyp"
		}
	},
	deleteAlertConfirmation: {
		text: "Du wirst keine weiteren Benachrichtigungen für neue Suchtreffer erhalten",
		title: "Bist du sicher, dass du diese gespeicherte Suche löschen möchtest?"
	},
	distribution_type: {
		buy: "Kauf",
		compulsory_auction: "Zwangsversteigerung",
		rent: "Miete"
	},
	emailResubscribed: {
		action: "Weitersuchen",
		text: "Danke, dass du bei uns geblieben bist.",
		title: "Du hast die Benachrichtigungen für deinen Suchauftrag wieder aktiviert."
	},
	enums: {
		distributionType: {
			BUY: "Kauf",
			BUY_AUCTION: "Kauf",
			BUY_AUCTION_prefixed: "zum Kauf",
			BUY_prefixed: "zum Kauf",
			COMPULSORY_AUCTION: "Zwangsversteigerung",
			COMPULSORY_AUCTION_prefixed: "zur Zwangsversteigerung",
			RENT: "Miete",
			RENT_prefixed: "zur Miete"
		},
		estateType: {
			AGRICULTURE_FORESTRY: "Land-/Forstwirtschaft",
			AGRICULTURE_FORESTRY_prefixed: "eine Land-/Forstwirtschaft",
			APARTMENT: "Wohnung",
			APARTMENT_prefixed: "eine Wohnung",
			BUILDING: "Gebäude",
			BUILDING_prefixed: "ein Gebäude",
			GASTRONOMY_HOTEL: "Gastronomie/Hotel",
			GASTRONOMY_HOTEL_prefixed: "eine Gastronomie/Hotel",
			HOUSE: "Haus",
			HOUSE_prefixed: "ein Haus",
			MISCELLANEOUS: "Sonstiges",
			MISCELLANEOUS_prefixed: "Sonstiges",
			OFFICE: "Büro-/Praxisfläche",
			OFFICE_prefixed: "eine Büro-/Praxisfläche",
			PARKING: "Parkplatz",
			PARKING_prefixed: "ein Parkplatz",
			PLOT: "Grundstück",
			PLOT_prefixed: "ein Grundstück",
			PROJECT: "Bauprojekt",
			PROJECT_prefixed: "ein Bauprojekt",
			SENIOR: "Seniorenwohnung",
			SENIOR_prefixed: "eine Seniorenwohnung",
			STORAGE_PRODUCTION: "Halle/Industriefläche",
			STORAGE_PRODUCTION_prefixed: "eine Halle/Industriefläche",
			TRADING: "Ladenfläche",
			TRADING_prefixed: "eine Ladenfläche"
		},
		travelMode: {
			BIKE: "Fahrrad",
			BIKE_prefixed: "mit dem Fahrrad",
			CAR: "Auto",
			CAR_prefixed: "mit dem Auto",
			TRANSIT: "ÖPNV",
			TRANSIT_prefixed: "mit dem ÖPNV",
			WALK: "zu Fuß",
			WALK_prefixed: "zu Fuß"
		}
	},
	errorCreate: {
		text: "Deine Suche konnte nicht gespeichert werden. Bitte versuche es später noch einmal."
	},
	errorDelete: {
		text: "Deine gespeicherte Suche kann nicht gelöscht werden."
	},
	errorPage: {
		action: "Zu den Suchbenachrichtigungen",
		text: "Der Link, dem Sie gefolgt sind, ist möglicherweise defekt, oder die Seite wurde entfernt.",
		title: "Diese Seite ist nicht verfügbar."
	},
	errorPageGeneric: {
		text: "Leider konnte diese Seite nicht geladen werden, bitte versuchen Sie es später erneut.",
		title: "Es ist ein Fehler aufgetreten"
	},
	errorTechnicalRetry: {
		text: "Es ist ein technischer Fehler aufgetreten. Bitte versuche es später noch einmal."
	},
	errorUpdate: {
		text: "Deine gespeicherte Suche kann nicht aktualisiert werden. Bitte versuche es später noch einmal."
	},
	estate_type: {
		agriculture_forestry: "Land- / Forstwirtschaft",
		apartment: "Wohnung",
		gastronomy_hotel: "Gastronomie / Hotel",
		house: "Haus",
		house_apartment: "Haus oder Wohnung",
		investment_property: "Renditeobjekt",
		miscellaneous: "Sonstiges",
		office: "Büro- / Praxisfläche",
		parking: "Parkplatz",
		plot: "Grundstück",
		project: "Projekt",
		shared_apartment: "Wohngemeinschaft",
		storage_production: "Halle / Industriefläche",
		temporary_living: "Wohnen auf Zeit",
		trading: "Ladenfläche"
	},
	geometry_location: {
		title: "persönlicher Kartenausschnitt"
	},
	getAlerts: {
		action: "Suche speichern"
	},
	goBack: {
		action: "Zurück"
	},
	goToSearch: {
		action: "Gehe zur Suche"
	},
	hint: {
		"default": {
			text: "Benachrichtigungen aktivieren, um sofort über neue Immobilien informiert zu werden, die den Suchkriterien entsprechen.",
			title: "Suchauftrag einrichten"
		},
		travel_time: {
			text: "Das Speichern Ihrer Suche ist jetzt möglich!",
			title: "Neue Funktion"
		}
	},
	hpMfe: {
		mvp: {
			blockText: "Sollen wir weitermachen?"
		},
		noMore: {
			firstAction: "Weitere Unterkünfte anzeigen",
			firstText: "Interessiert an weiteren Treffern?",
			secondAction: "Neue Suche starten",
			secondText: "Dann kannst du jetzt..."
		},
		seeMore: {
			action: "Mehr laden",
			text: "{{number}} weitere Treffer für dich"
		},
		text: "Neueste Treffer zu deinem Suchauftrag",
		title: "Ihre letzte gespeicherte Suche"
	},
	iconCriteria: {
		rooms: {
			text: "Zi."
		}
	},
	intermediaryMatchingMandate: {
		errors: {
			consent: "",
			email: "",
			phoneNumber: "",
			required: "",
			string: ""
		},
		informations: "",
		labels: {
			consent: "",
			email: "",
			message: "",
			name: "",
			phoneNumber: ""
		},
		legal: {
			text: {
				first: "",
				fourth: "",
				link: {
					first: "AVIV Group",
					second: "Datenschutzrichtlinie.",
					third: "Allgemeine Nutzungsbedingungen"
				},
				second: "",
				third: ""
			}
		},
		placeholders: {
			message: "",
			phoneNumber: ""
		},
		submit: "",
		success: "",
		suggestions: {
			text: "",
			title: ""
		}
	},
	loader_text: "Seite wird geladen",
	noAlertError: {
		text: "Du hast noch keine Suchaufträge. Erstelle eine neue Suche und aktiviere Benachrichtigungen.",
		title: "Erstelle deinen ersten Suchauftrag"
	},
	notifications: {
		recommendations: "Empfehlungen",
		title: "Benachrichtigungen",
		tooltip: "Aktualisiere deine Einstellungen und erhalte Empfehlungen basierend auf deinem Suchauftrag"
	},
	notificationsEdit: {
		email: "E-Mail-Benachrichtigungen",
		frequency: {
			daily: "Einmal täglich",
			hourly: "Einmal stündlich",
			never: "Aus",
			realtime: "In Echtzeit"
		},
		recommendation: {
			text: "Erhalten Sie alternative Angebote zu Ihren vordefinierten Suchkriterien",
			title: "Empfehlungen"
		},
		title: "Benachrichtigungen erhalten",
		trigger: "Benachrichtigungen"
	},
	pauseAllNotifications: {
		action: "Alle Benachrichtigungen deaktiviert."
	},
	resubscribe: {
		action: "Wieder aktivieren"
	},
	secondPage: {
		text: "Aktiviere einen Suchauftrag und erhalte Benachrightigungen für neue Angebote, die deinen Suchkriterien entsprechen",
		title: "Beschleunige deine Suche mit einem Suchauftrag"
	},
	segmentationUnsub: {
		buttons: {
			"continue": "Weitersuchen",
			manage: "Zu deinen Suchaufträgen"
		},
		text: {
			confirm: "Alle Suchaufträge wurden deaktiviert.",
			landing: "Möchtest du alle deine Suchaufträge abbestelen?"
		},
		title: {
			confirm: "Sie haben sich erfolgreich abgemeldet",
			landing: "Abbestellen"
		}
	},
	title: {
		circles: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) in {{placeName}} +{{radius}} {{radiusUnit}}",
		"default": "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed)",
		errors: {
			tooLarge: "Maximal 100 Zeichen erlaubt"
		},
		location: "{{estateTypes, list(type: disjunction)}} {{distributionType}} {{location}}",
		places: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) $t(savedSearch.criterias.location.places, { \"context\": \"prefixed\", \"placeName\":  \"{{placeName}}\", \"count\": {{placeCount}} })",
		polygons: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) in persönlichem Kartenausschnitt",
		travels: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) {{duration}} min {{mode}} von {{placeName}}"
	},
	unsubscribedEmailAlertSuccessfully: {
		title: "Du hast die Benachrichtigungen für deinen Suchauftrag \"{{alertName}}\" erfolgreich deaktiviert."
	},
	youCanAlso: {
		title: "Du kannst sie außerdem:"
	}
};
var savedSearch_alertClose_action$3 = "Schließen";
var savedSearch_alertCreated_text$3 = "Du wirst per E-Mail über neue Immobilien benachrichtigt.";
var savedSearch_alertDelete_text$3 = "Deine gespeicherte Suche wurde gelöscht.";
var savedSearch_alertNotCreated_text$3 = "Die Suche kann nicht gespeichert werden.";
var savedSearch_alertUpdated_text$3 = "Deine Suche wurde gespeichert.";
var savedSearch_buttonCard_title$3 = "Benachrichtigungen erhalten";
var savedSearch_deleteAlert_title$3 = "Suche speichern";
var savedSearch_duplicateError_text$3 = "Dieser Suchauftrag existiert bereits";
var savedSearch_duplicatePlaceId_text$3 = "Leider gab es ein Problem mit dem gewählten Suchbereich, bitte versuche es später noch einmal.";
var savedSearch_errorSameSearchCriteria_text$3 = "Du hast bereits eine gespeicherte Suche mit denselben Suchkriterien.";
var savedSearch_errorTechnical_text$3 = "Es ist ein technischer Fehler aufgetreten.";
var savedSearch_locationTooLarge_text$3 = "Suchgebiet zu groß, bitte reduziere die Anzahl der Standorte";
var savedSearch_locationTooSmall_text$3 = "Der ausgewählte Suchbereich ist zu klein.";
var savedSearch_niceJob_text$3 = "Jetzt kannst du die neue Suche speichern.";
var savedSearch_niceJob_title$3 = "Deine gespeicherte Suche wurde gelöscht.";
var savedSearch_placeIDforbidden_text$3 = "Der gewählte Ortstyp kann nicht gespeichert werden, bitte wähle einen anderen Ort";
var savedSearch_polygonAlert_text$3 = "Eine Umkreissuche kann leider nicht gespeichert werden";
var deTranslations = {
	savedSearch: savedSearch$3,
	savedSearch_alertClose_action: savedSearch_alertClose_action$3,
	savedSearch_alertCreated_text: savedSearch_alertCreated_text$3,
	savedSearch_alertDelete_text: savedSearch_alertDelete_text$3,
	savedSearch_alertNotCreated_text: savedSearch_alertNotCreated_text$3,
	savedSearch_alertUpdated_text: savedSearch_alertUpdated_text$3,
	savedSearch_buttonCard_title: savedSearch_buttonCard_title$3,
	savedSearch_deleteAlert_title: savedSearch_deleteAlert_title$3,
	savedSearch_duplicateError_text: savedSearch_duplicateError_text$3,
	savedSearch_duplicatePlaceId_text: savedSearch_duplicatePlaceId_text$3,
	savedSearch_errorSameSearchCriteria_text: savedSearch_errorSameSearchCriteria_text$3,
	savedSearch_errorTechnical_text: savedSearch_errorTechnical_text$3,
	savedSearch_locationTooLarge_text: savedSearch_locationTooLarge_text$3,
	savedSearch_locationTooSmall_text: savedSearch_locationTooSmall_text$3,
	savedSearch_niceJob_text: savedSearch_niceJob_text$3,
	savedSearch_niceJob_title: savedSearch_niceJob_title$3,
	savedSearch_placeIDforbidden_text: savedSearch_placeIDforbidden_text$3,
	savedSearch_polygonAlert_text: savedSearch_polygonAlert_text$3,
	"savedSearch_there’sNoRoomLeft_text": "Du hast die maximale Anzahl von {{maxAlertsCount}} gespeicherten Suchen erreicht. Lösche mindestens eine, um fortzufahren.",
	"savedSearch_there’sNoRoomLeft_title": "Gespeicherte Suche löschen"
};

var savedSearch$2 = {
	actions: {
		alertTitle: "Alert Title",
		disabled: "Disabled",
		edit: "Edit",
		editAlert: "Edit Alert",
		enabled: "Enabled",
		listings: "See listings",
		update: "Save"
	},
	alert: {
		title: "Alerts"
	},
	alertActivated: {
		action: "Alert enabled"
	},
	alertAlertsTab: {
		action: "Search Alerts"
	},
	alertAppNotifications: {
		option: "App notifications"
	},
	alertContinue: {
		action: "Continue"
	},
	alertCreate: {
		action: "Create an alert",
		text: "Get alerts when a new property match your criterias"
	},
	alertDelete: {
		action: "Delete"
	},
	alertDeletePopUp: {
		text: "You have reached the maximum number of alerts. Delete at least one to continue.",
		title: "Delete an alert"
	},
	alertEdit: {
		title: "Edit my alert"
	},
	alertEmail: {
		actionCreate: "Create a saved search",
		created: {
			action: "See search results",
			text: "You will now receive new property suggestions to the following email address:",
			title: "Your email alert has been successfully created!"
		},
		disclaimer: {
			link: "click here",
			text: "Digital Classifieds France processes your data in order to manage your request to alert new real estate advertisements by e-mail. To find out more and exercise your rights, "
		},
		invalid: "Please enter a valid email address",
		option: "Email",
		text: "Enter your email to receive all new personalized real estate ads and the selection of professionals."
	},
	alertEmptyState: {
		text: "Get notified when new classifieds match your criteria.",
		title: "No alert"
	},
	alertFavouritesTab: {
		action: "Wishlist"
	},
	alertForceLogin: {
		promote: "Access your saved searches and alerts from any device. Easily manage and update your preferences by creating an account or logging in.",
		text: "Log in to set up your alerts. You will get notified when new classifieds match your criteria.",
		title: "Log in to manage your alerts"
	},
	alertInternetError: {
		text: "Turn on your mobile data or connect to a Wi-Fi network.",
		title: "No internet connection"
	},
	alertInvalid: {
		text: "Invalid search criteria"
	},
	alertLoading: {
		title: "Alerts loading"
	},
	alertRetry: {
		action: "Retry"
	},
	alertSave: {
		action: "Create the alert"
	},
	alertSee: {
		action: "See listings"
	},
	alertSetup: {
		title: "Get notified of new classifieds"
	},
	alertSignin: {
		action: "Log in "
	},
	alertSignup: {
		action: "Sign up"
	},
	alertTechnicalError: {
		text: "We are experiencing some difficulties. Please wait a few minutes and try again.",
		title: "Something went wrong"
	},
	alertValidate: {
		action: "Validate"
	},
	alertsPaused: {
		text: "Notifications on all alerts have been paused. You can reactivate them at anytime."
	},
	anonymousRegister: {
		action: "Register",
		text: "Only then will you be able to see all your saved searches.",
		title: "Please login"
	},
	cancel: {
		action: "Cancel"
	},
	createAlertsByHitting: {
		text: "You can create an alert from the search result page."
	},
	criterias: {
		area: {
			title: "Area"
		},
		common: {
			range: {
				label: {
					max: "Max",
					min: "Min"
				}
			}
		},
		context_switch: {
			commercial: "For my business",
			residential: "For me"
		},
		estate_subtype: {
			agriculture_forestry: {
				checkbox: {
					agriculture_company: "Agriculture company",
					farm_ranch: "Farm / Ranch",
					forestry: "Forestry",
					gardening_orchard: "Gardening / Orchard",
					hunting_fishing: "Hunting / Fishing",
					riding: "Riding",
					winery: "Winery"
				},
				title: "Type of estate"
			},
			apartment: {
				checkbox: {
					loft: "Loft",
					multi_storey: "Multi Storey",
					penthouse: "Penthouse",
					studio: "Studio",
					terrace: "Terrace"
				},
				title: "Type of apartment"
			},
			gastronomy_hotel: {
				checkbox: {
					cafe_bar_pub: "Café / Bar / Pub",
					club_discotheque: "Club / Discotheque",
					hotel_pension: "Hotel / Pension",
					restaurant: "Restaurant"
				},
				title: "Type of estate"
			},
			house: {
				checkbox: {
					bungalow: "Bungalow",
					castle: "Castle",
					chalet: "Chalet",
					farmhouse: "Farmhouse",
					finca: "Finca",
					manor_house: "Manor house",
					multi_family_house: "Multi family house",
					rustico: "Rustico",
					semidetached_house: "Semidetached house",
					single_family_house: "Single family house",
					terrace_house: "Terrace house",
					town_house: "Town house",
					villa: "Villa"
				},
				title: "Type of house"
			},
			office: {
				checkbox: {
					atelier: "Atelier",
					coworking: "Coworking",
					living_and_commercial_building: "Living and commercial building",
					medical: "Medical",
					office: "Office"
				},
				title: "Type of office"
			},
			parking: {
				checkbox: {
					carport: "Carport",
					duplex: "Duplex",
					garage: "Garage",
					outside: "Outside parking",
					underground_garage: "Underground garage",
					underground_parking_space: "Underground parking"
				},
				title: "Type of parking"
			},
			plot: {
				checkbox: {
					commercial: "Commercial",
					leisure_facility: "Leisure facility",
					living: "Living"
				},
				title: "Type of plot"
			},
			storage_production: {
				checkbox: {
					garage_repair: "Garage / Repair",
					logistic_center: "Logistic Center",
					miscellaneous_storage: "Miscellaneous storage",
					outdoor_space: "Outdoor space",
					production_hall: "Production hall",
					warehouse_hall: "Warehouse hall"
				},
				title: "Type of estate"
			},
			trading: {
				checkbox: {
					kiosk: "Kiosk",
					sales_area: "Sales area",
					shopping_centre: "Shopping centre",
					showroom_space: "Showroom space",
					store: "Store"
				},
				title: "Type of shop"
			}
		},
		features: {
			checkbox: {
				attic_apartment: "Attic apartment",
				balcony_terrace: "Balcony / Terrace",
				bathroom_with_bathtub: "Bathroom with bathtub",
				bathroom_with_window: "Bathroom with window",
				built_in_kitchen: "Built-in kitchen",
				cellar: "Cellar",
				certificate_of_eligibility_needed: "certificate of eligibility required",
				elevator: "Elevator",
				free_of_commission: "Free of commission",
				furnished: "Fully furnished",
				garden: "Garden",
				no_attic_apartment: "No attic apartment",
				no_ground_floor: "No ground floor",
				parking_garage: "Parking / Garage",
				pets_allowed: "Pets allowed",
				reduced_mobility_access: "Reduced-mobility access",
				unfurnished: "Unfurnished",
				vacant: "Vacant / Not rented"
			},
			subtitle: {
				accessibility: "Accessibility",
				furnished_unfurnished: "Furnished / Unfurnished",
				indoor: "Indoor",
				usage: "Usage"
			},
			title: {
				features_excluded: "Features to exclude",
				features_included: "Features",
				foreclosure: "Foreclosure sale",
				new_builds: "New builds",
				subsidizing_housing: "Subsidizing housing"
			}
		},
		floor: "Floor",
		floors: {
			checkbox: {
				ground_floor: "Ground floor",
				no_ground_floor: "No ground floor",
				other_floors: "Other floors"
			}
		},
		living_area: {
			input_field: {
				surface_type: "m²"
			},
			title: "Living area"
		},
		location: {
			circles: "{{placeName}} +{{radius}} {{radiusUnit}}",
			circles_prefixed: "in {{placeName}} +{{radius}} {{radiusUnit}}",
			places_one: "{{placeName}}",
			places_other: "{{placeName}} (+{{count}})",
			places_prefixed_one: "in {{placeName}}",
			places_prefixed_other: "in {{placeName}} (+{{count}})",
			places_prefixed_zero: "",
			polygons: "personal map area",
			polygons_prefixed: "in personal map area",
			travels: "{{duration}} min {{mode}} from {{placeName}}",
			travels_prefixed: "at {{duration}} min {{mode}} from {{placeName}}"
		},
		plot_area: {
			input_field: {
				surface_type: "m²"
			},
			title: "Plot area"
		},
		project_type: {
			checkbox: {
				foreclosure_sale: "Foreclosure sale",
				investment: "Investment",
				new_builds: "New builds",
				resale: "Resale",
				shared_flats: "Shared flats",
				stock: "Stock",
				temporary_living: "Temporary living"
			},
			title: "Project type"
		}
	},
	deleteAlertConfirmation: {
		text: "You will no longer get notifications matching the criteria of this search",
		title: "Are you sure you want to delete this alert?"
	},
	distribution_type: {
		buy: "Buy",
		compulsory_auction: "Compulsory auction",
		rent: "Rent"
	},
	emailResubscribed: {
		action: "Continue searching",
		text: "Thanks for staying with us.",
		title: "You have resubscribed."
	},
	enums: {
		distributionType: {
			BUY: "buy",
			BUY_AUCTION: "buy",
			BUY_AUCTION_prefixed: "to buy",
			BUY_prefixed: "to buy",
			COMPULSORY_AUCTION: "compulsory auction",
			COMPULSORY_AUCTION_prefixed: "for compulsory auction",
			RENT: "rent",
			RENT_prefixed: "to rent"
		},
		estateType: {
			AGRICULTURE_FORESTRY: "Agriculture / Forestry",
			AGRICULTURE_FORESTRY_prefixed: "an Agriculture / Forestry",
			APARTMENT: "Apartment",
			APARTMENT_prefixed: "an Apartment",
			BUILDING: "Building",
			BUILDING_prefixed: "a Building",
			GASTRONOMY_HOTEL: "Gastronomy / Hotel",
			GASTRONOMY_HOTEL_prefixed: "a Gastronomy / Hotel",
			HOUSE: "House",
			HOUSE_prefixed: "a House",
			MISCELLANEOUS: "Miscellaneous",
			MISCELLANEOUS_prefixed: "Miscellaneous",
			OFFICE: "Office",
			OFFICE_prefixed: "an Office",
			PARKING: "Parking",
			PARKING_prefixed: "a Parking",
			PLOT: "Plot of land",
			PLOT_prefixed: "a Plot of land",
			PROJECT: "Project",
			PROJECT_prefixed: "a Project",
			SENIOR: "Senior apartment",
			SENIOR_prefixed: "a Senior apartment",
			STORAGE_PRODUCTION: "Storage / Production",
			STORAGE_PRODUCTION_prefixed: "a Storage / Production",
			TRADING: "Trading Area",
			TRADING_prefixed: "a Trading Area"
		},
		travelMode: {
			BIKE: "biking",
			BIKE_prefixed: "by biking",
			CAR: "car",
			CAR_prefixed: "by car",
			TRANSIT: "public transport",
			TRANSIT_prefixed: "by public transport",
			WALK: "walking",
			WALK_prefixed: "by walking"
		}
	},
	errorCreate: {
		text: "We were not able to create the alert. Try again later."
	},
	errorDelete: {
		text: "Can't delete your alert."
	},
	errorPage: {
		action: "Go to search alerts",
		text: "The link you followed may be broken, or the page may have been removed.",
		title: "This page isn’t available"
	},
	errorPageGeneric: {
		text: "The page is not available. Please try again later.",
		title: "An error has occured"
	},
	errorTechnicalRetry: {
		text: "A technical error has occured. Try again later."
	},
	errorUpdate: {
		text: "Can't update your alert. Try again later."
	},
	estate_type: {
		agriculture_forestry: "Agriculture / Forestry",
		apartment: "Apartment",
		gastronomy_hotel: "Gastronomy / Hotel",
		house: "House",
		house_apartment: "House / Apartment",
		investment_property: "Investment property",
		miscellaneous: "Miscellaneous",
		office: "Office",
		parking: "Parking",
		plot: "Plot of land",
		project: "Project",
		shared_apartment: "Shared apartment",
		storage_production: "Storage / Production",
		temporary_living: "Temporary living",
		trading: "Shop"
	},
	geometry_location: {
		title: "personal map area"
	},
	getAlerts: {
		action: "Create an alert"
	},
	goBack: {
		action: "Back"
	},
	goToSearch: {
		action: "Go to search"
	},
	hint: {
		"default": {
			text: "Get alerts for new properties matching your criteria.",
			title: "Set up your search alert"
		},
		travel_time: {
			text: "Saving your search is now possible!",
			title: "New feature"
		}
	},
	hpMfe: {
		mvp: {
			blockText: "Shall we continue ?"
		},
		noMore: {
			firstAction: "See more properties",
			firstText: "That's all for now !",
			secondAction: "Start a new search",
			secondText: "Now you can..."
		},
		seeMore: {
			action: "Load more",
			text: "{{number}} more properties in the selection"
		},
		text: "Latest matches based on your saved search",
		title: "Latest match based on your saved search"
	},
	iconCriteria: {
		rooms: {
			text: "ro"
		}
	},
	intermediaryMatchingMandate: {
		errors: {
			consent: "Without your consent, we cannot transmit your contact details to a selection of real estate agencies to assist you in your real estate project.",
			email: "Email is not valid",
			phoneNumber: "Phone number format is not valid",
			required: "{{field}} is required",
			string: "Wrong format"
		},
		informations: "By sending the request, you accept the Immowelt service. You will receive information based on the data you entered, the services used and the purpose of our business. You can object to this service anytime at datenschutz@immowelt.de. Further information can be found in the DataProtection policy.",
		labels: {
			consent: "I agree that my data are sent to a selection of real estate agencies proposing properties matching my alert, who could contact me by email or phone to helping me with my property project.",
			email: "Email",
			message: "Your message",
			name: "Name",
			phoneNumber: "Phone number"
		},
		legal: {
			text: {
				first: "{{upperLocal}} - Digital Classifieds {{country}} processes your data in order to transmit it to the indicated advertiser. In this context, we will transmit this data to our entities",
				fourth: "apply.",
				link: {
					first: "AVIV Group",
					second: "Privacy Policy.",
					third: "General Conditions of Use"
				},
				second: "and our external service providers. You can withdraw your consent to the processing of optional data at any time. For more information about your rights and your data, see our",
				third: "By clicking on \"Confirm my request\", our"
			}
		},
		placeholders: {
			message: "Provide details to help our experts understand exactly what you’re looking for.",
			phoneNumber: "+33 XXX XXX XXX"
		},
		submit: "Confirm my request",
		success: "Your contact request has been sent successfully.",
		suggestions: {
			text: "Receive direct proposals from trusted agents including unpublished listings tailored to your preferences.",
			title: "Get property suggestions from agents"
		}
	},
	loader_text: "Page is loading",
	noAlertError: {
		text: "You don't have any alerts yet. Go to the search page and set up search alerts that match your criteria",
		title: "Set up your first alert"
	},
	notifications: {
		recommendations: "Recommendations",
		title: "Notifications",
		tooltip: "Update your notification settings and get alternative properties based on your search alert"
	},
	notificationsEdit: {
		email: "Email notifications",
		frequency: {
			daily: "Once a day",
			hourly: "Once per hour",
			never: "Disabled",
			realtime: "In real time"
		},
		recommendation: {
			text: "Get alternative offers to your predefined search criteria",
			title: "Recommendations"
		},
		title: "Alert Settings",
		trigger: "Notifications"
	},
	pauseAllNotifications: {
		action: "Pause all notifications"
	},
	resubscribe: {
		action: "Resubscribe"
	},
	secondPage: {
		text: "Activate an alert to receive new properties matching your search criteria",
		title: "Speed up your search by creating a search alert"
	},
	segmentationUnsub: {
		buttons: {
			"continue": "Continue searching",
			manage: "Manage your search alerts"
		},
		text: {
			confirm: "All of your search alerts have been disabled.",
			landing: "You will be unsubscribed from all your saved searches"
		},
		title: {
			confirm: "You have successfully unsubscribed",
			landing: "Unsubscribe"
		}
	},
	title: {
		circles: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) in {{placeName}} +{{radius}} {{radiusUnit}}",
		"default": "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed)",
		errors: {
			tooLarge: "Maximum 100 characters allowed"
		},
		location: "{{estateTypes, list(type: disjunction)}} {{distributionType}} {{location}}",
		places: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) $t(savedSearch.criterias.location.places, { \"context\": \"prefixed\", \"placeName\":  \"{{placeName}}\", \"count\": {{placeCount}} })",
		polygons: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) in personal map area",
		travels: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) {{duration}} min {{mode}} from {{placeName}}"
	},
	unsubscribedEmailAlertSuccessfully: {
		title: "You have been successfully unsubscribed from {{alertName}}."
	},
	youCanAlso: {
		title: "You can also:"
	}
};
var savedSearch_alertClose_action$2 = "Close";
var savedSearch_alertCreated_text$2 = "Your alert has been created.";
var savedSearch_alertDelete_text$2 = "Your alert has been deleted.";
var savedSearch_alertNotCreated_text$2 = "Can't create the alert.";
var savedSearch_alertUpdated_text$2 = "Your alert has been created.";
var savedSearch_buttonCard_title$2 = "Receive alerts in real time";
var savedSearch_deleteAlert_title$2 = "Create an alert";
var savedSearch_duplicateError_text$2 = "You have already saved this search";
var savedSearch_duplicatePlaceId_text$2 = "There is an issue with your selected location, pelase try again later";
var savedSearch_errorSameSearchCriteria_text$2 = "You already have an alert with the same search criteria.";
var savedSearch_errorTechnical_text$2 = "A technical error has occured.";
var savedSearch_locationTooLarge_text$2 = "Search area too large, please reduce number of locations";
var savedSearch_locationTooSmall_text$2 = "The selected search area is too small";
var savedSearch_niceJob_text$2 = "Now you can create the new alert.";
var savedSearch_niceJob_title$2 = "Your alert has been deleted";
var savedSearch_placeIDforbidden_text$2 = "Your selected place type is not supported, please choose a different value";
var savedSearch_polygonAlert_text$2 = "We can't create an alert from the radius search";
var enTranslations = {
	savedSearch: savedSearch$2,
	savedSearch_alertClose_action: savedSearch_alertClose_action$2,
	savedSearch_alertCreated_text: savedSearch_alertCreated_text$2,
	savedSearch_alertDelete_text: savedSearch_alertDelete_text$2,
	savedSearch_alertNotCreated_text: savedSearch_alertNotCreated_text$2,
	savedSearch_alertUpdated_text: savedSearch_alertUpdated_text$2,
	savedSearch_buttonCard_title: savedSearch_buttonCard_title$2,
	savedSearch_deleteAlert_title: savedSearch_deleteAlert_title$2,
	savedSearch_duplicateError_text: savedSearch_duplicateError_text$2,
	savedSearch_duplicatePlaceId_text: savedSearch_duplicatePlaceId_text$2,
	savedSearch_errorSameSearchCriteria_text: savedSearch_errorSameSearchCriteria_text$2,
	savedSearch_errorTechnical_text: savedSearch_errorTechnical_text$2,
	savedSearch_locationTooLarge_text: savedSearch_locationTooLarge_text$2,
	savedSearch_locationTooSmall_text: savedSearch_locationTooSmall_text$2,
	savedSearch_niceJob_text: savedSearch_niceJob_text$2,
	savedSearch_niceJob_title: savedSearch_niceJob_title$2,
	savedSearch_placeIDforbidden_text: savedSearch_placeIDforbidden_text$2,
	savedSearch_polygonAlert_text: savedSearch_polygonAlert_text$2,
	"savedSearch_there’sNoRoomLeft_text": "You’ve reached the maximum number of {{maxAlertsCount}} alerts. Delete at least one to continue.",
	"savedSearch_there’sNoRoomLeft_title": "Delete an alert"
};

var savedSearch$1 = {
	actions: {
		alertTitle: "Titre de l'alerte",
		disabled: "Désactivée",
		edit: "Modifier",
		editAlert: "Modifier l'alerte",
		enabled: "Activée",
		listings: "Voir les annonces",
		update: "Mettre à jour"
	},
	alert: {
		title: "Alertes"
	},
	alertActivated: {
		action: "Alerte active"
	},
	alertAlertsTab: {
		action: "Alertes"
	},
	alertAppNotifications: {
		option: "Notifications de l'app"
	},
	alertContinue: {
		action: "Continuer"
	},
	alertCreate: {
		action: "Créer une alerte",
		text: "Recevoir une alerte dès qu'une nouvelle annonce correspond à vos critères"
	},
	alertDelete: {
		action: "Supprimer"
	},
	alertDeletePopUp: {
		text: "Vous avez atteint le nombre maximum d'alertes. Supprimez au moins une alerte pour en créer une nouvelle.",
		title: "Supprimez une alerte"
	},
	alertEdit: {
		title: "Modifier mon alerte"
	},
	alertEmail: {
		actionCreate: "Créer une alerte",
		created: {
			action: "Voir les résultats de la recherche",
			text: "Vous recevrez désormais de nouvelles suggestions de propriétés à l'adresse e-mail suivante :",
			title: "Votre alerte e-mail a été créée avec succès !"
		},
		disclaimer: {
			link: "cliquez ici",
			text: "Digital Classifieds France traite vos données afin de gérer votre demande d'alerte de nouvelles annonces immobilières par e-mail. Pour en savoir plus et exercer vos droits, "
		},
		invalid: "Vous devez entrer une adresse e-mail valide",
		option: "E-mail",
		text: "Entrez votre email pour recevoir toutes les nouvelles annonces immobilières personnalisées et la sélection des professionnels."
	},
	alertEmptyState: {
		text: "Soyez notifié dès que de nouvelles annonces correspondent à vos critères.",
		title: "Aucune alerte"
	},
	alertFavouritesTab: {
		action: "Favoris"
	},
	alertForceLogin: {
		promote: "Accédez à vos alertes depuis n'importe quel appareil. Gérez facilement vos préférences en créant un compte ou en vous connectant.",
		text: "Connectez-vous pour configurer vos alertes. Vous recevrez une notification dès qu'une nouvelle annonce correspond à vos critères.",
		title: "Connectez-vous pour gérer vos alertes"
	},
	alertInternetError: {
		text: "Activez vos données mobiles ou connectez-vous à un réseau Wi-Fi.",
		title: "Aucune connexion internet"
	},
	alertInvalid: {
		text: "Critères de recherche invalides"
	},
	alertLoading: {
		title: "Chargement des alertes"
	},
	alertRetry: {
		action: "Réessayer"
	},
	alertSave: {
		action: "Créer l'alerte"
	},
	alertSee: {
		action: "Voir les annonces"
	},
	alertSetup: {
		title: "Ne manquez aucune annonce"
	},
	alertSignin: {
		action: "Se connecter"
	},
	alertSignup: {
		action: "S'inscrire"
	},
	alertTechnicalError: {
		text: "Une erreur technique est survenue. Réessayez plus tard.",
		title: "Une erreur s'est produite"
	},
	alertValidate: {
		action: "Valider"
	},
	alertsPaused: {
		text: "Les notifications de toutes vos alertes ont été désactivées."
	},
	anonymousRegister: {
		action: "S’inscrire",
		text: "Ce n'est qu'alors que vous pourrez voir toutes vos recherches enregistrées.",
		title: "Veuillez vous connecter"
	},
	cancel: {
		action: "Annuler"
	},
	createAlertsByHitting: {
		text: "Vous pouvez créer une alerte depuis la page des résultats de recherche."
	},
	criterias: {
		area: {
			title: "Surface"
		},
		common: {
			range: {
				label: {
					max: "Max",
					min: "Min"
				}
			}
		},
		context_switch: {
			commercial: "Pour mon entreprise",
			residential: "Pour moi"
		},
		estate_subtype: {
			agriculture_forestry: {
				checkbox: {
					agriculture_company: "Entreprise agricole",
					farm_ranch: "Ferme / Ranch",
					forestry: "Forêts",
					gardening_orchard: "Jardinage / Verger",
					hunting_fishing: "Chasse / Pêche",
					riding: "Équestre",
					winery: "Vignobles"
				},
				title: "Type de propriété"
			},
			apartment: {
				checkbox: {
					loft: "Loft",
					multi_storey: "Plusieurs étages",
					penthouse: "Penthouse",
					studio: "Studio",
					terrace: "Terrasse"
				},
				title: "Type d'immobilier"
			},
			gastronomy_hotel: {
				checkbox: {
					cafe_bar_pub: "Café / Bar / Pub",
					club_discotheque: "Club / Discothèque",
					hotel_pension: "Hôtel / Pension",
					restaurant: "Restaurant"
				},
				title: "Type de propriété"
			},
			house: {
				checkbox: {
					bungalow: "Bungalow",
					castle: "Château",
					chalet: "Chalet",
					farmhouse: "Ferme",
					finca: "Finca",
					manor_house: "Manoir",
					multi_family_house: "Maison multifamiliale",
					rustico: "Rustico",
					semidetached_house: "Maison jumelée",
					single_family_house: "Maison individuelle",
					terrace_house: "Maison en terrasse",
					town_house: "Maison de ville",
					villa: "Villa"
				},
				title: "Type de maison"
			},
			office: {
				checkbox: {
					atelier: "Atelier",
					coworking: "Coworking",
					living_and_commercial_building: "Bâtiment d'habitation et commercial",
					medical: "Médical",
					office: "Bureau"
				},
				title: "Type de bureau"
			},
			parking: {
				checkbox: {
					carport: "Abri de voiture",
					duplex: "Garage double",
					garage: "Garage",
					outside: "Parking extérieur",
					underground_garage: "Garage souterrain",
					underground_parking_space: "Parking souterrain"
				},
				title: "Type de parking"
			},
			plot: {
				checkbox: {
					commercial: "Commercial",
					leisure_facility: "Centre de loisirs",
					living: "Résidentiel"
				},
				title: "Type de parcelle"
			},
			storage_production: {
				checkbox: {
					garage_repair: "Garage / Réparation",
					logistic_center: "Centre logistique",
					miscellaneous_storage: "Stockage divers",
					outdoor_space: "Espace extérieur",
					production_hall: "Salle de production",
					warehouse_hall: "Entrepôt"
				},
				title: "Type de propriété"
			},
			trading: {
				checkbox: {
					kiosk: "Kiosque",
					sales_area: "Espace de vente",
					shopping_centre: "Centre commercial",
					showroom_space: "Espace d'exposition",
					store: "Magasin"
				},
				title: "Type de magasin"
			}
		},
		features: {
			checkbox: {
				attic_apartment: "Appartement au dernier étage",
				balcony_terrace: "Balcon / Terrasse",
				bathroom_with_bathtub: "Salle de bain avec baignoire",
				bathroom_with_window: "Salle de bain avec fenêtre",
				built_in_kitchen: "Cuisine intégrée",
				cellar: "Cave",
				certificate_of_eligibility_needed: "Un certificat d'éligibilité est requis",
				elevator: "Ascenseur",
				free_of_commission: "Sans commission",
				furnished: "Entièrement meublé",
				garden: "Jardin",
				no_attic_apartment: "Pas d'appartement au dernier étage",
				no_ground_floor: "Pas de rez-de-chaussée",
				parking_garage: "Parking / Garage",
				pets_allowed: "Animaux autorisés",
				reduced_mobility_access: "Accès à mobilité réduite",
				unfurnished: "Non meublé",
				vacant: "Vacant / Non loué"
			},
			subtitle: {
				accessibility: "Accessibilité",
				furnished_unfurnished: "Meublé / Non meublé",
				indoor: "Intérieur",
				usage: "Utilisation"
			},
			title: {
				features_excluded: "Caractéristiques à exclure",
				features_included: "Caractéristiques",
				foreclosure: "Vente sur saisie",
				new_builds: "Nouvelles constructions",
				subsidizing_housing: "Logement subventionné"
			}
		},
		floor: "Etage",
		floors: {
			checkbox: {
				ground_floor: "Rez-de-chaussée",
				no_ground_floor: "Pas de rez-de-chaussée",
				other_floors: "Autres étages"
			}
		},
		living_area: {
			input_field: {
				surface_type: "m²"
			},
			title: "Surface"
		},
		location: {
			circles: "{{placeName}} +{{radius}} {{radiusUnit}}",
			circles_prefixed: "autour de {{placeName}} +{{radius}} {{radiusUnit}}",
			places_one: "{{placeName}}",
			places_other: "{{placeName}} (+{{count}})",
			places_prefixed_one: "à {{placeName}}",
			places_prefixed_other: "à {{placeName}} (+{{count}})",
			places_prefixed_zero: "",
			polygons: "zone personnalisée",
			polygons_prefixed: "dans zone personnalisée",
			travels: "{{duration}} min {{mode}} de {{placeName}}",
			travels_prefixed: "à {{duration}} min {{mode}} de {{placeName}}"
		},
		plot_area: {
			input_field: {
				surface_type: "m²"
			},
			title: "Surface de la parcelle"
		},
		project_type: {
			checkbox: {
				foreclosure_sale: "Vente sur saisie",
				investment: "Investissement",
				new_builds: "Nouvelles constructions",
				resale: "Ancien",
				shared_flats: "Appartements partagés",
				stock: "Stock",
				temporary_living: "Logement temporaire"
			},
			title: "Type de projet"
		}
	},
	deleteAlertConfirmation: {
		text: "Vous ne recevrez plus de notifications correspondant aux critères de cette recherche",
		title: "Etes-vous sûr de vouloir supprimer cette alerte ?"
	},
	distribution_type: {
		buy: "Acheter",
		compulsory_auction: "Enchères obligatoires",
		rent: "Louer"
	},
	emailResubscribed: {
		action: "Continuer la recherche",
		text: "Merci d'être resté avec nous.",
		title: "Vous vous êtes réabonné."
	},
	enums: {
		distributionType: {
			BUY: "acheter",
			BUY_AUCTION: "acheter aux enchères",
			BUY_AUCTION_prefixed: "pour acheter aux enchères",
			BUY_prefixed: "à acheter",
			COMPULSORY_AUCTION: "enchères obligatoires",
			COMPULSORY_AUCTION_prefixed: "pour enchères obligatoires",
			RENT: "louer",
			RENT_prefixed: "à louer"
		},
		estateType: {
			AGRICULTURE_FORESTRY: "Agriculture / Sylviculture",
			AGRICULTURE_FORESTRY_prefixed: "une Agriculture / Sylviculture",
			APARTMENT: "Appartement",
			APARTMENT_prefixed: "un Appartement",
			BUILDING: "Bâtiment",
			BUILDING_prefixed: "un Bâtiment",
			GASTRONOMY_HOTEL: "Gastronomie / Hôtel",
			GASTRONOMY_HOTEL_prefixed: "un Gastronomie / Hôtel",
			HOUSE: "Maison",
			HOUSE_prefixed: "une Maison",
			MISCELLANEOUS: "Divers",
			MISCELLANEOUS_prefixed: "Divers",
			OFFICE: "Bureau",
			OFFICE_prefixed: "un Bureau",
			PARKING: "Parking",
			PARKING_prefixed: "un Parking",
			PLOT: "Terrain",
			PLOT_prefixed: "un Terrain",
			PROJECT: "Projet",
			PROJECT_prefixed: "un Projet",
			SENIOR: "Résidence pour personnes âgées",
			SENIOR_prefixed: "une Résidence pour personnes âgées",
			STORAGE_PRODUCTION: "Stockage / Production",
			STORAGE_PRODUCTION_prefixed: "un Stockage / Production",
			TRADING: "Boutique",
			TRADING_prefixed: "une Boutique"
		},
		travelMode: {
			BIKE: "vélo",
			BIKE_prefixed: "à vélo",
			CAR: "voiture",
			CAR_prefixed: "en voiture",
			TRANSIT: "transport en commun",
			TRANSIT_prefixed: "en transport en commun",
			WALK: "à pied",
			WALK_prefixed: "à pied"
		}
	},
	errorCreate: {
		text: "Impossible de créer l'alerte. Réessayez plus tard."
	},
	errorDelete: {
		text: "Impossible de supprimer votre alerte."
	},
	errorPage: {
		action: "Accédez aux alertes de recherche",
		text: "Il se peut que le lien que vous avez suivi soit rompu ou que la page ait été supprimée.",
		title: "Cette page n'est pas disponible."
	},
	errorPageGeneric: {
		text: "La page n'est pas disponible. Veuillez réessayer ultérieurement.",
		title: "Une erreur s'est produite"
	},
	errorTechnicalRetry: {
		text: "Une erreur technique ait survenue. Réessayez plus tard."
	},
	errorUpdate: {
		text: "Impossible de mettre à jour votre alerte. Réessayez plus tard."
	},
	estate_type: {
		agriculture_forestry: "Agriculture / sylviculture",
		apartment: "Appartement",
		gastronomy_hotel: "Gastronomie / Hôtel",
		house: "Maison",
		house_apartment: "Maison / Appartement",
		investment_property: "Investissement immobilier",
		miscellaneous: "Divers",
		office: "Bureau",
		parking: "Parking",
		plot: "Terrain",
		project: "Projet",
		shared_apartment: "Appartement partagé",
		storage_production: "Stockage / Production",
		temporary_living: "Logement temporaire",
		trading: "Boutique"
	},
	geometry_location: {
		title: "zone personnalisée"
	},
	getAlerts: {
		action: "Créer une alerte"
	},
	goBack: {
		action: "Retour"
	},
	goToSearch: {
		action: "Rechercher"
	},
	hint: {
		"default": {
			text: "Activez l'alerte pour recevoir en temps réel les nouveaux biens correspondant à vos critères",
			title: "Créez votre alerte de recherche"
		},
		travel_time: {
			text: "Gagnez du temps en sauvegardant votre recherche et recevez les dernières annonces dès qu’elles sont publiées !",
			title: "Nouvelle fonctionnalité"
		}
	},
	hpMfe: {
		mvp: {
			blockText: "On continue ?"
		},
		noMore: {
			firstAction: "Voir plus de biens",
			firstText: "C'est tout pour le moment !",
			secondAction: "Nouvelle recherche",
			secondText: "Vous pouvez désormais..."
		},
		seeMore: {
			action: "Charger plus",
			text: "{{number}} autres propriétés dans la sélection"
		},
		text: "Nouveau(x) bien(s) lié(s) à votre recherche sauvegardée",
		title: "Du nouveau pour votre recherche sauvegardée"
	},
	iconCriteria: {
		rooms: {
			text: "pc"
		}
	},
	intermediaryMatchingMandate: {
		errors: {
			consent: "Sans votre accord, nous ne pouvons transmettre vos coordonnées à une sélection d'agences immobilières pour vous accompagner dans votre projet immobilier.",
			email: "L'email n'est pas valide",
			phoneNumber: "Le format du numéro de téléphone n'est pas valide",
			required: "Le champ {{field}} est obligatoire",
			string: "Format incorrect"
		},
		informations: "En envoyant votre demande, vous acceptez le service Immowelt. Vous recevrez des informations basées sur les données que vous avez saisies, les services utilisés et l'objectif de notre activité. Vous pouvez vous opposer à ce service à tout moment à l'adresse datenschutz@immowelt.de. Vous trouverez de plus amples informations dans la déclaration de protection des données.",
		labels: {
			consent: "J'accepte que mes données soient transmises à une sélection d'agences immobilières proposant des biens correspondant à mon alerte, qui pourront me contacter par email ou par téléphone pour m'aider dans mon projet immobilier.",
			email: "Email",
			message: "Votre message",
			name: "Nom",
			phoneNumber: "Téléphone"
		},
		legal: {
			text: {
				first: "{{upperLocal}} - Digital Classifieds {{country}} procède au traitement de vos données afin les transmettre à l’annonceur indiqué. Dans ce cadre, nous transmettrons ces données à nos entités",
				fourth: "s'appliquent.",
				link: {
					first: "AVIV Group",
					second: "Politique de confidentialité.",
					third: "Conditions Générales d’Utilisation"
				},
				second: "et nos prestataires de services externes. Vous pouvez retirer à tout moment votre consentement au traitement des données facultatives. Pour plus d'informations sur vos droits et vos données, consultez notre",
				third: "En cliquant sur \"Confirmer ma demande\", notre"
			}
		},
		placeholders: {
			message: "Fournissez des détails pour aider nos experts à comprendre exactement ce que vous recherchez.",
			phoneNumber: "+33 XXX XXX XXX"
		},
		submit: "Confirmer ma demande",
		success: "Votre demande de contact a été envoyée avec succès.",
		suggestions: {
			text: "Recevez des propositions directes d'agents de confiance, y compris des annonces inédites adaptées à vos préférences.",
			title: "Obtenez des suggestions de propriétés auprès d'agents"
		}
	},
	loader_text: "La page est en cours de chargement",
	noAlertError: {
		text: "Vous n'avez pas encore d'alertes. Allez sur la page de recherche et créez des alertes de recherche qui correspondent à vos critères",
		title: "Créez votre première alerte"
	},
	notifications: {
		recommendations: "Recommandations",
		title: "Notifications",
		tooltip: "Mettez à jour vos paramètres de notification et recevez des biens alternatifs basés sur votre alerte."
	},
	notificationsEdit: {
		email: "E-mail",
		frequency: {
			daily: "Une fois par jour",
			hourly: "Une fois par heure",
			never: "Désactivée",
			realtime: "En temps réel"
		},
		recommendation: {
			text: "Obtenez des offres alternatives à vos critères de recherche prédéfinis",
			title: "Recommandations"
		},
		title: "Paramètres d'alerte",
		trigger: "Paramètres des notifications"
	},
	pauseAllNotifications: {
		action: "Suspendre toutes les notifications"
	},
	resubscribe: {
		action: "Se réabonner"
	},
	secondPage: {
		text: "Activez l'alerte pour recevoir les nouveaux biens correspondant à vos critères",
		title: "Accélérez votre recherche en créant une alerte"
	},
	segmentationUnsub: {
		buttons: {
			"continue": "Continuer la recherche",
			manage: "Gérez vos recherches"
		},
		text: {
			confirm: "Toutes vos alertes de recherche ont été désactivées.",
			landing: "Vous vous désabonnerez également de toutes les recherches enregistrées"
		},
		title: {
			confirm: "Vous vous êtes désabonné avec succès",
			landing: "Se désinscrire"
		}
	},
	title: {
		circles: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) autour de {{placeName}} +{{radius}} {{radiusUnit}}",
		"default": "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed)",
		errors: {
			tooLarge: "Maximum 100 caractères autorisés"
		},
		location: "{{estateTypes, list(type: disjunction)}} {{distributionType}} {{location}}",
		places: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) $t(savedSearch.criterias.location.places, { \"context\": \"prefixed\", \"placeName\":  \"{{placeName}}\", \"count\": {{placeCount}} })",
		polygons: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) dans zone personnalisée",
		travels: "{{estateTypes, list(type: disjunction)}} $t(savedSearch.enums.distributionType.{{distributionType}}_prefixed) à {{duration}} min {{mode}} de {{placeName}}"
	},
	unsubscribedEmailAlertSuccessfully: {
		title: "Vous avez été désabonné avec succès de {{alertName}}."
	},
	youCanAlso: {
		title: "Vous pouvez également :"
	}
};
var savedSearch_alertClose_action$1 = "Fermer";
var savedSearch_alertCreated_text$1 = "Votre alerte a été créée.";
var savedSearch_alertDelete_text$1 = "Votre alerte a été supprimée.";
var savedSearch_alertNotCreated_text$1 = "Impossible de créer l'alerte.";
var savedSearch_alertUpdated_text$1 = "Votre alerte a été créée.";
var savedSearch_buttonCard_title$1 = "Recevez des alertes en temps réel";
var savedSearch_deleteAlert_title$1 = "Créer une alerte";
var savedSearch_duplicateError_text$1 = "Vous avez déjà sauvegardé cette recherche";
var savedSearch_duplicatePlaceId_text$1 = "Il y a un problème avec la zone que vous avez sélectionnée, veuillez réessayer plus tard";
var savedSearch_errorSameSearchCriteria_text$1 = "Vous avez déjà une alerte avec les mêmes critères de recherche";
var savedSearch_errorTechnical_text$1 = "Une erreur technique s'est produite";
var savedSearch_locationTooLarge_text$1 = "Zone de recherche trop grande, veuillez réduire le nombre de localisations.";
var savedSearch_locationTooSmall_text$1 = "La zone de recherche sélectionnée est trop petite";
var savedSearch_niceJob_text$1 = "Vous pouvez maintenant créer une nouvelle alerte";
var savedSearch_niceJob_title$1 = "Votre alerte a été supprimée";
var savedSearch_placeIDforbidden_text$1 = "";
var savedSearch_polygonAlert_text$1 = "Nous ne pouvons pas créer d'alerte à partir de la recherche de rayon";
var frTranslations = {
	savedSearch: savedSearch$1,
	savedSearch_alertClose_action: savedSearch_alertClose_action$1,
	savedSearch_alertCreated_text: savedSearch_alertCreated_text$1,
	savedSearch_alertDelete_text: savedSearch_alertDelete_text$1,
	savedSearch_alertNotCreated_text: savedSearch_alertNotCreated_text$1,
	savedSearch_alertUpdated_text: savedSearch_alertUpdated_text$1,
	savedSearch_buttonCard_title: savedSearch_buttonCard_title$1,
	savedSearch_deleteAlert_title: savedSearch_deleteAlert_title$1,
	savedSearch_duplicateError_text: savedSearch_duplicateError_text$1,
	savedSearch_duplicatePlaceId_text: savedSearch_duplicatePlaceId_text$1,
	savedSearch_errorSameSearchCriteria_text: savedSearch_errorSameSearchCriteria_text$1,
	savedSearch_errorTechnical_text: savedSearch_errorTechnical_text$1,
	savedSearch_locationTooLarge_text: savedSearch_locationTooLarge_text$1,
	savedSearch_locationTooSmall_text: savedSearch_locationTooSmall_text$1,
	savedSearch_niceJob_text: savedSearch_niceJob_text$1,
	savedSearch_niceJob_title: savedSearch_niceJob_title$1,
	savedSearch_placeIDforbidden_text: savedSearch_placeIDforbidden_text$1,
	savedSearch_polygonAlert_text: savedSearch_polygonAlert_text$1,
	"savedSearch_there’sNoRoomLeft_text": "Vous avez atteint le nombre maximum de {{maxAlertsCount}} alertes. Supprimez-en au moins une pour continuer.",
	"savedSearch_there’sNoRoomLeft_title": "Supprimer une alerte"
};

var savedSearch = {
	actions: {
		alertTitle: "",
		disabled: "",
		edit: "",
		editAlert: "",
		enabled: "",
		listings: "",
		update: ""
	},
	alert: {
		title: "Meldingen"
	},
	alertActivated: {
		action: "Melding ingeschakeld"
	},
	alertAlertsTab: {
		action: "Meldingen"
	},
	alertAppNotifications: {
		option: "App-berichten"
	},
	alertContinue: {
		action: ""
	},
	alertCreate: {
		action: "Een melding maken",
		text: "Ontvang meldingen wanneer een nieuwe eigendom voldoet aan je criteria"
	},
	alertDelete: {
		action: "Verwijderen"
	},
	alertDeletePopUp: {
		text: "Je hebt het maximale aantal meldingen bereikt. Verwijder er ten minste één van om door te gaan.",
		title: "Een melding verwijderen"
	},
	alertEdit: {
		title: "Mijn melding bewerken"
	},
	alertEmail: {
		actionCreate: "",
		created: {
			action: "",
			text: "",
			title: ""
		},
		disclaimer: {
			link: "",
			text: ""
		},
		invalid: "",
		option: "E-mail",
		text: ""
	},
	alertEmptyState: {
		text: "Krijg een bericht wanneer nieuwe advertenties voldoen aan je criteria.",
		title: "Geen melding"
	},
	alertFavouritesTab: {
		action: "Verlanglijst"
	},
	alertForceLogin: {
		promote: "",
		text: "Log in om je meldingen in te stellen. Je ontvangt een bericht wanneer nieuwe advertenties aan je criteria voldoen.",
		title: "Log in om je meldingen te beheren"
	},
	alertInternetError: {
		text: "Schakel je mobiele gegevens in of maak verbinding met een wifi-netwerk.",
		title: "Geen internetverbinding"
	},
	alertInvalid: {
		text: "Ongeldige zoekcriteria"
	},
	alertLoading: {
		title: "Meldingen worden geladen"
	},
	alertRetry: {
		action: "Opnieuw proberen"
	},
	alertSave: {
		action: "De melding maken"
	},
	alertSee: {
		action: "Eigendommen bekijken"
	},
	alertSetup: {
		title: "Ontvang een bericht van nieuwe advertenties"
	},
	alertSignin: {
		action: "Inloggen"
	},
	alertSignup: {
		action: "Registreren"
	},
	alertTechnicalError: {
		text: "Er is een technische fout opgetreden. Probeer het later opnieuw.",
		title: "Er ging iets mis"
	},
	alertValidate: {
		action: "Valideren"
	},
	alertsPaused: {
		text: "Berichten voor alle meldingen zijn gepauzeerd. Je kunt ze op elk moment opnieuw activeren."
	},
	anonymousRegister: {
		action: "Registreren",
		text: "Alleen dan kunt u al uw opgeslagen zoekopdrachten zien.",
		title: "Log alsjeblieft in"
	},
	cancel: {
		action: "Annuleer"
	},
	createAlertsByHitting: {
		text: "Je kunt een melding maken vanaf de pagina met zoekresultaten."
	},
	criterias: {
		area: {
			title: "Gebied"
		},
		common: {
			range: {
				label: {
					max: "Max",
					min: "Min"
				}
			}
		},
		context_switch: {
			commercial: "Voor mijn bedrijf",
			residential: "Voor mij"
		},
		estate_subtype: {
			agriculture_forestry: {
				checkbox: {
					agriculture_company: "Landbouwbedrijf",
					farm_ranch: "Boerderij",
					forestry: "Bosbouw",
					gardening_orchard: "Tuinieren / Boomgaard",
					hunting_fishing: "Jagen/Vissen",
					riding: "rijden",
					winery: "Wijnmakerij"
				},
				title: "Type landgoed"
			},
			apartment: {
				checkbox: {
					loft: "Zolder",
					multi_storey: "Meer verdiepingen",
					penthouse: "Penthouse",
					studio: "Studio",
					terrace: "Terras"
				},
				title: "Landgoed type"
			},
			gastronomy_hotel: {
				checkbox: {
					cafe_bar_pub: "Café / Bar / Pub",
					club_discotheque: "Club / Discotheek",
					hotel_pension: "Hotel / Pension",
					restaurant: "Restaurant"
				},
				title: "Type landgoed"
			},
			house: {
				checkbox: {
					bungalow: "Bungalow",
					castle: "Kasteel",
					chalet: "Chalet",
					farmhouse: "Boerderij",
					finca: "Finca",
					manor_house: "Landhuis",
					multi_family_house: "Meergezinswoning",
					rustico: "Rustico",
					semidetached_house: "Halfvrijstaand huis",
					single_family_house: "Eengezinswoning",
					terrace_house: "Terraswoning",
					town_house: "Herenhuis",
					villa: "Villa"
				},
				title: "Type huis"
			},
			office: {
				checkbox: {
					atelier: "Atelier",
					coworking: "Coworking",
					living_and_commercial_building: "Wonen en commerciële gebouwen",
					medical: "Medisch",
					office: "Kantoor"
				},
				title: "Type kantoor"
			},
			parking: {
				checkbox: {
					carport: "Carport",
					duplex: "Duplex",
					garage: "Garage",
					outside: "Buiten parkeren",
					underground_garage: "Ondergrondse garage",
					underground_parking_space: "Ondergronds parkeren"
				},
				title: "Type parkeerplaats"
			},
			plot: {
				checkbox: {
					commercial: "Commercieel",
					leisure_facility: "Vrijetijdscentrum",
					living: "Wonen"
				},
				title: "Type perceel"
			},
			storage_production: {
				checkbox: {
					garage_repair: "Garage / Reparatie",
					logistic_center: "Logistiek centrum",
					miscellaneous_storage: "Diverse opslag",
					outdoor_space: "Buitenruimte",
					production_hall: "Productiehal",
					warehouse_hall: "Magazijn"
				},
				title: "Type landgoed"
			},
			trading: {
				checkbox: {
					kiosk: "Kiosk",
					sales_area: "Verkoopgebied",
					shopping_centre: "Winkelcentrum",
					showroom_space: "Showroom ruimte",
					store: "Winkel"
				},
				title: "Type winkel"
			}
		},
		features: {
			checkbox: {
				attic_apartment: "Zolder appartement",
				balcony_terrace: "Balkon / Terras",
				bathroom_with_bathtub: "Badkamer met bad",
				bathroom_with_window: "Badkamer met raam",
				built_in_kitchen: "Ingebouwde keuken",
				cellar: "Kelder",
				certificate_of_eligibility_needed: "",
				elevator: "Lift",
				free_of_commission: "Vrij van commissie",
				furnished: "Volledig gemeubileerd",
				garden: "Tuin",
				no_attic_apartment: "Geen zolderappartement",
				no_ground_floor: "Geen begane grond",
				parking_garage: "Parkeren / Garage",
				pets_allowed: "Huisdieren toegestaan",
				reduced_mobility_access: "Toegang met beperkte mobiliteit",
				unfurnished: "Ongemeubileerd",
				vacant: "Leeg / Niet verhuurd"
			},
			subtitle: {
				accessibility: "Toegankelijkheid",
				furnished_unfurnished: "Gemeubileerd / Gestoffeerd",
				indoor: "Binnen",
				usage: "Gebruik"
			},
			title: {
				features_excluded: "",
				features_included: "",
				foreclosure: "Verkoop",
				new_builds: "Nieuwbouw",
				subsidizing_housing: "Huisvesting subsidiëren"
			}
		},
		floor: "Vloer",
		floors: {
			checkbox: {
				ground_floor: "Begane grond",
				no_ground_floor: "Geen begane grond",
				other_floors: "Andere vloeren"
			}
		},
		living_area: {
			input_field: {
				surface_type: "m²"
			},
			title: "Leefruimte"
		},
		location: {
			circles: "",
			circles_prefixed: "",
			places_one: "",
			places_other: "",
			places_prefixed_one: "",
			places_prefixed_other: "",
			places_prefixed_zero: "",
			polygons: "",
			polygons_prefixed: "",
			travels: "",
			travels_prefixed: ""
		},
		plot_area: {
			input_field: {
				surface_type: "m²"
			},
			title: ""
		},
		project_type: {
			checkbox: {
				foreclosure_sale: "Verkoop",
				investment: "Investering",
				new_builds: "Nieuwbouw",
				resale: "Wederverkoop",
				shared_flats: "Gedeelde flats",
				stock: "Voorraad",
				temporary_living: "Tijdelijk wonen"
			},
			title: "Type project"
		}
	},
	deleteAlertConfirmation: {
		text: "",
		title: "Weet je zeker dat je deze melding wilt verwijderen?"
	},
	distribution_type: {
		buy: "Kopen",
		compulsory_auction: "Verplichte veiling",
		rent: "Huren"
	},
	emailResubscribed: {
		action: "Doorgaan met zoeken",
		text: "Bedankt om bij ons te blijven.",
		title: "Je hebt je opnieuw geabonneerd."
	},
	enums: {
		distributionType: {
			BUY: "",
			BUY_AUCTION: "",
			BUY_AUCTION_prefixed: "",
			BUY_prefixed: "",
			COMPULSORY_AUCTION: "",
			COMPULSORY_AUCTION_prefixed: "",
			RENT: "",
			RENT_prefixed: ""
		},
		estateType: {
			AGRICULTURE_FORESTRY: "",
			AGRICULTURE_FORESTRY_prefixed: "",
			APARTMENT: "",
			APARTMENT_prefixed: "",
			BUILDING: "",
			BUILDING_prefixed: "",
			GASTRONOMY_HOTEL: "",
			GASTRONOMY_HOTEL_prefixed: "",
			HOUSE: "",
			HOUSE_prefixed: "",
			MISCELLANEOUS: "",
			MISCELLANEOUS_prefixed: "",
			OFFICE: "",
			OFFICE_prefixed: "",
			PARKING: "",
			PARKING_prefixed: "",
			PLOT: "",
			PLOT_prefixed: "",
			PROJECT: "",
			PROJECT_prefixed: "",
			SENIOR: "",
			SENIOR_prefixed: "",
			STORAGE_PRODUCTION: "",
			STORAGE_PRODUCTION_prefixed: "",
			TRADING: "",
			TRADING_prefixed: ""
		},
		travelMode: {
			BIKE: "",
			BIKE_prefixed: "",
			CAR: "",
			CAR_prefixed: "",
			TRANSIT: "",
			TRANSIT_prefixed: "",
			WALK: "",
			WALK_prefixed: ""
		}
	},
	errorCreate: {
		text: "We konden de melding niet maken. Probeer het later opnieuw."
	},
	errorDelete: {
		text: "Kan je melding niet verwijderen."
	},
	errorPage: {
		action: "Ga naar zoekmeldingen",
		text: "De link die je volgde kan verbroken zijn of de pagina kan verwijderd zijn.",
		title: "Deze pagina is niet beschikbaar."
	},
	errorPageGeneric: {
		text: "",
		title: ""
	},
	errorTechnicalRetry: {
		text: "Er is een technische fout opgetreden. Probeer het later opnieuw."
	},
	errorUpdate: {
		text: "Kan je melding niet bijwerken. Probeer het later opnieuw."
	},
	estate_type: {
		agriculture_forestry: "Landbouw / Bosbouw",
		apartment: "Appartement",
		gastronomy_hotel: "Gastronomie / Hotel",
		house: "Huis",
		house_apartment: "Huis / Appartement",
		investment_property: "Vastgoedbelegging",
		miscellaneous: "Diverse",
		office: "Kantoor",
		parking: "Parkeren",
		plot: "Perceel",
		project: "Project",
		shared_apartment: "Gedeeld appartement",
		storage_production: "Opslag / Productie",
		temporary_living: "Tijdelijk wonen",
		trading: "Winkel op"
	},
	geometry_location: {
		title: ""
	},
	getAlerts: {
		action: "Een melding maken"
	},
	goBack: {
		action: ""
	},
	goToSearch: {
		action: "Ga naar zoekopdracht"
	},
	hint: {
		"default": {
			text: "",
			title: ""
		},
		travel_time: {
			text: "Het is nu mogelijk om uw zoekopdracht op te slaan!",
			title: "Nieuwe functie"
		}
	},
	hpMfe: {
		mvp: {
			blockText: ""
		},
		noMore: {
			firstAction: "",
			firstText: "",
			secondAction: "",
			secondText: ""
		},
		seeMore: {
			action: "",
			text: ""
		},
		text: "",
		title: ""
	},
	iconCriteria: {
		rooms: {
			text: ""
		}
	},
	intermediaryMatchingMandate: {
		errors: {
			consent: "",
			email: "",
			phoneNumber: "",
			required: "",
			string: ""
		},
		informations: "",
		labels: {
			consent: "",
			email: "",
			message: "",
			name: "",
			phoneNumber: ""
		},
		legal: {
			text: {
				first: "",
				fourth: "",
				link: {
					first: "",
					second: "",
					third: ""
				},
				second: "",
				third: ""
			}
		},
		placeholders: {
			message: "",
			phoneNumber: ""
		},
		submit: "",
		success: "",
		suggestions: {
			text: "",
			title: ""
		}
	},
	loader_text: "",
	noAlertError: {
		text: "",
		title: ""
	},
	notifications: {
		recommendations: "",
		title: "",
		tooltip: ""
	},
	notificationsEdit: {
		email: "",
		frequency: {
			daily: "",
			hourly: "",
			never: "",
			realtime: ""
		},
		recommendation: {
			text: "",
			title: ""
		},
		title: "",
		trigger: ""
	},
	pauseAllNotifications: {
		action: "Pauzeer alle berichten"
	},
	resubscribe: {
		action: "Opnieuw abonneren"
	},
	secondPage: {
		text: "Activeer de waarschuwing om de nieuwe eigendommen te ontvangen die voldoen aan uw criteria",
		title: "Versnel uw zoekopdracht door een opgeslagen zoekopdracht aan te maken"
	},
	segmentationUnsub: {
		buttons: {
			"continue": "",
			manage: ""
		},
		text: {
			confirm: "",
			landing: ""
		},
		title: {
			confirm: "U bent succesvol afgemeld",
			landing: "Uitschrijven"
		}
	},
	title: {
		circles: "",
		"default": "",
		errors: {
			tooLarge: "Maximaal 100 tekens toegestaan"
		},
		location: "",
		places: "",
		polygons: "",
		travels: ""
	},
	unsubscribedEmailAlertSuccessfully: {
		title: "Je bent met succes afgemeld voor {{alertName}}."
	},
	youCanAlso: {
		title: "Je kunt ook:"
	}
};
var savedSearch_alertClose_action = "Afsluiten";
var savedSearch_alertCreated_text = "Je melding werd gemaakt.";
var savedSearch_alertDelete_text = "Je melding werd verwijderd.";
var savedSearch_alertNotCreated_text = "Kan de melding niet maken.";
var savedSearch_alertUpdated_text = "Je melding werd gemaakt.";
var savedSearch_buttonCard_title = "";
var savedSearch_deleteAlert_title = "Bewaar zoekactie";
var savedSearch_duplicateError_text = "";
var savedSearch_duplicatePlaceId_text = "";
var savedSearch_errorSameSearchCriteria_text = "Je hebt al een melding met dezelfde zoekcriteria.";
var savedSearch_errorTechnical_text = "Er is een technische fout opgetreden.";
var savedSearch_locationTooLarge_text = "";
var savedSearch_locationTooSmall_text = "";
var savedSearch_niceJob_text = "Nu kun je de nieuwe melding maken.";
var savedSearch_niceJob_title = "Je melding werd verwijderd";
var savedSearch_placeIDforbidden_text = "";
var savedSearch_polygonAlert_text = "";
var nlTranslations = {
	savedSearch: savedSearch,
	savedSearch_alertClose_action: savedSearch_alertClose_action,
	savedSearch_alertCreated_text: savedSearch_alertCreated_text,
	savedSearch_alertDelete_text: savedSearch_alertDelete_text,
	savedSearch_alertNotCreated_text: savedSearch_alertNotCreated_text,
	savedSearch_alertUpdated_text: savedSearch_alertUpdated_text,
	savedSearch_buttonCard_title: savedSearch_buttonCard_title,
	savedSearch_deleteAlert_title: savedSearch_deleteAlert_title,
	savedSearch_duplicateError_text: savedSearch_duplicateError_text,
	savedSearch_duplicatePlaceId_text: savedSearch_duplicatePlaceId_text,
	savedSearch_errorSameSearchCriteria_text: savedSearch_errorSameSearchCriteria_text,
	savedSearch_errorTechnical_text: savedSearch_errorTechnical_text,
	savedSearch_locationTooLarge_text: savedSearch_locationTooLarge_text,
	savedSearch_locationTooSmall_text: savedSearch_locationTooSmall_text,
	savedSearch_niceJob_text: savedSearch_niceJob_text,
	savedSearch_niceJob_title: savedSearch_niceJob_title,
	savedSearch_placeIDforbidden_text: savedSearch_placeIDforbidden_text,
	savedSearch_polygonAlert_text: savedSearch_polygonAlert_text,
	"savedSearch_there’sNoRoomLeft_text": "Je hebt het maximale aantal {{maxAlertsCount}} meldingen bereikt. Verwijder er ten minste één van om door te gaan.",
	"savedSearch_there’sNoRoomLeft_title": "Een melding verwijderen"
};

const getTranslationsFiles = (locale)=>{
    switch(locale){
        case 'de':
            return deTranslations;
        case 'en':
            return enTranslations;
        case 'fr':
            return frTranslations;
        case 'nl':
            return nlTranslations;
        default:
            return deTranslations;
    }
};
const resources = {
    de: {
        translation: getTranslationsFiles('de')
    },
    en: {
        translation: getTranslationsFiles('en')
    },
    fr: {
        translation: getTranslationsFiles('fr')
    }
};
instance.use(initReactI18next).init({
    resources,
    lng: 'en',
    fallbackLng: 'de',
    interpolation: {
        escapeValue: false
    }
});

const createOnLi = async ({ payload, configCalls })=>{
    let result = {
        error: 'undefined'
    };
    const wlBody = {
        title: payload?.title,
        email: payload?.email,
        criteria: payload?.search?.criteria,
        channels: defaultChannels,
        userInteractionData: payload?.userInteractionData
    };
    const legacyBody = {
        title: payload?.title,
        email: payload?.email,
        criteria: payload?.search?.criteria,
        currentUrl: payload?.currentUrl,
        externalProperties: payload?.search?.externalProperties
    };
    const success = (response)=>{
        result = commonSuccess(response);
    };
    const errorOnWl = (errorResponse)=>{
        result = commonError(errorResponse);
    };
    const error = async (errorResponse)=>{
        if (errorResponse.httpStatusCode === 410) {
            await addSavedSearchEmail(success, errorOnWl, {
                body: wlBody
            });
        } else {
            result = {
                error: errorResponse?.errorCode === 'UNKNOWN_ERROR' ? `Error response with status: ${errorResponse?.httpStatusCode}` : errorResponse?.errorCode ?? `Error response with status: ${errorResponse?.httpStatusCode}`
            };
        }
    };
    if (payload?.search?.criteria?.location?.placeIds?.length === 0) {
        addSavedSearchEmail(success, errorOnWl, {
            body: wlBody
        });
    } else {
        await addSavedSearchEmailLI(success, error, {
            config: configCalls,
            body: legacyBody
        });
    }
    while(result.error === 'undefined'){
        await new Promise((resolve)=>setTimeout(resolve, 50));
    }
    return result;
};

const createOnDefault = async ({ payload, configCalls })=>{
    let result = {
        error: 'undefined'
    };
    const unloggedBody = {
        title: payload?.title,
        email: payload?.email,
        channels: defaultChannels,
        criteria: payload?.search?.criteria,
        userInteractionData: payload?.userInteractionData
    };
    const success = (response)=>{
        result = commonSuccess(response);
    };
    const error = (errorResponse)=>{
        result = commonError(errorResponse);
    };
    if (payload?.oauthToken) {
        await addSavedSearch(success, error, {
            authenticationToken: payload.oauthToken,
            body: payload.search
        });
    } else if (payload?.email) {
        await addSavedSearchEmail(success, error, {
            body: unloggedBody
        });
    } else {
        result = {
            error: 'No token or email provided'
        };
    }
    while(result.error === 'undefined'){
        await new Promise((resolve)=>setTimeout(resolve, 50));
    }
    return result;
};

const commonSuccess = (response)=>{
    return {
        identity: response?.id ?? `No ID provided on success`
    };
};
const commonError = (errorResponse)=>{
    return {
        error: errorResponse?.errorCode === 'UNKNOWN_ERROR' ? `Error response with status: ${errorResponse?.httpStatusCode}` : errorResponse?.errorCode ?? `Error response with status: ${errorResponse?.httpStatusCode}`
    };
};
const getCreateConfig = ({ payload, configCalls })=>{
    switch(configCalls.brand){
        case 'li':
            return payload.search.criteria.location.placeIds.length > 0 ? createOnLi({
                payload,
                configCalls
            }) : createOnDefault({
                payload,
                configCalls
            });
        case 'iwt':
            return createOnDefault({
                payload,
                configCalls
            });
        case 'imn':
            return createOnDefault({
                payload,
                configCalls
            });
        case 'sl':
            return createOnDefault({
                payload,
                configCalls
            });
        default:
            return Promise.resolve({
                error: 'Brand not supported'
            });
    }
};

const createSavedSearch = async ({ searchModel, page, stage, brand, locale, email, oauthToken })=>{
    const config = getCustomBrandConfig(brand);
    if (!config) {
        return {
            error: `${brand} is not configured yet`
        };
    }
    try {
        if (!config.createUtils.supported) {
            return {
                error: `${brand} is not supported`
            };
        }
        if (oauthToken && !config.login?.oauth) {
            return {
                error: `${brand} is not supported for oauth`
            };
        }
        if (!email && !oauthToken) {
            return {
                error: 'No email or oauthToken provided'
            };
        }
        if (!email && config.createUtils.emailRequired) {
            return {
                error: `Email is needed for ${brand}`
            };
        }
        if (searchModel?.locations?.length > 10) {
            return {
                error: 'Too many locations'
            };
        }
        const translate = instance.getFixedT(locale);
        const configCalls = {
            basicsKey: {
                imn: '',
                iwt: ''
            },
            stage,
            brand
        };
        const search = mapSearchDataModelToWLSearch(searchModel);
        const title = generateSavedSearchTitle(search.criteria, translate);
        const currentUrl = !isWindowUndefined() ? window?.location?.href : '';
        const userInteractionData = getUserInteractionData(page, config.brandId);
        const payload = {
            search,
            title,
            currentUrl,
            translate,
            userInteractionData,
            email,
            oauthToken
        };
        const result = await getCreateConfig({
            payload,
            configCalls
        });
        return result;
    } catch (error) {
        return {
            error
        };
    }
};

const useSavedSearch = (stage, brand, token)=>{
    const [savedSearches, setSavedSearches] = useState([]);
    const [lastSavedSearch, setLastSavedSearch] = useState(null);
    const [error, setError] = useState();
    const [isFetching, setIsFetching] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(getToken() !== undefined);
    const [authenticationToken, setAuthenticationToken] = useState(token);
    const config = getCustomBrandConfig(brand);
    if (!config) {
        throw new Error('Brand not supported');
    }
    if (!authenticationToken) {
        const cookieToken = getToken();
        if (cookieToken) {
            setAuthenticationToken(cookieToken);
        }
    }
    const fetchSavedSearches = async ()=>{
        setIsFetching(true);
        const options = {
            authenticationToken};
        const onError = (errorResponse)=>{
            setError(errorResponse);
            setIsLoggedIn(false);
            setIsFetching(false);
        };
        await getSavedSearches((response)=>{
            if (response.data.length > 0) {
                const latest = getMostRecentSearch(response.data);
                setLastSavedSearch(latest);
                setSavedSearches((oldState)=>[
                        ...oldState,
                        ...response.data
                    ]);
            }
        }, onError, options);
        setIsFetching(false);
    };
    return {
        savedSearches,
        lastSavedSearch,
        isFetching,
        fetchSavedSearches,
        isLoggedIn,
        setIsLoggedIn,
        setSavedSearches,
        error
    };
};

export { ButtonSearchCriteriaMock, JWT_COOKIE_KEY, LEGACY_MODEL_VERSION, REST_RETRY_COUNT, REST_RETRY_TIMEOUT, URL_addSavedSearch, URL_addSavedSearchEmail, URL_baseRoute, URL_deleteSavedSearch, URL_getLogicImmoMapping, URL_getMigrationState, URL_getSavedSearch, URL_getSavedSearchMatches, URL_getSavedSearches, URL_getWLSavedSearchAnonymous, URL_unsubscribeAllWLChannelAnonymous, URL_updateChannelState, URL_updateRelaxedCriteria, URL_updateSavedSearch, URL_updateWLAlternativeAnonymous, URL_updateWLChannelAnonymous, addSavedSearch, addSavedSearchEmail, addSavedSearchEmailLI, baseApiWithResponse, baseApiWithoutResponse, cardList, clearObject, createIntermediaryMatchingMandatePayload, createLegacyEndpoint, createSavedSearch, currencySymbol, defaultChannels, deleteLegacyIWTSavedSearches, deleteLegacyIWTSavedSearchesAnonymous, deleteLegacySLGAnonymousSavedSearch, deleteLegacySLGSavedSearches, deleteSavedSearches, energyClassToWording, errorCodes, formatNumber, generateSavedSearchTitle, getAnonymousLegacyIWTSavedSearch, getBrandConfig, getEPToken, getHintType, getIWTAnonymousAuthToken, getLegacyIWTSavedSearches, getLegacyParams, getLegacySLGAnonymous, getLegacySLGSavedSearches, getListingsLink, getLocationsWording, getMigrationState, getMostRecentSearch, getNumberOfLocations, getOldFormatLocale, getParams, getRange, getSavedSearch, getSavedSearchCriteria, getSavedSearchMatches, getSavedSearches, getSearchModelFromSavedSearchCriteria, getTheme, getToken, getTrackingFavorite, getUserInteractionData, getViewProps, getWLItemWording, getWLSavedSearchAnonymous, includeCommuteLocation, includeUpperOrLower, intermediaryMatchingMandateLocalStorageKey, intermediaryMatchingMandateLokalizePrefix, intermediaryMatchingMandateTestId, intermediaryMatchingMandateThorEvent, isDocumentUndefined, isIntermediaryMatchingMandateShowable, isLegacyIWTSavedSearch, isLegacySLGSavedSearch, isLegacySavedSearch, isLocalStorageUndefined, isSSR, isValidEmail, isWindowUndefined, legacyAlternativesOff, legacyAlternativesOn, legacySLMock, legacySLMockAsWL, localKeys, mapCriteriaToWording, mapLegacyIWTToSavedSearch, mapLegacySLGDataToSavedSearch, mapSavedSearchCommonsThorKeys, mapSavedSearchDeletedToThorEvent, mapSavedSearchEditAlert, mapSavedSearchInterestToThorEvent, mapSavedSearchSeePropertiesToThorEvent, mapSavedSearchSubscripionToThorEvent, mapSavedSearchToogleAlertToThorEvent, mapSearchDataModelToWLSearch, matchingListTracking, mockedLegacySavedSearches, mockedLegacySavedSearchesToWLResult, mockedResponseId, redirectTo, redirectToLogin, reverseWLItemToSearchModel, roomsKey, savedSearchApiErrorCode, savedSearchButtonModalTestId, searchModelMock, searchModelWithCommuteTime, searchModelWithRadius, sortByRencency, sortClassifiedsByRecency, spacesSymbol, unsubscribeAllWLChannelAnonymous, updateAlternativeState, updateChannelState, updateLegacyIWTNotificationsState, updateLegacyIWTNotificationsStateAnonymous, updateLegacyIWTNotificationsStates, updateLegacyNotificationsStatesAnonymous, updateLegacySLGNotificationsState, updateLegacySLGNotificationsStateAnonymous, updateSavedSearch, updateWLAlternativeAnonymous, updateWLChannelAnonymous, useSavedSearch };
//# sourceMappingURL=index.mjs.map
